import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildTommyUrl } from '@/constants/support';
import { DollarSign, Clock, Calendar, CalendarClock, ArrowRight, RotateCcw, CreditCard, Zap, CheckCircle, Loader2, AlertCircle, Check, History, PlaneTakeoff, Minus, Plus, FastForward, SkipForward, Wallet } from 'lucide-react';
import { format, parseISO, addWeeks, isBefore, subDays, isAfter, startOfDay } from 'date-fns';
import { formatScheduleDate } from '@/utils/formatScheduleDate';
import { buildNmiScheduleChangePreview } from '@/utils/nmiScheduleChangePreview';
import { filterNmiOpenInstallmentRows } from '@/utils/nmiOpenInstallmentRows';
import { seedNmiOverrideRows } from '@/utils/nmiOverrideSeed';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { User } from '@/api/entities';
import {
  calculatePaymentSchedule,
  SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  SLICE_PAY_HOLD_FEE_RATE,
  DEFERA_CAPITAL_HOLD_FEE_RATE,
} from '@/utils/paymentScheduleCalculator';
import { cn } from '@/lib/utils';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '@/lib/stripe';
import { reserveUrl, deferaZeroUrl, deferaCapitalUrl, deferaCapitalNmiUrl, deferaCreditUrl, getApiUrl } from '@/lib/apiUrls';
import { mergeReserveActAsHeaders, mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import { useInstallmentsCopy } from '@/hooks/useInstallmentsCopy';
import { useIsMounted } from '@/hooks/useIsMounted';
import { formatStripeErrorMessage } from '@/lib/stripeErrors';
import { SKIP_PAYMENT_FEE_CENTS, EXTENSION_FEE_LABEL, SCHEDULE_DATE_CHANGE_FEE_LABEL, SCHEDULE_DATE_CHANGE_CUTOFF_DAYS } from '@/constants/paymentPolicy';
import {
  ACTION_MODAL_CONTENT_CLASS,
  ACTION_MODAL_BODY_CLASS,
  MODAL_PRIMARY_BTN_CLASS,
  MODAL_GHOST_BTN_CLASS,
} from '@/components/booking/paymentScheduleModalStyles';
import { scheduleSummary } from '@/components/booking/scheduleSummary';
import FailedAttemptPill from '@/components/booking/FailedAttemptPill';
import { canViewTripInternals } from '@/utils/roles';
import ActionModalHeader from '@/components/ui/ActionModalHeader';
import SavedPaymentMethodCard, { stripePaymentMethodToCardProps } from '@/components/payment/SavedPaymentMethodCard';
import { BRAND_ICON_CLASS, BRAND_PRIMARY_BTN_CLASS, BRAND_ROYAL_RGB, SECTION_CARD_SHELL } from '@/constants/brandColors';
import { resolveCarrierLogo } from '@/utils/airlineBranding';
import DeferaCapitalNmiUpdateCardForm from '@/components/defera-capital-nmi/DeferaCapitalNmiUpdateCardForm';
import DeferaCapitalNmiMakePaymentForm from '@/components/defera-capital-nmi/DeferaCapitalNmiMakePaymentForm';
import DeferaCapitalNmiSkipPaymentForm from '@/components/defera-capital-nmi/DeferaCapitalNmiSkipPaymentForm';
import DeferaCapitalNmiRestartPlanForm from '@/components/defera-capital-nmi/DeferaCapitalNmiRestartPlanForm';
import MovePaymentDialog from '@/components/booking/MovePaymentDialog';
import { paymentExtensionChoices, pacificTodayYmd, selectPaymentToMove } from '@/utils/paymentExtensionChoices';
import { fmtAmount, fmtCurrency } from '@/utils/money';

function formatSliceDuration(isoDuration) {
  if (isoDuration == null) return '';
  if (typeof isoDuration === 'number') {
    const h = Math.floor(isoDuration / 60);
    const m = isoDuration % 60;
    return `${h}h ${m}m`;
  }
  const matches = String(isoDuration).match(/PT(\d+H)?(\d+M)?/);
  if (!matches) return '';
  const hours = matches[1] ? parseInt(matches[1].slice(0, -1)) : 0;
  const minutes = matches[2] ? parseInt(matches[2].slice(0, -1)) : 0;
  return `${hours}h ${minutes}m`;
}

/** Safe JSON parse for fetch responses - handles rate limit (429) plain text */
/**
 * "Pick my own dates" as the update-schedule functions take it. One builder
 * for the preview (`dry_run`) and the confirm, so what the customer is shown
 * is exactly what they buy. Null when there's nothing to send yet.
 */
function customerDatesPayload({ tripId, isNmi, rows, autoAmounts, anchorDate, frequency }) {
  if (!tripId) return null;
  if (isNmi) {
    if (!rows || rows.length === 0) return null;
    const sortedRows = [...rows].sort((a, b) => (a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0));
    return {
      booking_id: tripId,
      installments: sortedRows.map((r) => (
        autoAmounts
          ? { due_date: r.due_date }
          : { due_date: r.due_date, amount_cents: Math.round((parseFloat(r.amount) || 0) * 100) }
      )),
      auto_amounts: autoAmounts,
    };
  }
  if (!anchorDate) return null;
  return { booking_id: tripId, payment_frequency: frequency, next_charge_date: anchorDate };
}

/** `2026-10-17T17:00:00Z` → "Oct 17, 2026". UTC, because the anchor is 17:00 UTC on the chosen day. */
function fmtPlanDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

async function safeResJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    if (res.status === 429) throw new Error('Too many requests. Please try again in a few minutes.');
    throw new Error(text || 'Request failed');
  }
}

/**
 * Primary-action button skin shared by Make a Payment, Update Card, Change Schedule,
 * Request Extension, and Revert Extension — royal mid → royal anchor, same family
 * as the trip modal header wash.
 */
const PRIMARY_ACTION_BTN_CLASS = `h-9 text-xs rounded-full ${BRAND_PRIMARY_BTN_CLASS}`;

/** Date+amount rows editor shared by the admin Override Dates modal and the
 *  customer "$25 custom dates" mode inside Change Schedule. House-styled to
 *  match the schedule preview cards. */
/* eslint-disable react/prop-types -- local helper, props documented above */
function DateAmountRowsEditor({ rows, setRows, autoAmounts, autoSplitCents, maxDate }) {
  return (
    <div className="space-y-1.5">
      {rows.map((row, idx) => (
        <div key={idx} className="flex items-center gap-2 rounded-xl bg-white/[0.03] border border-white/10 px-2.5 py-1.5">
          <span className="text-white/30 text-[11px] w-4 text-right tabular-nums">{idx + 1}</span>
          <input
            type="date"
            value={row.due_date}
            max={maxDate || undefined}
            onChange={(e) => setRows((rs) => rs.map((r, i) => i === idx ? { ...r, due_date: e.target.value } : r))}
            className="flex-1 bg-transparent border-0 text-xs text-white focus:outline-none [color-scheme:dark]"
          />
          {autoAmounts ? (
            <span className="w-24 text-right text-xs text-brand-200/90 font-medium tabular-nums">
              ${fmtCurrency(autoSplitCents[idx] ?? 0)}
            </span>
          ) : (
            <div className="relative w-24">
              <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-white/35 text-xs">$</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={row.amount}
                onChange={(e) => setRows((rs) => rs.map((r, i) => i === idx ? { ...r, amount: e.target.value } : r))}
                className="w-full bg-white/5 border border-white/15 rounded-lg pl-4 pr-1.5 py-1 text-xs text-white text-right tabular-nums"
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => setRows((rs) => rs.filter((_, i) => i !== idx))}
            className="text-white/30 hover:text-red-300 text-base leading-none px-0.5"
            aria-label={`Remove payment ${idx + 1}`}
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setRows((rs) => [...rs, { due_date: '', amount: '' }])}
        className="w-full rounded-xl border border-dashed border-white/15 py-1.5 text-[11px] text-brand-300 hover:text-brand-200 hover:border-brand-500/40 transition-colors"
      >
        + Add payment
      </button>
    </div>
  );
}
/* eslint-enable react/prop-types */

/** Count cleared installment slots for Defera Capital / Zero Stripe plans.
 *  Must stay aligned with defera-capital-schedule `payment_history` semantics.
 *  `planTerminated` = the booking itself is cancelled/defaulted (see the
 *  zero-remaining note below — a terminated plan can't use that shortcut). */
function countPaidInstallmentSlots(history, installmentCount, remainingCents, planTerminated = false) {
  const paidAheadInstallmentsCount = (history || [])
    .filter((p) => p?.type === 'one_time' && Array.isArray(p?.covers_dates))
    .reduce((sum, p) => sum + p.covers_dates.length, 0);
  /** `cash_paid_separately` marks the credit part of a cycle whose cash came in
   *  as its own one_time row (a failed card charge paid by hand, then the
   *  invoice closed by credit note). That one_time row already holds the
   *  cycle's slot below; counting this one too read one installment high. */
  const subscriptionCount = (history || [])
    .filter((p) => p?.type === 'subscription' && Number(p?.amount_cents) > 0 && !p?.cash_paid_separately)
    .length;
  /** Make-a-Payment / auto-correction one-offs clear principal but aren't subscription
   *  invoices — count each as one installment slot so PAID X/Y matches reality. */
  const oneTimeInstallmentCount = (history || [])
    .filter((p) => p?.type === 'one_time'
      && Number(p?.amount_cents) > 0
      && !(Array.isArray(p?.covers_dates) && p.covers_dates.length > 0))
    .length;
  let count = subscriptionCount + paidAheadInstallmentsCount + oneTimeInstallmentCount;
  /** Stripe-truth remaining is authoritative — when it's zero every installment
   *  has cleared even if a paid invoice row is missing from `payment_history`.
   *
   *  NOT on a cancelled/defaulted booking. Cancelling an NMI plan flips every
   *  open installment to `cancelled`, and `defera-capital-nmi-schedule` drops
   *  `cancelled` rows from its pending sum — so `remaining_cents` reads 0 for a
   *  plan that was barely paid at all. OPTJZQ collected $933.60 of $7,157.65
   *  (deposit + 2 pay-aheads, zero installments ever charged) and rendered
   *  "Paid 22/22"; every cancelled NMI trip had the same tell. On a terminated
   *  plan nothing more will EVER be charged, so `payment_history` is already the
   *  complete record — count the rows and stop. Trade-off accepted: a terminal
   *  trip that really was paid in full AND is missing a history row now
   *  undercounts by one, which beats telling a customer who paid $0 that their
   *  trip is complete. Same guard `isPaidInFull` carries; see I703QH. */
  if (!planTerminated && Number(remainingCents) <= 0 && installmentCount > 0) {
    count = installmentCount;
  }
  return installmentCount > 0 ? Math.min(installmentCount, count) : count;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: '500',
      '::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
    },
    invalid: { color: '#f87171', iconColor: '#f87171' },
  },
  hidePostalCode: false,
};

function DeferaZeroUpdateCardForm({ tripId, onClose, onSuccess, apiUrlFn = deferaZeroUrl }) {
  const stripe = useStripe();
  const elements = useElements();
  /** The card list can come back after the dialog closes. See useIsMounted. */
  const isMounted = useIsMounted();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [useNewCard, setUseNewCard] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted() || !session?.access_token) return;
        const res = await fetch(reserveUrl('payment-methods'), {
          headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
        });
        const data = await safeResJson(res);
        if (!isMounted()) return;
        if (res.ok) {
          setPaymentMethods(data.payment_methods || []);
          setDefaultPaymentMethod(data.default_payment_method || null);
          const defaultPm = data.payment_methods?.find((p) => p.is_default);
          setSelectedCardId(defaultPm?.id || data.payment_methods?.[0]?.id || null);
          if (!data.payment_methods?.length) setUseNewCard(true);
        }
      } catch (e) {
        // A dialog the customer already closed has nothing to tell them.
        if (!isMounted()) return;
        console.error('Failed to fetch payment methods', e);
        toast.error(e.message || 'Could not load payment methods');
      } finally {
        if (isMounted()) setLoading(false);
      }
    })();
  }, [isMounted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsProcessing(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in to continue');

      let pmId;
      if (useNewCard) {
        if (!stripe || !elements) throw new Error('Payment system not ready');
        const setupRes = await fetch(apiUrlFn('setup-intent'), {
          method: 'POST',
          headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
          body: JSON.stringify({ booking_id: tripId })
        });
        const setupData = await safeResJson(setupRes);
        if (!setupRes.ok) throw new Error(setupData?.error || 'Failed to set up card');

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('Card input not found');

        const { error: confirmErr, setupIntent } = await stripe.confirmCardSetup(setupData.client_secret, {
          payment_method: { card: cardElement }
        });
        if (confirmErr) throw new Error(formatStripeErrorMessage(confirmErr) || confirmErr.message);
        pmId = setupIntent?.payment_method;
      } else {
        pmId = selectedCardId;
      }
      if (!pmId) throw new Error('Please select or add a card');

      const updateRes = await fetch(apiUrlFn('update-payment-method'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: tripId, payment_method_id: pmId })
      });
      const updateData = await safeResJson(updateRes);
      if (!updateRes.ok) throw new Error(updateData?.error || 'Failed to update card');

      toast.success('Card updated successfully');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className={paymentCardGradient.container}>
        <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-4">Card for installments</p>
        {paymentMethods.length > 0 && (
          <div className="space-y-2.5">
            {paymentMethods.map((pm) => (
              <SavedPaymentMethodCard
                key={pm.id}
                {...stripePaymentMethodToCardProps(pm)}
                selected={!useNewCard && selectedCardId === pm.id}
                onClick={() => { setSelectedCardId(pm.id); setUseNewCard(false); }}
              />
            ))}
            <button
              type="button"
              onClick={() => setUseNewCard(true)}
              className={paymentCardGradient.addNewCard}
            >
              <div className={paymentCardGradient.addNewCardIcon}>
                <CreditCard className="w-5 h-5 text-white/40" />
              </div>
              Use a new card
            </button>
          </div>
        )}
        {(useNewCard || paymentMethods.length === 0) && (
          <div className={paymentCardGradient.cardElementWrapper}>
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
        )}
      </div>
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        </div>
      )}
      <div className="flex gap-2.5">
        <Button type="button" variant="ghost" onClick={onClose} className={cn(MODAL_GHOST_BTN_CLASS, 'flex-1')}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isProcessing || (!useNewCard && !selectedCardId) || (useNewCard && !stripe)}
          className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1')}
        >
          {isProcessing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Update Card'}
        </Button>
      </div>
    </form>
  );
}

/**
 * Restart Plan form — companion of `defera-capital-restart-plan` edge fn.
 *
 * Shows up only when a Defera Capital sub was canceled by Stripe due to
 * exhausted Smart Retries (cancellation_details.reason='payment_failed').
 * The schedule endpoint computes `past_due_cents` + new schedule via the
 * shared `_shared/defera-capital-restart-plan.ts` helper; this form renders
 * those numbers, lets the customer swap to a different card if their old
 * one is the one that kept failing, and POSTs to the restart endpoint.
 *
 * Mirrors the DeferaZeroUpdateCardForm patterns for card selection +
 * Stripe Elements wiring, but the submit hits restart-plan (not update-pm).
 */
function DeferaCapitalRestartPlanForm({ tripId, onClose, onSuccess, fmtCurrency, planRestart, apiUrlFn = deferaCapitalUrl }) {
  const stripe = useStripe();
  const elements = useElements();
  /** The card list can come back after the dialog closes. See useIsMounted. */
  const isMounted = useIsMounted();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useNewCard, setUseNewCard] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted() || !session?.access_token) return;
        const res = await fetch(reserveUrl('payment-methods'), {
          headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
        });
        const data = await safeResJson(res);
        if (!isMounted()) return;
        if (res.ok) {
          setPaymentMethods(data.payment_methods || []);
          const defaultPm = data.payment_methods?.find((p) => p.is_default);
          setSelectedCardId(defaultPm?.id || data.payment_methods?.[0]?.id || null);
          if (!data.payment_methods?.length) setUseNewCard(true);
        }
      } catch (e) {
        // A dialog the customer already closed has nothing to tell them.
        if (!isMounted()) return;
        console.error('Failed to fetch payment methods', e);
        toast.error(e.message || 'Could not load payment methods');
      } finally {
        if (isMounted()) setLoading(false);
      }
    })();
  }, [isMounted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsProcessing(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in to continue');

      let pmId;
      if (useNewCard) {
        if (!stripe || !elements) throw new Error('Payment system not ready');
        // Set up the new card off-session so the restart endpoint can charge
        // both the past-due and future installments without further customer
        // action. Re-uses the same setup-intent path the Update Card form
        // hits, scoped to this trip.
        const setupRes = await fetch(apiUrlFn('setup-intent'), {
          method: 'POST',
          headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
          body: JSON.stringify({ booking_id: tripId })
        });
        const setupData = await safeResJson(setupRes);
        if (!setupRes.ok) throw new Error(setupData?.error || 'Failed to set up card');

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('Card input not found');

        const { error: confirmErr, setupIntent } = await stripe.confirmCardSetup(setupData.client_secret, {
          payment_method: { card: cardElement }
        });
        if (confirmErr) throw new Error(formatStripeErrorMessage(confirmErr) || confirmErr.message);
        pmId = setupIntent?.payment_method;
      } else {
        pmId = selectedCardId;
      }
      if (!pmId) throw new Error('Please select or add a card');

      // Idempotency: stamp the request with a client-side UUID so a network
      // retry collapses to the same Stripe PaymentIntent on the backend.
      const clientRequestId = crypto.randomUUID();
      const res = await fetch(apiUrlFn('restart-plan'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({
          booking_id: tripId,
          payment_method_id: pmId,
          client_request_id: clientRequestId,
          /** Round-trips the past-due the customer SAW in the modal. Backend
           *  refuses to charge if its freshly-computed past-due exceeds this
           *  (with a 1-cent tolerance) so a stale modal can never become a
           *  surprise larger charge. See drift guard in restart-plan/index.ts. */
          expected_past_due_cents: planRestart?.past_due_cents ?? null,
        }),
      });
      const data = await safeResJson(res);
      if (res.status === 409 && data?.mode === 'past_due_changed') {
        // Math drifted while the modal was open. Tell the customer + refresh.
        toast.error(
          `Your past-due has changed to $${fmtCurrency(data.current_past_due_cents)}. Please reopen Restart Plan to confirm the new amount.`,
        );
        onSuccess?.(); // reuses parent's `fetchDzSchedule()` + close modal
        return;
      }
      if (!res.ok) throw new Error(data?.error || 'Failed to restart plan');

      if (data.mode === 'payoff_required') {
        // Edge case: by the time the customer hit submit, the schedule got
        // even tighter (e.g. they sat on the modal for a while). Fall back
        // to the existing Make-a-Payment flow rather than charging anything.
        toast.message('Your trip is too close to departure to restart on a schedule. Please use Make a Payment to pay off the remaining balance.');
        onClose?.();
        return;
      }
      if (data.mode === 'partial_success') {
        // Past-due charged but new sub failed. The edge fn already logged
        // for ops; tell the customer their money landed.
        toast.success(data.message || 'Payment received — we\'ll finish setting up your schedule shortly.');
        onSuccess?.();
        return;
      }
      toast.success('Plan restarted — your schedule is back on track.');
      onSuccess?.();
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Summary panel: shows EXACTLY what the customer is signing up for. */}
      <div className="rounded-2xl border border-brand-500/15 bg-brand-500/[0.04] px-4 py-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-brand-100/70 uppercase tracking-widest font-medium">Past-due (charged now)</span>
          <span className="text-base text-white font-normal tabular-nums">${fmtCurrency(planRestart?.past_due_cents || 0)}</span>
        </div>
        <div className="h-px bg-white/[0.06]" />
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/60 uppercase tracking-widest font-normal">Resumed schedule</span>
          <span className="text-base text-white font-normal tabular-nums">
            {planRestart?.new_num_payments || 0}× ${fmtCurrency(planRestart?.new_per_payment_cents || 0)}
          </span>
        </div>
        {planRestart?.first_payment_date ? (
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/50 uppercase tracking-widest font-normal">First installment</span>
            <span className="text-sm text-white/85 font-normal">
              {format(parseISO(planRestart.first_payment_date), 'MMM d, yyyy')}
            </span>
          </div>
        ) : null}
      </div>

      {/* Card picker — defaults to the on-file card so a customer with a
       *  good card (whose plan happened to fail temporarily, e.g. one-off
       *  bank hold) can confirm in one click. Customers whose card actually
       *  needs replacing pick "Use a new card". */}
      <div className={paymentCardGradient.container}>
        <p className="text-white/80 text-sm font-normal">Choose a card to charge</p>
        {paymentMethods.length > 0 && (
          <div className="space-y-2.5">
            {paymentMethods.map((pm) => (
              <SavedPaymentMethodCard
                key={pm.id}
                {...stripePaymentMethodToCardProps(pm)}
                selected={!useNewCard && selectedCardId === pm.id}
                onClick={() => { setSelectedCardId(pm.id); setUseNewCard(false); }}
              />
            ))}
            <button type="button" onClick={() => setUseNewCard(true)} className={paymentCardGradient.addNewCard}>
              <div className={paymentCardGradient.addNewCardIcon}>
                <CreditCard className="w-5 h-5 text-white/40" />
              </div>
              Use a new card
            </button>
          </div>
        )}
        {(useNewCard || paymentMethods.length === 0) && (
          <div className={paymentCardGradient.cardElementWrapper}>
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        </div>
      )}
      <div className="flex gap-3">
        <Button type="button" variant="ghost" onClick={onClose} className="flex-1 border border-white/20 text-white hover:bg-white/10 rounded-full py-6">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isProcessing || (!useNewCard && !selectedCardId) || (useNewCard && !stripe)}
          className="flex-1 bg-[var(--accent-color)] text-white hover:opacity-90 rounded-full py-6 disabled:opacity-50"
        >
          {isProcessing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Restarting…</> : 'Pay & Restart'}
        </Button>
      </div>
    </form>
  );
}

function DeferaZeroMakePaymentForm({ tripId, onClose, onSuccess, fmtCurrency, apiUrlFn = deferaZeroUrl, openInvoices = [], openInvoicesTotalCents = 0, subscriptionDefaultPaymentMethodId = null, stripeUnitAmountCents = 0, currentScheduleDates = [], remainingCentsHint = 0, payoffCreditHintCents = 0, pastDuePartialEnabled = false, initialMode = null }) {
  const stripe = useStripe();
  const elements = useElements();
  /** The card list can come back after the dialog closes. See useIsMounted. */
  const isMounted = useIsMounted();
  /** Past-due invoices block ALL non-invoice payments (server enforces via `past_due_blocks_payment`).
   *  Why: a fresh PI on top of an open invoice doesn't actually settle the invoice — Stripe keeps
   *  retrying it on the customer's card while the new payment sits in the ledger. Customer gets
   *  charged twice. The only way to clear past_due is via `/invoices/{id}/pay`, which is what the
   *  Pay-past-due banner does. So we disable skip / custom / payoff tabs entirely until the
   *  customer settles the past-due. Computed early so the initial `mode` default can avoid landing
   *  on a disabled tab. */
  const hasPastDueAtMount = Array.isArray(openInvoices) && openInvoices.length > 0 && openInvoicesTotalCents > 0;
  const allTabsDisabledByPastDue = hasPastDueAtMount;
  const skipDisabledByPastDue = hasPastDueAtMount;
  /** "Pay part of it" on the Stripe rail (2026-09-10): the Past due tab takes any amount ≥ $5
   *  toward the open invoices, oldest first — server mode 'past_due' charges a PaymentIntent and
   *  reduces each allocated invoice with a pre-payment credit note, so nothing sits on top of a
   *  retrying invoice. The other tabs stay locked until nothing is past due. Enabled by the
   *  parent for both Stripe rails (Defera Capital on Stripe and Defera Zero). */
  const pastDueTabEnabled = hasPastDueAtMount && pastDuePartialEnabled;

  /** Make-a-Payment modes:
   *   - "skip"   (default when sub has a recurring price AND no past-due): pay N upcoming
   *              installments now; per-payment stays the same, schedule loses its first N dates
   *              (Stripe `trial_end` is pushed forward). Customers asking to "prolong the next
   *              payment date" want this.
   *   - "custom":           pay any dollar amount; per-payment shrinks across the same dates
   *              (the original behavior — kept for customers who prefer lower weekly amounts).
   *   - "payoff":           clear the entire remaining balance and end the subscription.
   *   Falls back to "custom" when stripeUnitAmountCents is 0 (no locked recurring price) or when
   *   the trip has past-due invoices (skip is disabled until those are settled). */
  const [mode, setMode] = useState(initialMode || (pastDueTabEnabled ? 'past_due' : (stripeUnitAmountCents > 0 && !skipDisabledByPastDue ? 'skip' : 'custom')));
  const [skipCount, setSkipCount] = useState(1);
  const [amount, setAmount] = useState('');
  /** Past-due tab amount, prefilled with everything past due so the default tap clears it. */
  const [pastDueAmount, setPastDueAmount] = useState(openInvoicesTotalCents > 0 ? (openInvoicesTotalCents / 100).toFixed(2) : '');
  /** Pay-ahead has TWO sub-modes once the customer is on the "Pay ahead" tab:
   *    'installments' (default) — integer count via the +/− stepper, charges N × unit_amount and
   *                                pushes Stripe `trial_end` by N intervals. Schedule loses its
   *                                first N rows; everything else stays at the locked unit_amount.
   *    'custom'                  — typed dollar amount; greedy-applied: covers as many full
   *                                installments as the amount allows, then credits any partial
   *                                leftover against the next remaining cycle via a sub-scoped
   *                                Stripe invoice item (so it can't leak across other trips on
   *                                the same customer). Different from the top-level Custom tab,
   *                                which spreads the leftover across ALL remaining cycles. */
  const [payAheadSubMode, setPayAheadSubMode] = useState('installments');
  const [payAheadCustomAmount, setPayAheadCustomAmount] = useState('');
  /** Tracks whether the customer manually picked a tab. Set true on any tab click so the
   *  past-due-clearing effect below doesn't yank them off a tab they intentionally chose. */
  const userPickedModeRef = useRef(false);

  /** OCNBDW canary: a trip with a past-due invoice forces this modal into Custom at mount
   *  (skip is disabled until past-due clears). The customer pays past-due via the banner,
   *  the prop flips, but the modal stays parked on Custom — and the next thing they typically
   *  do is enter a small "round-up" amount, which kicks off the price-churn loop. Re-default
   *  back to Pay ahead the moment past-due clears, unless the customer has already engaged
   *  with the modal (typed an amount, loaded a preview, or actively picked a tab). */
  useEffect(() => {
    if (
      !skipDisabledByPastDue &&
      stripeUnitAmountCents > 0 &&
      mode === 'custom' &&
      !amount &&
      !preview &&
      !userPickedModeRef.current
    ) {
      setMode('skip');
    }
    // Intentionally narrow deps — we only want to react to the past-due transition, not to
    // the customer typing into the amount field (would re-render mid-typing).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipDisabledByPastDue, stripeUnitAmountCents]);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [charging, setCharging] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [useNewCard, setUseNewCard] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [loadingPm, setLoadingPm] = useState(true);
  /** When true, after a successful charge, the server updates `subscription.default_payment_method`
   *  (and customer-level invoice default) to the card just used. Off by default per product req:
   *  customers must explicitly opt-in before we change which card auto-installments will use. */
  const [setDefaultPm, setSetDefaultPm] = useState(false);
  /** When set, this past-due open invoice will be paid via Stripe `/invoices/{id}/pay` instead of
   *  a one-off PaymentIntent — clears the subscription's `past_due` state in one shot. */
  const [payPastDueInvoiceId, setPayPastDueInvoiceId] = useState(null);
  /** Stable idempotency nonce for this pay attempt. Generated lazily on first submit and reused on
   *  retries (e.g. network timeout) so Stripe returns the cached PaymentIntent instead of creating
   *  a duplicate charge. Cleared on amount change (different params = different transaction) and on
   *  successful pay. */
  const payNonceRef = useRef(null);
  /** Monotonic preview sequence id. Each `fetchPreview` call captures the next value;
   *  responses whose id no longer matches `current` are dropped. Without this guard a
   *  slow earlier preview could land AFTER a faster newer one and repopulate the UI
   *  with stale numbers — letting the customer confirm a breakdown that doesn't match
   *  the amount they'll actually be charged. */
  const previewRequestIdRef = useRef(0);
  /** Closing the dialog makes every in-flight preview stale, so none lands on
   *  (or fetches for) a form that's gone. Same mechanism as a newer preview
   *  superseding an older one. See useIsMounted for why that matters. */
  useEffect(() => () => { previewRequestIdRef.current += 1; }, []);
  /** Tracks the cent amount associated with the current `preview` state. Used to disable
   *  Pay while the preview lags behind the typed amount (e.g. mid-typing). */
  const [previewAmountCents, setPreviewAmountCents] = useState(null);

  /** Past-due summary surfaced to the customer at the top of the modal. Show them the exact amount
   *  outstanding and let them pay it in one click (separate from the optional ad-hoc payment). */
  const hasPastDue = hasPastDueAtMount;

  /** Skip-mode derived values. The stepper hard-cap comes from the schedule (we never let the
   *  customer "skip more than they have"); the server's `max_skip_count` (returned by preview)
   *  refines this further once we know the exact deadline math, and `would_exceed_deadline`
   *  surfaces the "pay off instead" CTA when they ask for too many. */
  const totalUpcomingCount = Array.isArray(currentScheduleDates) ? currentScheduleDates.length : 0;
  const upcomingCap = Math.max(1, totalUpcomingCount);
  const serverMaxSkip = preview?.max_skip_count ?? upcomingCap;
  /** What paying off costs: the remaining balance minus Defera Credit already committed to this
   *  trip, which the server spends when the payoff lands. Every preview returns the server's
   *  figure; until one arrives, the schedule's remaining and committed credit give the same net. */
  const payoffCreditCents = preview?.payoff_credit_cents ?? payoffCreditHintCents ?? 0;
  const payoffAmountCents = preview?.payoff_amount_cents ?? Math.max(0, (remainingCentsHint || 0) - (payoffCreditHintCents || 0));
  /** Committed credit covers everything left: there is nothing to charge. */
  const payoffCoveredByCredit = payoffAmountCents <= 0 && payoffCreditCents > 0;
  /** Capped at the payoff: skipping past what's left to pay is a payoff, and the server charges
   *  only that. */
  const skipChargeCents = mode === 'skip' && stripeUnitAmountCents > 0
    ? Math.min(payoffAmountCents || stripeUnitAmountCents * skipCount, skipCount * stripeUnitAmountCents)
    : 0;
  /** Cents the customer typed into the Pay-ahead → Custom Amount sub-tab. NaN-safe (an
   *  empty/garbage input gives 0). Capped at `remainingCentsHint` for display purposes —
   *  the server has the real cap and re-validates with `code: 'exceeds_remaining'`. */
  const payAheadCustomCents = mode === 'skip' && payAheadSubMode === 'custom'
    ? Math.max(0, Math.round((parseFloat(payAheadCustomAmount) || 0) * 100))
    : 0;
  /** Charge amount the Pay-ahead tab will submit — selects between the two sub-modes. */
  const payAheadChargeCents = payAheadSubMode === 'custom' ? payAheadCustomCents : skipChargeCents;
  const pastDueCents = mode === 'past_due' ? Math.max(0, Math.round((parseFloat(pastDueAmount) || 0) * 100)) : 0;
  const wouldExceedDeadline = mode === 'skip' && preview?.would_exceed_deadline === true;
  /** Inline error for skip-mode auto-preview failures. We surface this in the modal instead of a
   *  toast so customers can see the real reason (e.g. "subscription has no recurring price") and
   *  fall back to Custom mode without the alarm of a red notification on every modal open. */
  const [skipPreviewError, setSkipPreviewError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted() || !session?.access_token) return;
        const res = await fetch(reserveUrl('payment-methods'), {
          headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
        });
        const data = await safeResJson(res);
        if (!isMounted()) return;
        if (res.ok) {
          setPaymentMethods(data.payment_methods || []);
          /** Prefer the subscription's default PM (= what auto-installments actually charge) over
           *  the customer-level "is_default" — they can diverge and customers expect the modal to
           *  default to "the card you'll be charged automatically". */
          const subDefault = subscriptionDefaultPaymentMethodId
            ? data.payment_methods?.find((p) => p.id === subscriptionDefaultPaymentMethodId)
            : null;
          const defaultPm = subDefault || data.payment_methods?.find((p) => p.is_default);
          setSelectedCardId(defaultPm?.id || data.payment_methods?.[0]?.id || null);
          if (!data.payment_methods?.length) setUseNewCard(true);
        }
      } catch (e) {
        // A dialog the customer already closed has nothing to tell them.
        if (!isMounted()) return;
        toast.error(e.message || 'Could not load payment methods');
      } finally {
        if (isMounted()) setLoadingPm(false);
      }
    })();
  }, [subscriptionDefaultPaymentMethodId, isMounted]);

  /** Centralized preview reset. Bumps `previewRequestIdRef` so any in-flight
   *  fetchPreview drops its response on arrival (handles the mid-tab-switch
   *  race where a stale Pay-ahead preview would otherwise repopulate the UI
   *  AFTER the user navigated to Principal). Always pair `setPreview(null)`
   *  with this when the reset is navigation-driven; the typing-debounce path
   *  in `fetchPreview` already bumps via `++previewRequestIdRef.current`. */
  const resetPreview = useCallback(() => {
    previewRequestIdRef.current += 1;
    setPreview(null);
    setPreviewAmountCents(null);
    setLoadingPreview(false);
  }, []);

  /** Single preview fetcher used by all three modes. Skip mode auto-fires on stepper / amount
   *  change so the customer always sees the live "before/after" without an extra click; custom
   *  and payoff modes are button-triggered (custom requires a typed amount; payoff is unambiguous). */
  const fetchPreview = useCallback(async (overrides = {}) => {
    if (!tripId) return;
    const effectiveMode = overrides.mode ?? mode;
    const effectiveSubMode = overrides.payAheadSubMode ?? payAheadSubMode;
    let cents = 0;
    let body = { booking_id: tripId };
    if (effectiveMode === 'skip' && effectiveSubMode === 'custom') {
      cents = Math.max(0, Math.round(parseFloat(overrides.payAheadCustomAmount ?? payAheadCustomAmount ?? 0) * 100));
      if (cents <= 0) return;
      body.mode = 'pay_ahead_custom';
      body.amount_cents = cents;
    } else if (effectiveMode === 'skip') {
      const sc = Math.max(1, Math.floor(overrides.skipCount ?? skipCount));
      body.mode = 'skip_installments';
      body.skip_count = sc;
    } else if (effectiveMode === 'past_due') {
      cents = Math.max(0, Math.round((parseFloat(overrides.pastDueAmount ?? pastDueAmount ?? 0) || 0) * 100));
      if (cents <= 0) return;
      body.mode = 'past_due';
      body.amount_cents = cents;
    } else if (effectiveMode === 'payoff') {
      cents = Math.max(1, Math.round(Number(overrides.payoffCents ?? payoffAmountCents) || 0));
      if (cents <= 0) return;
      body.mode = 'payoff';
      body.amount_cents = cents;
    } else {
      cents = Math.round(parseFloat(overrides.amount ?? amount ?? 0) * 100) || 0;
      if (cents <= 0) return;
      body.amount_cents = cents;
    }
    /** Capture this request's monotonic id BEFORE the network round-trip. If a newer
     *  fetchPreview starts before we get the response, we'll drop ours on arrival. */
    const requestId = ++previewRequestIdRef.current;
    setLoadingPreview(true);
    if (effectiveMode === 'skip') setSkipPreviewError(null);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (requestId !== previewRequestIdRef.current) return;
      if (!session?.access_token) throw new Error('Please sign in');
      const res = await fetch(apiUrlFn('preview-early-payment'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify(body)
      });
      const data = await safeResJson(res);
      if (requestId !== previewRequestIdRef.current) {
        /** A newer fetchPreview started while we were waiting on the network. Drop our
         *  response — applying it would clobber the in-flight (newer, correct) request's
         *  result on arrival, or repopulate the UI after the user already changed amount. */
        return;
      }
      if (!res.ok) {
        /** Special case: `exceeds_remaining` is a structured "Pay off instead" recommendation
         *  from the server, not a hard failure. Surface it AS preview state so the UI can
         *  render a "Pay off $X" CTA (parity with `would_exceed_deadline` on skip-mode and
         *  with the NMI form). Pre-fix, this fell through to the generic error toast +
         *  cleared preview, and the skip-mode error inline copy pointed customers to
         *  Principal — server message said "use Pay off". */
        if (data && data.code === 'exceeds_remaining') {
          setPreview({ ...data, exceeds_remaining: true, mode: body.mode });
          setPreviewAmountCents(cents);
          return;
        }
        /** Past-due coded refusals are preview STATE, not failures: the panel renders the
         *  server's sentence under the input instead of a toast. */
        if (data && body.mode === 'past_due' && ['exceeds_past_due', 'nothing_past_due', 'below_minimum'].includes(data.code)) {
          setPreview({ ...data, mode: 'past_due', preview_error: data.code });
          setPreviewAmountCents(cents);
          return;
        }
        /** A drafted installment is about to charge; the server's sentence says to wait. */
        if (data?.code !== 'installment_processing') {
          console.error('[Make a Payment] preview-early-payment failed', { status: res.status, body, response: data });
        }
        throw new Error(data.error || `Preview failed (${res.status})`);
      }
      setPreview(data);
      setPreviewAmountCents(cents);
    } catch (err) {
      if (requestId !== previewRequestIdRef.current) return;
      /** Skip-mode preview is auto-fired on modal open — surface the error inline (so the
       *  customer can switch to Custom) instead of a red toast that fires before they've even
       *  interacted with the modal. Other modes still show toast since they're explicit. */
      if (effectiveMode === 'skip') {
        setSkipPreviewError(err.message || 'Could not load preview');
      } else {
        toast.error(err.message || 'Failed to load preview');
      }
      setPreview(null);
      setPreviewAmountCents(null);
    } finally {
      if (requestId === previewRequestIdRef.current) setLoadingPreview(false);
    }
  }, [tripId, mode, payAheadSubMode, skipCount, payAheadCustomAmount, amount, payoffAmountCents, apiUrlFn, pastDueAmount]);

  /** Auto-preview Pay-ahead → Installments sub-mode on entry and whenever skipCount changes —
   *  debounced so rapid stepper taps don't spam the edge function. */
  useEffect(() => {
    if (mode !== 'skip' || payAheadSubMode !== 'installments' || !tripId || stripeUnitAmountCents <= 0) return;
    const t = setTimeout(() => { fetchPreview({ mode: 'skip', payAheadSubMode: 'installments', skipCount }); }, 300);
    return () => clearTimeout(t);
  }, [mode, payAheadSubMode, skipCount, tripId, stripeUnitAmountCents, fetchPreview]);

  /** Auto-preview Pay-ahead → Custom Amount sub-mode whenever the typed dollar amount changes —
   *  debounced longer (500ms) since the customer is typing. Mirrors the auto-preview behavior of
   *  the installments stepper so customers see the "covers N installments + $P credit on the
   *  next cycle" breakdown live without clicking a preview button. */
  useEffect(() => {
    if (mode !== 'skip' || payAheadSubMode !== 'custom' || !tripId || stripeUnitAmountCents <= 0) return;
    const cents = Math.max(0, Math.round((parseFloat(payAheadCustomAmount) || 0) * 100));
    if (cents <= 0) {
      resetPreview();
      return;
    }
    /** Client-side $5 floor short-circuit. The server returns 400 below_minimum for cents < 500,
     *  and the previous code path surfaced that as an error toast / `skipPreviewError` banner with
     *  copy that pointed customers at Principal — wrong nudge for "amount too small". The inline
     *  amber "Minimum payment is $5.00" warning under the input is already shown; skipping the
     *  fetch here keeps the UI calm while the customer is still typing. */
    if (cents < 500) {
      resetPreview();
      setSkipPreviewError(null);
      return;
    }
    const t = setTimeout(() => {
      fetchPreview({ mode: 'skip', payAheadSubMode: 'custom', payAheadCustomAmount });
    }, 500);
    return () => clearTimeout(t);
  }, [mode, payAheadSubMode, payAheadCustomAmount, tripId, stripeUnitAmountCents, fetchPreview]);

  /** Auto-preview the Past due tab as the amount changes (500ms, like Pay-ahead → Custom).
   *  Below the $5 floor or above the past-due total the panel already explains itself. */
  useEffect(() => {
    if (mode !== 'past_due' || !tripId) return;
    const cents = Math.max(0, Math.round((parseFloat(pastDueAmount) || 0) * 100));
    if (cents < 500 || cents > openInvoicesTotalCents) {
      resetPreview();
      return;
    }
    const t = setTimeout(() => { fetchPreview({ mode: 'past_due', pastDueAmount }); }, 500);
    return () => clearTimeout(t);
  }, [mode, pastDueAmount, tripId, openInvoicesTotalCents, fetchPreview, resetPreview]);

  /** Pay off asks the server on entry, so the figure confirmed is the one the charge accepts: what's
   *  left after the Defera Credit already committed to this trip (the server spends that credit). */
  useEffect(() => {
    if (mode !== 'payoff' || !tripId) return;
    fetchPreview({ mode: 'payoff' });
    // Only on entering the tab; fetchPreview's identity changes with every keystroke elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, tripId]);

  /** Shared submit handler. Branches:
   *   - past-due invoice: server pays via /invoices/{id}/pay (clears `past_due` in one shot)
   *   - skip mode:        server charges N × unit_amount and pushes Stripe `trial_end` forward
   *   - payoff mode:      server charges full remaining and DELETEs the subscription
   *   - custom mode:      server charges typed amount and recomputes per-payment (existing) */
  const submitCharge = async ({ chargeInvoiceId = null, chargeCents = null, submitMode = null } = {}) => {
    const effectiveMode = submitMode ?? mode;
    let cents = 0;
    if (chargeInvoiceId) {
      cents = Math.max(100, Math.round(Number(chargeCents) || 0));
    } else if (effectiveMode === 'skip' && payAheadSubMode === 'custom') {
      /** $5 floor matches the server (pay_ahead_custom rejects < $5 with `below_minimum`) and
       *  the UI warning. Pre-fix the form sent $1 minimum to a server that floors at $5,
       *  surfacing a confusing "below_minimum" toast for a click that the disabled button
       *  should already have blocked. */
      cents = Math.max(500, payAheadCustomCents);
      if (payAheadCustomCents < 500) {
        toast.error('Minimum payment is $5.00.');
        return;
      }
    } else if (effectiveMode === 'skip') {
      cents = Math.max(100, skipChargeCents);
    } else if (effectiveMode === 'past_due') {
      cents = pastDueCents;
      if (cents < 500) {
        toast.error('Minimum payment is $5.00.');
        return;
      }
    } else if (effectiveMode === 'payoff') {
      /** Exactly the previewed figure: the server refuses a payoff whose amount no longer matches
       *  (`payoff_amount_changed`) rather than charge a number the customer didn't see. */
      cents = Math.round(Number(payoffAmountCents) || 0);
    } else {
      cents = Math.round(parseFloat(amount || 0) * 100) || 0;
    }
    if (cents <= 0 || !tripId) {
      toast.error('Enter an amount');
      return;
    }
    if (!useNewCard && !selectedCardId) {
      toast.error('Please select a card');
      return;
    }
    setCharging(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');

      let pmId = selectedCardId;
      if (useNewCard) {
        if (!stripe || !elements) throw new Error('Payment system not ready');
        const setupRes = await fetch(apiUrlFn('setup-intent'), {
          method: 'POST',
          headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
          body: JSON.stringify({ booking_id: tripId })
        });
        const setupData = await safeResJson(setupRes);
        if (!setupRes.ok) throw new Error(setupData?.error || 'Failed to set up card');
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('Card input not found');
        const { error: confirmErr, setupIntent } = await stripe.confirmCardSetup(setupData.client_secret, {
          payment_method: { card: cardElement }
        });
        if (confirmErr) throw new Error(formatStripeErrorMessage(confirmErr) || confirmErr.message);
        pmId = setupIntent?.payment_method;
      }

      if (!payNonceRef.current) {
        payNonceRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }
      const requestBody = {
        booking_id: tripId,
        amount_cents: cents,
        payment_method_id: pmId,
        client_request_id: payNonceRef.current,
        /** New trip card uploaded inline ⇒ "use a new card" implies the customer expects this to
         *  be their card going forward. Piggy-back on the explicit checkbox or the new-card flag
         *  so we update the subscription default in one round-trip. */
        set_default_payment_method: setDefaultPm || (useNewCard && Boolean(pmId)),
        /** Set only when the user clicked "Pay past-due" — server pays via /invoices/{id}/pay. */
        pay_invoice_id: chargeInvoiceId || undefined,
      };
      if (!chargeInvoiceId && effectiveMode === 'skip' && payAheadSubMode === 'custom') {
        /** Pay-ahead → Custom Amount: greedy split. Server splits `amount_cents` into N full
         *  installments (skip-style trial_end push) + P leftover (sub-scoped invoice item credit
         *  on the next cycle). Sub-scoped credit (not customer.balance) so multi-trip users
         *  don't leak Trip A credit onto Trip B. */
        requestBody.mode = 'pay_ahead_custom';
      } else if (!chargeInvoiceId && effectiveMode === 'skip') {
        requestBody.mode = 'skip_installments';
        requestBody.skip_count = Math.max(1, Math.floor(skipCount));
      } else if (!chargeInvoiceId && effectiveMode === 'past_due') {
        requestBody.mode = 'past_due';
      } else if (!chargeInvoiceId && effectiveMode === 'payoff') {
        requestBody.mode = 'payoff';
      }
      const res = await fetch(apiUrlFn('charge-early-payment'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify(requestBody)
      });
      const data = await safeResJson(res);
      if (!res.ok) {
        /** Special-case the deadline-overflow path so the customer sees a one-tap "pay off
         *  remaining" CTA in the modal instead of a dead-end toast. */
        if (data?.code === 'would_exceed_deadline') {
          setPreview((p) => ({
            ...(p || {}),
            would_exceed_deadline: true,
            payoff_amount_cents: data.payoff_amount_cents ?? p?.payoff_amount_cents ?? remainingCentsHint,
          }));
          toast.error(data.error || 'Skipping that many would push past your deadline.');
          return;
        }
        /** Server blocks any non-invoice payment when there's an open/past-due invoice — a fresh PI
         *  on top of an unpaid invoice doesn't actually settle it (Stripe keeps retrying on the
         *  customer's card). Customer gets charged twice. Surface the reason and let them click the
         *  Pay-past-due banner above. `past_due_blocks_skip` is the legacy code (pre-fix); keep it
         *  for backwards compat with any older deployed function still around. */
        if (['exceeds_past_due', 'nothing_past_due', 'invoice_payment_pending'].includes(data?.code)) {
          toast.error(data.error || 'That amount cannot be applied to your past-due balance right now.');
          return;
        }
        if (data?.code === 'past_due_blocks_payment' || data?.code === 'past_due_blocks_skip') {
          toast.error(data.error || 'Pay your past-due balance first.');
          return;
        }
        /** Stripe has drafted the next installment and charges it within the hour. A payment now
         *  would be collected again by that installment, so the server waits it out. */
        if (data?.code === 'installment_processing') {
          toast.error(data.error || 'Your next installment is being processed. Please try again once it goes through.');
          return;
        }
        /** Booking-level lock contention (double-click, two-tab, parallel request). The
         *  server's lease has a TTL so the contention will clear on its own; nudge the
         *  customer to wait rather than surfacing a generic "Payment failed" toast. */
        if (data?.code === 'payment_in_progress') {
          toast.error(data.error || 'Another payment for this trip is already running. Please wait a moment and try again.');
          return;
        }
        /** The balance moved since the preview (a cycle billed, credit was applied). Show the new
         *  figure and let the customer confirm it; nothing was charged. */
        if (data?.code === 'payoff_amount_changed') {
          setPreview((p) => ({
            ...(p || {}),
            payoff_amount_cents: data.payoff_amount_cents,
            payoff_credit_cents: data.payoff_credit_cents ?? p?.payoff_credit_cents,
          }));
          payNonceRef.current = null;
          toast.error(data.error || 'Your payoff amount changed. Please review it and try again.');
          return;
        }
        throw new Error(formatStripeErrorMessage(data) || data.error || 'Payment failed');
      }
      payNonceRef.current = null;
      let successMsg;
      if (data.trip_fully_paid) {
        successMsg = Number(data.defera_credit_applied_cents) > 0
          ? `Trip fully paid! Your Defera Credit covered the last $${fmtCurrency(Number(data.defera_credit_applied_cents))}.`
          : 'Trip fully paid!';
      } else if (chargeInvoiceId) {
        successMsg = 'Past-due invoice paid. Schedule updated.';
      } else if (effectiveMode === 'skip' && payAheadSubMode === 'custom') {
        /** Pay-ahead → Custom Amount: the server returned `skip_count` (full installments
         *  covered) and `partial_credit_cents` (leftover applied to the next cycle). Build a
         *  single sentence that tells the whole story without listing both numbers separately —
         *  customers don't think in terms of "8 installments + $26 credit", they think
         *  "I paid $722 and it covered me through August". */
        const sc = Number(data.skip_count) || 0;
        const pc = Number(data.partial_credit_cents) || 0;
        if (sc > 0 && pc > 0) {
          successMsg = sc === 1
            ? `Next installment paid early — $${fmtCurrency(pc)} credit applied to your next payment.`
            : `${sc} upcoming installments paid early — $${fmtCurrency(pc)} credit applied to your next payment.`;
        } else if (sc > 0) {
          successMsg = sc === 1 ? 'Next installment paid early.' : `${sc} upcoming installments paid early.`;
        } else if (pc > 0) {
          successMsg = `Credit of $${fmtCurrency(pc)} applied to your next payment.`;
        } else {
          successMsg = 'Payment successful. Schedule updated.';
        }
      } else if (effectiveMode === 'past_due') {
        const applied = Number(data.applied_to_past_due_cents) || cents;
        const still = Number(data.past_due_remaining_cents) || 0;
        successMsg = data.plan_resumed
          ? 'Past-due balance cleared — your plan resumes on its normal schedule.'
          : `${fmtCurrency(applied)} applied to your past-due balance — ${fmtCurrency(still)} is still past due.`;
        if (data.credit_note_sync_pending) {
          toast.message('Payment received. We are finishing the bookkeeping on our side — your balance already reflects it.');
        }
      } else if (effectiveMode === 'skip') {
        const sc = data.skip_count || skipCount;
        successMsg = sc === 1 ? 'Next installment paid early.' : `${sc} upcoming installments paid early.`;
      } else {
        successMsg = 'Payment successful. Schedule updated.';
      }
      toast.success(successMsg);
      /** Skip-mode `data.proposed_schedule` is null on the server (we don't recompute the schedule
       *  for skip — we just push trial_end forward), so fall back to the preview's
       *  `proposed_schedule` (which buildSkipInstallmentsProposal computed) to power the
       *  before/after comparison the parent shows after the modal closes. */
      const finalProposed = data.proposed_schedule ?? preview?.proposed_schedule ?? null;
      onSuccess?.({ proposed_schedule: finalProposed, current_schedule: preview?.current_schedule });
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Payment failed');
    } finally {
      setCharging(false);
      setPayPastDueInvoiceId(null);
    }
  };

  const handlePay = () => submitCharge();
  const handlePayoffSwitch = () => {
    setMode('payoff');
    resetPreview();
    userPickedModeRef.current = true;
  };
  const handlePayPastDue = () => {
    /** When there's exactly one past-due invoice, pay that one directly. With multiple, pay the
     *  oldest first (server validates ownership + status either way). */
    const target = openInvoices[0];
    if (!target?.id) {
      toast.error('No past-due invoice found');
      return;
    }
    setPayPastDueInvoiceId(target.id);
    /** Schedule endpoint returns `amount_remaining_cents` (Stripe's invoice.amount_remaining).
     *  The server re-fetches the invoice from Stripe and uses its actual amount for the charge,
     *  so the value we send here is only used for the local idempotency key + record bookkeeping
     *  in Branch B fallback. Still, send the right one — guards against the (legacy) case where
     *  the server can't reach Stripe and falls through to a default-to-100c custom-mode charge. */
    submitCharge({ chargeInvoiceId: target.id, chargeCents: target.amount_remaining_cents ?? target.amount_cents });
  };

  return (
    <div className="space-y-4">
      {/* Past-due banner: surfaces open Stripe invoices ahead of the ad-hoc input so customers
          land on the most-pressing action first. Pays via /invoices/{id}/pay (clears past_due in
          one step) instead of stranding the open invoice next to a fresh PaymentIntent. */}
      {hasPastDue && (() => {
        /** When multiple invoices are unpaid, the button pays them ONE AT A TIME (oldest first —
         *  schedule endpoint sorts that way). Show the single-invoice amount on the button so the
         *  customer isn't surprised when only that amount is charged. The total stays in the
         *  headline so they know what's still owed in aggregate. After each pay the modal closes
         *  and the banner re-appears with the next oldest until cleared. */
        const oldestInvoice = openInvoices[0];
        const oldestInvoiceCents = Number(oldestInvoice?.amount_remaining_cents ?? oldestInvoice?.amount_cents ?? 0);
        const buttonLabelCents = openInvoices.length > 1 ? oldestInvoiceCents : openInvoicesTotalCents;
        return (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.06] p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-rose-300 text-xs font-normal uppercase tracking-wider">Past-due balance</p>
                <p className="text-rose-100 text-base font-normal mt-1">${fmtCurrency(openInvoicesTotalCents)}</p>
                <p className="text-rose-200/70 text-[11px] mt-1">
                  {openInvoices.length === 1
                    ? 'You have 1 unpaid invoice on this trip.'
                    : `You have ${openInvoices.length} unpaid invoices — pay them one at a time.`}
                </p>
              </div>
              <Button
                size="sm"
                type="button"
                onClick={handlePayPastDue}
                disabled={charging || loadingPm || (!useNewCard && !selectedCardId) || (useNewCard && !stripe)}
                className="bg-rose-500/90 text-white hover:bg-rose-500 rounded-full text-xs h-9 px-4"
              >
                {charging && payPastDueInvoiceId
                  ? <><Loader2 className="w-3 h-3 animate-spin mr-1.5" />Paying…</>
                  : `Pay $${fmtCurrency(buttonLabelCents)}${openInvoices.length > 1 ? ' (oldest)' : ' past-due'}`}
              </Button>
            </div>
            <p className="text-rose-200/50 text-[10px]">
              {pastDueTabEnabled
                ? 'Pay it in full here, or pay part of it from the Past due tab below.'
                : 'Pay this first to unlock the rest of the options below.'}
            </p>
          </div>
        );
      })()}

      {/* Mode selector — three options, "skip" first because it's what most customers asking
          about early payment are actually after (prolong the next charge date, not shrink the
          per-payment amount). "Pay ahead" is disabled if the subscription has no locked recurring
          price (rare edge case — fall back to custom in that case). All three are disabled when
          there's a past-due open invoice (server enforces via `past_due_blocks_payment`) — a fresh
          PI can't settle an open invoice, only `/invoices/{id}/pay` can. */}
      <div className={`grid ${pastDueTabEnabled ? 'grid-cols-4' : 'grid-cols-3'} gap-1.5 rounded-2xl border border-white/10 bg-white/[0.03] p-1`}>
        {[
          /** Stripe-rail partial catch-up. Only rendered while something is past due. */
          ...(pastDueTabEnabled ? [{ id: 'past_due', label: 'Past due', icon: AlertCircle, disabled: false, disabledReason: null }] : []),
          /** All tabs are disabled when there's a past-due open invoice — the only way to clear
           *  past_due is via the banner above (`/invoices/{id}/pay`). A fresh PI on top of the
           *  open invoice would be charged but Stripe would still retry the failed invoice on the
           *  customer's card → double charge. Server enforces via `past_due_blocks_payment`. */
          { id: 'skip', label: 'Pay ahead', icon: FastForward, disabled: stripeUnitAmountCents <= 0 || allTabsDisabledByPastDue, disabledReason: allTabsDisabledByPastDue ? 'Pay your past-due balance first' : null },
          /** "Principal" (formerly "Custom") — payments here go toward the overall balance: the
           *  per-cycle amount shrinks at the same dates. Renamed for clarity vs. the new
           *  Pay-ahead → Custom Amount sub-tab, which targets specific upcoming installments. */
          { id: 'custom', label: 'Principal', icon: DollarSign, disabled: allTabsDisabledByPastDue, disabledReason: allTabsDisabledByPastDue ? 'Pay your past-due balance first' : null },
          { id: 'payoff', label: 'Pay off', icon: CheckCircle, disabled: (payoffAmountCents <= 0 && !payoffCoveredByCredit) || allTabsDisabledByPastDue, disabledReason: allTabsDisabledByPastDue ? 'Pay your past-due balance first' : null },
        ].map(({ id, label, icon: Icon, disabled, disabledReason }) => (
          <button
            key={id}
            type="button"
            disabled={disabled || charging}
            title={disabledReason || undefined}
            onClick={() => {
              setMode(id);
              resetPreview();
              payNonceRef.current = null;
              userPickedModeRef.current = true;
            }}
            className={`flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-medium transition-all ${
              mode === id
                ? 'bg-brand-800 text-white shadow-[0_0_14px_-6px_rgba(10,18,51,0.8)]'
                : 'text-white/50 hover:text-white/80 hover:bg-white/[0.04]'
            } ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {mode === 'skip' && (
        <div className="space-y-3">
          {/** Pay-ahead sub-modes: integer stepper vs. typed dollar amount. Both end up pushing
           *   Stripe `trial_end` forward by N intervals — they only differ in how N is chosen.
           *   The custom-amount sub-mode additionally handles a partial leftover (P < unit_amount)
           *   by creating a sub-scoped Stripe invoice item credit against the next remaining cycle. */}
          {/** Borderless pill — sits inside the Pay-ahead gradient card. An outer border here
           *   would compete with the card's edge and (because `border-white/8` is not a valid
           *   Tailwind class — default scale is 5/10/15) used to render as harsh full-white. */}
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-white/[0.04] p-1">
            {[
              { id: 'installments', label: 'Installments' },
              { id: 'custom', label: 'Custom amount' },
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                disabled={charging}
                onClick={() => {
                  setPayAheadSubMode(id);
                  resetPreview();
                  payNonceRef.current = null;
                }}
                className={cn(
                  'rounded-lg py-2 text-[11px] font-medium transition-all',
                  payAheadSubMode === id
                    ? 'bg-brand-800/90 text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]'
                    : 'text-white/55 hover:text-white/80 hover:bg-white/[0.04]'
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {payAheadSubMode === 'installments' && (
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] via-white/[0.02] to-transparent p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-white text-sm font-medium leading-tight">Pay upcoming installments early</p>
                  <p className="text-white/60 text-[11px] font-normal mt-1 leading-snug">
                    Same amount per payment. Your next auto-charge moves out so you have more breathing room.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 pt-1">
                <div>
                  <p className="text-white/40 text-[10px] uppercase tracking-wider">Skip how many?</p>
                  {stripeUnitAmountCents > 0 && (
                    <p className="text-white/30 text-[10px] mt-0.5">${fmtCurrency(stripeUnitAmountCents)} each</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setSkipCount((n) => Math.max(1, n - 1)); payNonceRef.current = null; }}
                    disabled={skipCount <= 1 || charging}
                    className="w-10 h-10 rounded-full border border-white/15 bg-white/5 text-white flex items-center justify-center hover:bg-white/10 hover:border-brand-500/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-white text-base font-medium tabular-nums w-7 text-center">{skipCount}</span>
                  <button
                    type="button"
                    onClick={() => { setSkipCount((n) => Math.min(upcomingCap, n + 1)); payNonceRef.current = null; }}
                    disabled={skipCount >= upcomingCap || charging}
                    className="w-10 h-10 rounded-full border border-white/15 bg-white/5 text-white flex items-center justify-center hover:bg-white/10 hover:border-brand-500/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-white/5 pt-3">
                <span className="text-white/60 text-xs">You'll pay today</span>
                <span className="text-white text-base font-medium tabular-nums">${fmtCurrency(skipChargeCents)}</span>
              </div>
              {preview?.new_next_charge_date && preview?.current_next_charge_date && !wouldExceedDeadline && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-white/40">Next charge moves</span>
                  <span className="text-brand-300 tabular-nums">
                    {format(parseISO(preview.current_next_charge_date), 'MMM d')}
                    <ArrowRight className="w-3 h-3 inline mx-1.5 -mt-0.5 text-white/30" />
                    {format(parseISO(preview.new_next_charge_date), 'MMM d')}
                  </span>
                </div>
              )}
            </div>
          )}

          {payAheadSubMode === 'custom' && (
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] via-white/[0.02] to-transparent p-4 space-y-3">
              <div>
                <p className="text-white text-sm font-medium leading-tight">Pay a custom amount ahead</p>
                <p className="text-white/60 text-[11px] font-normal mt-1 leading-snug">
                  We'll cover as many full installments as possible, then credit any leftover toward your next payment.
                </p>
              </div>
              <div>
                <label className="block text-white/40 text-[10px] uppercase tracking-wider mb-1.5">Amount to pay ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={payAheadCustomAmount}
                    onChange={(e) => { setPayAheadCustomAmount(e.target.value); resetPreview(); payNonceRef.current = null; }}
                    placeholder="0.00"
                    className="pl-9 h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-brand-500/40"
                  />
                </div>
                {stripeUnitAmountCents > 0 && (
                  <p className="text-white/30 text-[10px] mt-1.5">Each installment is ${fmtCurrency(stripeUnitAmountCents)}.</p>
                )}
              </div>
              {/** Greedy breakdown — visible whenever the preview has come back with a non-zero
               *  amount and we know how the server split it. Reads:
               *    "$X covers N installments — $P credit applied to your next payment"
               *  Either field can be 0 (covers integer multiple of unit_amount, or partial-only). */}
              {preview?.mode === 'pay_ahead_custom' && !preview?.exceeds_remaining && payAheadCustomCents > 0 && (
                <div className="rounded-xl border border-brand-500/20 bg-brand-500/[0.05] p-3 space-y-1.5">
                  {Number(preview.skip_count) > 0 && (
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-white/60">Covers</span>
                      <span className="text-white/90 tabular-nums">
                        {preview.skip_count} {preview.skip_count === 1 ? 'installment' : 'installments'} (${fmtCurrency(Number(preview.skip_count) * stripeUnitAmountCents)})
                      </span>
                    </div>
                  )}
                  {Number(preview.partial_credit_cents) > 0 && (
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-white/60">Credit on next payment</span>
                      <span className="text-emerald-300 tabular-nums">${fmtCurrency(Number(preview.partial_credit_cents))}</span>
                    </div>
                  )}
                  {preview?.new_next_charge_date && preview?.current_next_charge_date && !wouldExceedDeadline && Number(preview.skip_count) > 0 && (
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-white/60">Next charge moves</span>
                      <span className="text-brand-300 tabular-nums">
                        {format(parseISO(preview.current_next_charge_date), 'MMM d')}
                        <ArrowRight className="w-3 h-3 inline mx-1.5 -mt-0.5 text-white/30" />
                        {format(parseISO(preview.new_next_charge_date), 'MMM d')}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between border-t border-white/5 pt-3">
                <span className="text-white/60 text-xs">You'll pay today</span>
                <span className="text-white text-base font-medium tabular-nums">${fmtCurrency(payAheadCustomCents)}</span>
              </div>
              {payAheadCustomCents > 0 && payAheadCustomCents < 500 && (
                <p className="text-amber-300/80 text-[10px]">Minimum payment is $5.00.</p>
              )}
              {/** Exceeds-remaining CTA. Pre-fix the server's `code: 'exceeds_remaining'` got
               *   bucketed into the generic `skipPreviewError` ("Pay-ahead isn't available…
               *   Switch to Principal") even though the server message said "use Pay off".
               *   Now we surface it as preview state and render a Pay-off CTA, matching the
               *   `would_exceed_deadline` UX below and the NMI form. */}
              {preview?.exceeds_remaining && preview?.mode === 'pay_ahead_custom' && payoffAmountCents > 0 && (
                <div className="pt-3 border-t border-white/5 space-y-2">
                  <p className="text-amber-300/90 text-[11px]">
                    That amount is more than your remaining balance. Use Pay off to settle the trip.
                  </p>
                  <Button
                    type="button"
                    onClick={handlePayoffSwitch}
                    disabled={charging}
                    className={cn(MODAL_PRIMARY_BTN_CLASS, 'w-full h-10 text-xs')}
                  >
                    Pay off ${fmtCurrency(payoffAmountCents)}
                  </Button>
                </div>
              )}
            </div>
          )}
          {wouldExceedDeadline && (
            <div className="rounded-xl border border-brand-500/30 bg-brand-500/10 p-3 space-y-2">
              <p className="text-brand-300 text-xs font-normal">That would push past your trip's payment deadline.</p>
              <p className="text-brand-200/70 text-[11px]">
                {serverMaxSkip > 0
                  ? `You can pay ahead up to ${serverMaxSkip} installment${serverMaxSkip === 1 ? '' : 's'}. Or pay off the entire remaining balance now to be done.`
                  : 'Pay off the entire remaining balance now to be done.'}
              </p>
              {payoffAmountCents > 0 && (
                <Button
                  type="button"
                  onClick={handlePayoffSwitch}
                  disabled={charging}
                  className={cn(MODAL_PRIMARY_BTN_CLASS, 'w-full h-10 text-xs')}
                >
                  Pay off ${fmtCurrency(payoffAmountCents)}
                </Button>
              )}
            </div>
          )}
          {skipPreviewError && !wouldExceedDeadline && (
            <div className="rounded-xl border border-brand-500/30 bg-brand-500/10 p-3 space-y-2">
              <p className="text-brand-300 text-xs font-normal">Pay-ahead isn't available for this trip.</p>
              <p className="text-brand-200/70 text-[11px]">{skipPreviewError}</p>
              <p className="text-brand-200/60 text-[11px]">Try the Principal tab to pay any amount toward your balance.</p>
              <Button
                type="button"
                onClick={() => { setMode('custom'); setSkipPreviewError(null); userPickedModeRef.current = true; }}
                disabled={charging}
                className={cn(MODAL_GHOST_BTN_CLASS, 'w-full h-10 text-xs')}
              >
                Switch to Principal
              </Button>
            </div>
          )}
        </div>
      )}

      {mode === 'past_due' && (
        <div className="rounded-2xl border border-rose-500/25 bg-rose-500/[0.05] p-4 space-y-3">
          <div>
            <p className="text-rose-100 text-sm font-medium">Pay toward what&apos;s past due</p>
            <p className="text-rose-200/60 text-[11px] leading-snug mt-1">
              Any amount from $5 up to ${fmtCurrency(openInvoicesTotalCents)}. It goes to your oldest missed payment first. Whatever you don&apos;t cover stays past due and keeps retrying; once nothing is past due, your plan resumes on its normal schedule.
            </p>
          </div>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              type="number"
              min="5"
              step="0.01"
              value={pastDueAmount}
              onChange={(e) => { setPastDueAmount(e.target.value); payNonceRef.current = null; }}
              placeholder="0.00"
              aria-label="Amount toward past due"
              className="pl-9 h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-rose-500/40"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              disabled={charging}
              onClick={() => { setPastDueAmount((openInvoicesTotalCents / 100).toFixed(2)); payNonceRef.current = null; }}
              className="text-[11px] text-rose-200/80 hover:text-rose-100 underline underline-offset-2 disabled:opacity-50"
            >
              Pay the full past-due balance (${fmtCurrency(openInvoicesTotalCents)})
            </button>
            {loadingPreview && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/40" />}
          </div>
          {pastDueCents > 0 && pastDueCents < 500 && (
            <p className="text-amber-300/80 text-[10px]">Minimum payment is $5.00.</p>
          )}
          {pastDueCents > openInvoicesTotalCents && (
            <p className="text-amber-300/80 text-[10px]">That&apos;s more than what&apos;s past due (${fmtCurrency(openInvoicesTotalCents)}). Pay the past-due amount here, then use Pay ahead or Principal for anything extra.</p>
          )}
          {preview?.mode === 'past_due' && preview.preview_error && pastDueCents >= 500 && pastDueCents <= openInvoicesTotalCents && (
            <p className="text-amber-300/80 text-[11px]">{preview.error}</p>
          )}
          {preview?.mode === 'past_due' && !preview.preview_error && previewAmountCents === pastDueCents && (
            <div className="border-t border-rose-500/15 pt-3 space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-rose-200/70">Applied to past due</span>
                <span className="text-white tabular-nums">${fmtCurrency(preview.applied_to_past_due_cents)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-rose-200/70">Still past due after</span>
                <span className="text-white tabular-nums">${fmtCurrency(preview.past_due_remaining_cents)}</span>
              </div>
              <p className="text-rose-200/60 text-[11px] pt-1">
                {preview.past_due_fully_cured
                  ? 'Nothing left past due — your plan resumes on schedule.'
                  : preview.settle_count > 0
                    ? `${preview.settle_count} missed payment${preview.settle_count === 1 ? '' : 's'} settled in full; the rest stays past due until it's paid.`
                    : 'Reduces your oldest missed payment; it stays past due until the rest is paid.'}
              </p>
            </div>
          )}
        </div>
      )}

      {mode === 'custom' && (
        <div>
          <label className="block text-white/50 text-xs mb-1">Amount to pay ($)</label>
          {/** Copy intentionally avoids the word "shrink" — customers (OCNBDW canary) read that
           *   as an invitation to type $1–$3 round-ups against a fractional "Each charge" number,
           *   then watched the per-cycle amount drift to a new fractional number and tried again.
           *   Each tiny custom payment creates a fresh Stripe Price and swaps the sub onto it
           *   (`applyCustomScheduleUpdate` in defera-zero-charge-early-payment), so the loop is
           *   self-perpetuating. We now nudge them toward Pay-ahead for clean numbers. */}
          <p className="text-white/30 text-[10px] mb-2">
            Pay any amount toward your balance. To pay one installment ahead with no schedule churn, use Pay ahead.
          </p>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              type="number"
              min="5"
              step="1"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); resetPreview(); payNonceRef.current = null; }}
              placeholder="0.00"
              className="pl-9 h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-brand-500/40"
            />
          </div>
          {/** Quick-fill: one installment. Gives the round-up reflex a clean target so customers
           *   don't end up typing $2 / $3 to "fix" a fractional per-cycle amount. Only rendered
           *   when we know the locked per-cycle amount (skip mode is available). */}
          {stripeUnitAmountCents > 0 && stripeUnitAmountCents <= (remainingCentsHint || stripeUnitAmountCents) && (
            <button
              type="button"
              onClick={() => {
                setAmount((stripeUnitAmountCents / 100).toFixed(2));
                resetPreview();
                payNonceRef.current = null;
              }}
              disabled={charging}
              className="mt-2 text-[11px] text-brand-300/80 hover:text-brand-200 underline underline-offset-2 disabled:opacity-50"
            >
              Match one installment (${fmtCurrency(stripeUnitAmountCents)})
            </button>
          )}
          {parseFloat(amount) > 0 && parseFloat(amount) < 5 && (
            <p className="mt-1.5 text-amber-300/80 text-[10px]">
              Small payments (under $5) leave fractional per-cycle amounts behind. Try Pay ahead, or round up to at least $5.
            </p>
          )}
        </div>
      )}

      {mode === 'payoff' && (
        <div className="rounded-2xl border border-brand-500/25 bg-gradient-to-br from-brand-600/15 via-brand-600/[0.06] to-transparent p-4 space-y-2">
          <p className="text-brand-200 text-sm font-medium">Pay off your trip in full</p>
          <p className="text-brand-200/60 text-[11px] leading-snug">
            {payoffCoveredByCredit
              ? 'Your Defera Credit already covers everything left on this trip. Nothing more will be charged, and your remaining installments will each come to $0.'
              : payoffCreditCents > 0
                ? 'Charges what\'s left after your Defera Credit and ends auto-installments — no more upcoming payments.'
                : 'Charges your remaining balance now and ends auto-installments — no more upcoming payments.'}
          </p>
          {payoffCreditCents > 0 && (
            /** The two numbers the customer has seen elsewhere (the balance, and what's due), shown
             *  as one sum so neither reads as the charge by mistake. */
            <div className="border-t border-brand-500/15 pt-3 space-y-1.5 text-xs tabular-nums">
              <div className="flex items-center justify-between">
                <span className="text-brand-200/70">Remaining balance</span>
                <span className="text-brand-100">${fmtCurrency(payoffAmountCents + payoffCreditCents)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-emerald-300/80">Defera Credit already on this trip</span>
                <span className="text-emerald-300/90">−${fmtCurrency(payoffCreditCents)}</span>
              </div>
            </div>
          )}
          {preview?.payoff_blocked_reason === 'credit_on_paused_plan' && (
            <p className="text-amber-300/90 text-[11px] leading-snug">
              {"Your plan is paused, so your Defera Credit can't be applied to a payoff here. Restart your plan, or contact us and we'll settle it with your credit applied."}
            </p>
          )}
          {!payoffCoveredByCredit && (
            <div className="flex items-center justify-between border-t border-brand-500/15 pt-3">
              <span className="text-brand-200/70 text-xs">You'll pay today</span>
              <span className="text-white text-base font-medium tabular-nums">
                {loadingPreview && preview?.mode !== 'payoff' ? <Loader2 className="w-4 h-4 animate-spin text-white/40" /> : `$${fmtCurrency(payoffAmountCents)}`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Payment method selector */}
      <div className={paymentCardGradient.container}>
        <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-4">Charge to</p>
        {loadingPm ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
        ) : (
          <>
            {paymentMethods.length > 0 && !useNewCard ? (
              <div className="space-y-2.5">
                {paymentMethods.map((pm) => (
                  <SavedPaymentMethodCard
                    key={pm.id}
                    {...stripePaymentMethodToCardProps(pm)}
                    selected={selectedCardId === pm.id}
                    onClick={() => { setSelectedCardId(pm.id); setUseNewCard(false); }}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setUseNewCard(true)}
                  className={paymentCardGradient.addNewCard}
                >
                  <div className={paymentCardGradient.addNewCardIcon}>
                    <CreditCard className="w-5 h-5 text-white/40" />
                  </div>
                  Use a new card
                </button>
              </div>
            ) : (
              <div className="space-y-4 min-w-0">
                <div className={paymentCardGradient.cardElementWrapper}>
                  <CardElement options={CARD_ELEMENT_OPTIONS} />
                </div>
                {paymentMethods.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setUseNewCard(false); setSelectedCardId(paymentMethods[0]?.id); }}
                    className={paymentCardGradient.useSavedCardLink}
                  >
                    ← Use saved card instead
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {/* Default-card opt-in. Only meaningful when picking a saved card; new cards are implicitly
            promoted to default server-side (the customer just typed it in for this trip, so they
            clearly intend to use it). */}
        {!useNewCard && !loadingPm && paymentMethods.length > 0 && selectedCardId && selectedCardId !== subscriptionDefaultPaymentMethodId && (
          <label className="mt-4 flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={setDefaultPm}
              onChange={(e) => setSetDefaultPm(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 text-brand-500 focus:ring-brand-500/30"
            />
            <span className="text-white/70 text-xs leading-snug">
              Use this card for future automatic installments on this trip.
            </span>
          </label>
        )}
      </div>

      {/* Preview / confirm area. Skip mode auto-loads preview on stepper change; custom and
          payoff are explicit-trigger so the customer is in control. */}
      {(() => {
        if (mode === 'past_due') {
          const previewReady = preview?.mode === 'past_due' && !preview.preview_error && previewAmountCents === pastDueCents;
          const canPay = pastDueCents >= 500
            && pastDueCents <= openInvoicesTotalCents
            && previewReady
            && !loadingPreview
            && !charging
            && !(useNewCard && !stripe)
            && (useNewCard || Boolean(selectedCardId));
          return (
            <div className="flex gap-2.5 pt-2">
              <Button
                onClick={handlePay}
                disabled={!canPay}
                className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1')}
              >
                {charging ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Charging…</> : `Pay $${fmtCurrency(pastDueCents)} toward past due`}
              </Button>
            </div>
          );
        }
        const chargeCentsForCta = mode === 'skip'
          ? (payAheadSubMode === 'custom' ? payAheadCustomCents : skipChargeCents)
          : mode === 'payoff'
            ? payoffAmountCents
            : Math.round((parseFloat(amount) || 0) * 100);
        const ctaLabel = chargeCentsForCta > 0
          ? `Pay $${fmtCurrency(chargeCentsForCta)}`
          : 'Pay';

        if (mode === 'skip') {
          if (loadingPreview && !preview) {
            return (
              <div className="flex items-center justify-center py-4 text-white/40 text-xs">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading schedule…
              </div>
            );
          }
          /** `exceeds_remaining` previews carry only `payoff_amount_cents` + the error code —
           *  no `current_schedule` / `proposed_schedule`. Without this gate, the After-schedule
           *  block renders with `undefined × $0.00` placeholders. The Pay-off CTA above already
           *  guides the user; suppress the schedule diff entirely. */
          if (!preview || wouldExceedDeadline || preview?.exceeds_remaining) return null;
          return (
            <>
              <div className="space-y-3">
                <p className="text-white/60 text-xs">Your schedule after this payment.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-3">
                    <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Current</p>
                    <p className="text-white/50 text-xs font-normal mb-2">
                      {scheduleSummary(preview.current_schedule, { summarizeNonUniform: true })}
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                      {(preview.current_schedule?.dates || []).map((d, i) => {
                        /** Strikethrough source depends on sub-mode:
                         *    installments  → first N rows are skipped (N from stepper / response)
                         *    pay_ahead_custom → server-computed `skip_count`: how many full
                         *                       installments the typed amount covered. The next
                         *                       row (idx = skip_count) becomes a partial-credit
                         *                       row in the "After" column, not strikethrough.   */
                        const struckCount = preview.mode === 'pay_ahead_custom'
                          ? Number(preview.skip_count) || 0
                          : skipCount;
                        const isStruck = i < struckCount;
                        return (
                          <div key={i} className={`flex justify-between text-[11px] ${isStruck ? 'line-through opacity-50' : ''}`}>
                            <span className="text-white/25">{d?.date ? format(parseISO(d.date), 'MMM d') : '—'}</span>
                            <span className="text-white/35">${fmtCurrency(d?.amount_cents || 0)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="rounded-xl border border-brand-500/25 bg-brand-500/[0.06] p-3">
                    <p className="text-brand-300/80 text-[10px] uppercase tracking-wider mb-2">After</p>
                    {preview.trip_fully_paid ? (
                      <p className="text-brand-200/90 text-xs">Trip fully paid — no more payments due</p>
                    ) : (
                      <>
                        <p className="text-brand-200/90 text-xs font-normal mb-2">
                          {scheduleSummary(preview.proposed_schedule, { summarizeNonUniform: true })}
                        </p>
                        <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                          {(preview.proposed_schedule?.dates || []).map((d, i) => {
                            /** `credit_applied_cents` is non-zero on the first row when the server
                             *  split the typed amount into N full installments + P leftover; the
                             *  row shows the NET amount with a tiny "credit applied" label so the
                             *  customer can see "$87 → $61" + "$26 credit" without re-reading the
                             *  breakdown above. */
                            const gross = Number(d?.amount_cents) || 0;
                            const credit = Number(d?.credit_applied_cents) || 0;
                            const net = Math.max(0, gross - credit);
                            return (
                              <div key={i} className="flex items-center justify-between text-[11px]">
                                <span className="text-brand-300/50">{d?.date ? format(parseISO(d.date), 'MMM d') : '—'}</span>
                                <span className="tabular-nums">
                                  {credit > 0 ? (
                                    <>
                                      <span className="text-emerald-300/70 mr-1.5 text-[10px] uppercase tracking-wider">−${fmtCurrency(credit)} credit</span>
                                      <span className="text-brand-200/90">${fmtCurrency(net)}</span>
                                    </>
                                  ) : (
                                    <span className="text-brand-200/70">${fmtCurrency(gross)}</span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2.5 pt-2">
                <Button
                  onClick={handlePay}
                  disabled={
                    charging
                    || (useNewCard && !stripe)
                    || payAheadChargeCents <= 0
                    /** Block submit when the typed dollar amount is below the server's $5 floor.
                     *  Without this, the customer could click Pay while the warning is showing
                     *  and the server would reject with `below_minimum`. */
                    || (payAheadSubMode === 'custom' && payAheadCustomCents < 500)
                    /** Block submit when a preview is still in flight — the breakdown above is
                     *  stale until it returns, and the server uses the typed amount to compute
                     *  the greedy split. Pre-fix you could click Pay before the preview landed
                     *  and confirm against an outdated breakdown. */
                    || loadingPreview
                    /** Block submit when the preview's amount disagrees with what the user is
                     *  about to confirm (mid-debounce typing). The 500ms debounce on Custom
                     *  Amount means there's a window where `payAheadCustomCents` is the new
                     *  value but `preview` reflects the old one. */
                    || (payAheadSubMode === 'custom' && previewAmountCents !== null && previewAmountCents !== payAheadCustomCents)
                    /** Block submit on the exceeds-remaining state — the CTA below offers Pay
                     *  off instead. */
                    || (payAheadSubMode === 'custom' && preview?.exceeds_remaining)
                  }
                  className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1')}
                >
                  {charging ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Charging…</> : ctaLabel}
                </Button>
              </div>
            </>
          );
        }

        if (mode === 'payoff') {
          return (
            <div className="flex gap-2.5 pt-2">
              <Button
                onClick={handlePay}
                disabled={charging || (useNewCard && !stripe) || payoffAmountCents <= 0 || (loadingPreview && preview?.mode !== 'payoff') || preview?.payoff_blocked_reason === 'credit_on_paused_plan'}
                className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1')}
              >
                {charging ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Charging…</> : ctaLabel}
              </Button>
            </div>
          );
        }

        if (!preview) {
          return (
            <Button
              onClick={() => fetchPreview()}
              disabled={loadingPreview || !amount || parseFloat(amount) <= 0}
              className={cn(MODAL_PRIMARY_BTN_CLASS, 'w-full')}
            >
              {loadingPreview ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</> : 'Preview new schedule'}
            </Button>
          );
        }

        /** Principal mode: customer typed amount → clicked Preview → preview returned.
         *  If they then edit `amount` to a different value, the input setter calls
         *  `resetPreview()` so the confirm view goes away and they have to click
         *  Preview again. But if `previewAmountCents` somehow lags `amount` (e.g.,
         *  a race-fix dropped an in-flight preview), block Pay until they re-preview
         *  the new amount. Mirror NMI's submit guards. */
        const principalCents = Math.round((parseFloat(amount) || 0) * 100);
        const principalAmountMismatch = previewAmountCents !== null && previewAmountCents !== principalCents;

        return (
          <>
            <div className="space-y-3">
              <p className="text-white/60 text-xs">Current vs new schedule. Confirm to charge.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-3">
                  <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Current</p>
                  <p className="text-white/50 text-xs font-normal mb-2">
                    {scheduleSummary(preview.current_schedule, { summarizeNonUniform: true })}
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                    {(preview.current_schedule?.dates || []).map((d, i) => (
                      <div key={i} className="flex justify-between text-[11px]">
                        <span className="text-white/25">{d?.date ? format(parseISO(d.date), 'MMM d') : '—'}</span>
                        <span className="text-white/35">${fmtCurrency(d?.amount_cents || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-brand-500/25 bg-brand-500/[0.06] p-3">
                  <p className="text-brand-300/80 text-[10px] uppercase tracking-wider mb-2">New</p>
                  {preview.trip_fully_paid ? (
                    <p className="text-brand-200/90 text-xs">Trip fully paid — no more payments due</p>
                  ) : (
                    <>
                      <p className="text-brand-200/90 text-xs font-normal mb-2">
                        {scheduleSummary(preview.proposed_schedule, { summarizeNonUniform: true })}
                      </p>
                      <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                        {(preview.proposed_schedule?.dates || []).map((d, i) => (
                          <div key={i} className="flex justify-between text-[11px]">
                            <span className="text-brand-300/50">{d?.date ? format(parseISO(d.date), 'MMM d') : '—'}</span>
                            <span className="text-brand-200/70">${fmtCurrency(d?.amount_cents || 0)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <p className="text-white/30 text-[10px] text-center">Charging ${fmtAmount(parseFloat(amount) || 0)} to {useNewCard ? 'new card' : 'selected card'}</p>
            </div>
            <div className="flex gap-2.5 pt-2">
              <Button
                onClick={handlePay}
                disabled={charging || (useNewCard && !stripe) || loadingPreview || principalAmountMismatch}
                className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1')}
              >
                {charging ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Charging…</> : ctaLabel}
              </Button>
              <Button
                variant="ghost"
                onClick={() => resetPreview()}
                disabled={charging}
                className={cn(MODAL_GHOST_BTN_CLASS, 'px-5')}
              >
                Back
              </Button>
            </div>
          </>
        );
      })()}
    </div>
  );
}

/**
 * Display component for payment schedules (read-only)
 * Used in Carts, EditCart, and FlightDetailsModal
 * When isDeferaZero: Make a Payment and Update Card use inline flows (like Reserve); Request Extension becomes Change Schedule.
 */
export default function PaymentScheduleDisplay({ paymentSchedule, totalWithFee, tripId, tripStatus, departureDate, bookingDate, onExtensionRequested, onScheduleUpdated, onInternalCommentAdded, tripReference, userName, userEmail, tripOwnerUserId = null, route, showActions = true, cardClassName, fillHeight, isDeferaZero = false, isDeferaCapital = false, deferaZeroPaymentSuccessBookingId, onDeferaZeroPaymentSuccessHandled, flightCreditCents = 0, changeFlightHistory = [], archivedFlightData = null, currentFlightData = {}, currentGrandTotalCents = 0, insurancePremiumCents = 0, onTripPaidClick, tripPaidBusy = false }) {
  const ic = useInstallmentsCopy();
  const navigate = useNavigate();
  /** The loaders below outlive the screen when it closes mid-request; they
   *  check this after each await and stop. See useIsMounted. */
  const isMounted = useIsMounted();
  const isDeferaStripe = isDeferaZero || isDeferaCapital;
  /**
   * NMI track for Defera Capital. We branch on the booking's stored
   * processor (`flight_data._payment_processor`) — NOT the live
   * `defera_capital_processor` platform setting — so existing Stripe-side
   * bookings keep talking to Stripe even after the toggle flips. The toggle
   * only routes NEW checkouts; the per-booking processor is locked at the
   * moment the deposit charges.
   */
  const isDeferaCapitalNmi = isDeferaCapital && (currentFlightData?._payment_processor === 'nmi');
  /** Stripe rails (Defera Capital on Stripe, Defera Zero): the Make-a-Payment form offers the
   *  Past due (partial catch-up) tab; both rails' charge/preview endpoints implement mode='past_due'. */
  const isStripePastDuePartial = isDeferaStripe && !isDeferaCapitalNmi;
  const dzApiUrl = isDeferaCapitalNmi
    ? deferaCapitalNmiUrl
    : (isDeferaCapital ? deferaCapitalUrl : deferaZeroUrl);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  /** "Can't make this payment?" (MovePaymentDialog). */
  const [showMovePayment, setShowMovePayment] = useState(false);
  /** The move just made, until the trip's own record catches up (the parent
   *  refetches flight_data after the schedule): keeps the card from offering
   *  the same payment again in between. */
  const [justMovedPayment, setJustMovedPayment] = useState(null);
  /** Who is looking: an admin on a customer's trip doesn't get the customer's fee consent. */
  const [viewerUserId, setViewerUserId] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);
  // Admin Override Dates modal (see the handlers further down for rationale).
  const [showAdminOverrideModal, setShowAdminOverrideModal] = useState(false);
  const [overrideRows, setOverrideRows] = useState([]);
  const [overrideAutoAmounts, setOverrideAutoAmounts] = useState(true);
  const [overrideBaselineCents, setOverrideBaselineCents] = useState(0);
  /** Past-due money folded into the baseline when "include past-due" is on. */
  const [overridePastDueCents, setOverridePastDueCents] = useState(0);
  const [overrideIncludePastDue, setOverrideIncludePastDue] = useState(false);
  const [overrideAllowTotalChange, setOverrideAllowTotalChange] = useState(false);
  const [overrideAnchorDate, setOverrideAnchorDate] = useState('');
  const [overrideFrequency, setOverrideFrequency] = useState('biweekly');
  const [overrideNote, setOverrideNote] = useState('');
  const [overrideRollPastDue, setOverrideRollPastDue] = useState(false);
  const [overrideErrors, setOverrideErrors] = useState([]);
  const [isSavingOverride, setIsSavingOverride] = useState(false);
  // Customer "$25 custom dates" mode inside Change Schedule.
  const [custDatesMode, setCustDatesMode] = useState(false);
  const [custRows, setCustRows] = useState([]);
  const [custAutoAmounts, setCustAutoAmounts] = useState(true);
  const [custBaselineCents, setCustBaselineCents] = useState(0);
  const [custAnchorDate, setCustAnchorDate] = useState('');
  const [custFrequency, setCustFrequency] = useState('biweekly');
  const [custSeededAnchor, setCustSeededAnchor] = useState('');
  const [custErrors, setCustErrors] = useState([]);
  const [custSaving, setCustSaving] = useState(false);
  /** The server's own answer to "what would this change do, and what would it
   *  cost today" (`dry_run`), shown before the customer pays. 9BZUP1 paid $25
   *  for a change she couldn't see, and it doubled her weekly payment. */
  const [custPreview, setCustPreview] = useState(null);
  const [custPreviewNonce, setCustPreviewNonce] = useState(0);
  const [selectedExtension, setSelectedExtension] = useState('');
  const [paymentOption, setPaymentOption] = useState('installment');
  const [customAmount, setCustomAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  /** Staff who may READ this trip's internals (admin + concierge); controls stay on isAdmin. */
  const [isStaffViewer, setIsStaffViewer] = useState(false);
  const [editDepositPercentage, setEditDepositPercentage] = useState(20);
  const [editPaymentPlan, setEditPaymentPlan] = useState('weekly');
  const [editDepositMode, setEditDepositMode] = useState('percentage'); // 'percentage' or 'custom'
  const [editCustomDepositAmount, setEditCustomDepositAmount] = useState('');
  // Defera Zero: Change Schedule (like Reserve)
  const [showDeferaZeroScheduleChange, setShowDeferaZeroScheduleChange] = useState(false);
  const [dzNewFrequency, setDzNewFrequency] = useState(paymentSchedule?.payment_plan ?? paymentSchedule?.paymentPlan ?? 'weekly');
  const [dzPreview, setDzPreview] = useState(null);
  const [dzLoadingPreview, setDzLoadingPreview] = useState(false);
  const [dzUpdating, setDzUpdating] = useState(false);
  // Defera Credit (house credit): customer applies balance to this trip's upcoming payments.
  // Balance is fetched once per mount; the Apply Credits button only renders when > 0.
  const [deferaCreditCents, setDeferaCreditCents] = useState(0);
  const [showApplyCreditsModal, setShowApplyCreditsModal] = useState(false);
  const [applyCreditsInput, setApplyCreditsInput] = useState('');
  /** 'input' → amount entry + preview; 'confirm' → "Are you sure…" gate. */
  const [applyCreditsStage, setApplyCreditsStage] = useState('input');
  const [applyCreditsSubmitting, setApplyCreditsSubmitting] = useState(false);
  const [applyCreditsError, setApplyCreditsError] = useState(null);
  /** Fresh per-confirmation idempotency token — a network retry of the same
   *  confirmation converges server-side; a NEW application gets a new token. */
  const applyCreditsRequestIdRef = useRef(null);
  // Defera Capital: Skip Upcoming Payment ($20 fee, next installment becomes $0, later ones go up)
  const [showSkipPaymentModal, setShowSkipPaymentModal] = useState(false);
  const [skipPreview, setSkipPreview] = useState(null);
  const [skipPreviewLoading, setSkipPreviewLoading] = useState(false);
  const [skipPreviewError, setSkipPreviewError] = useState(null);
  const [skipCharging, setSkipCharging] = useState(false);
  // Defera Zero: Make a Payment (inline, like Reserve inject)
  const [showDeferaZeroMakePayment, setShowDeferaZeroMakePayment] = useState(false);
  /** Which tab the Stripe Make-a-Payment form opens on; the paused-plan card's "Pay part of it"
   *  lands on Principal because a canceled subscription has no open invoices to allocate to. */
  const [dzMakePaymentInitialMode, setDzMakePaymentInitialMode] = useState(null);
  /** Set when the customer clicks "Pay now" on a rose retry-pending row in the
   *  upcoming-schedule list. The NMI Make-a-Payment form mounts with this row
   *  as its `pendingSettlementRow` prop, which makes it render a focal "Past
   *  due — Installment N · $X" banner with a one-click Pay button at the top
   *  (parallels Stripe's `Pay $X past-due` banner). Cleared in the modal's
   *  onClose/onSuccess handlers so a later open via the generic "Make a Payment"
   *  CTA falls back to the regular tabs-first layout.
   *  Shape: `{ amount_cents, installment_number, attempted_at, gateway_response_text }` */
  const [nmiPendingSettlementRow, setNmiPendingSettlementRow] = useState(null);
  // Post-payment comparison (original vs new)
  const [paymentSuccessComparison, setPaymentSuccessComparison] = useState(null);
  // Defera Zero: API schedule + payment history (source of truth)
  const [dzScheduleData, setDzScheduleData] = useState(null);
  const [dzScheduleLoading, setDzScheduleLoading] = useState(false);
  const [dzScheduleError, setDzScheduleError] = useState(null);
  /** Payment attempts that did NOT go through (declines, retries, failed
   *  one-time payments) — shown to admins in Payment History so the trip's
   *  whole payment story is visible; customers never see them. Display only:
   *  never part of any paid or remaining total. `attemptsStaffView` adds the
   *  processor's raw response. */
  const [failedAttempts, setFailedAttempts] = useState([]);
  const [attemptsStaffView, setAttemptsStaffView] = useState(false);
  /** Saved trips use Stripe API for amounts — never flash client Slice math first. */
  const needsStripeSchedule = Boolean(isDeferaStripe && tripId);
  // Defera Zero: Update Card (inline modal, like Reserve)
  const [showDeferaZeroUpdateCard, setShowDeferaZeroUpdateCard] = useState(false);
  /** Restart-plan modal — opens when a Defera Capital sub was canceled by
   *  Stripe due to exhausted retries (cancellation_details.reason ===
   *  'payment_failed'). Surfaces past-due + the new schedule and lets the
   *  customer kick the plan back to life. Companion of the
   *  `defera-capital-restart-plan` edge fn. */
  const [showRestartPlanModal, setShowRestartPlanModal] = useState(false);
  // Slice Pay portal redirect overlay (installment trips)
  const [showSlicePayRedirectOverlay, setShowSlicePayRedirectOverlay] = useState(false);
  // Change Flight History modal (admin)
  const [showChangeHistoryModal, setShowChangeHistoryModal] = useState(false);

  const hasPaymentScheduleData = !!paymentSchedule;
  // CRITICAL: Only default if payment_schedule (or its deposit_percentage) is missing entirely.
  // Use the lead-time-aware Defera Capital default for DC trips so a missing-deposit DC
  // booking doesn't get re-rendered at the old flat 20%; Slice Pay/Defera Zero keep 20%
  // (Zero forces 0% downstream via isDeferaZero). If payment_schedule exists with a stored
  // deposit_percentage, it is respected verbatim (past bookings unchanged).
  const _missingDepositFallback = isDeferaCapital ? 5 : 20;
  const deposit_percentage = paymentSchedule
    ? (paymentSchedule.deposit_percentage ?? paymentSchedule.depositPercentage ?? _missingDepositFallback)
    : _missingDepositFallback;
  const payment_plan = paymentSchedule?.payment_plan ?? paymentSchedule?.paymentPlan ?? 'weekly';
  const custom_deposit_amount = paymentSchedule?.custom_deposit_amount ?? paymentSchedule?.customDepositAmount ?? null;
  
  // Log when using stored vs default deposit_percentage for debugging (0 is valid for Defera Zero)
  const hasDepositPct = paymentSchedule && (paymentSchedule.deposit_percentage !== undefined || paymentSchedule.depositPercentage !== undefined);
  if (paymentSchedule && !hasDepositPct) {
    console.warn(`PaymentScheduleDisplay: payment_schedule exists but deposit_percentage is missing, defaulting to ${_missingDepositFallback}% (${isDeferaCapital ? 'Defera Capital' : 'Slice Pay'})`);
  }
  const extension_request = paymentSchedule?.extension_request;
  const hasExtensionRequest = extension_request && extension_request.status === 'pending';
  
  const effectiveDepartureDate = departureDate || paymentSchedule?.departure_date || paymentSchedule?.departureDate || null;
  const effectiveBookingDate = bookingDate || paymentSchedule?.booking_date || paymentSchedule?.bookingDate || null;

  /** Persisted on cart (Review) or inferred from trip type — must match Stripe cancel_at rules. */
  const resolvedLastPaymentDaysBeforeDeparture = useMemo(() => {
    const stored = paymentSchedule?.last_payment_days_before_departure ?? paymentSchedule?.lastPaymentDaysBeforeDeparture;
    if (stored != null && !Number.isNaN(Number(stored))) return Number(stored);
    if (isDeferaCapital) return DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE;
    if (isDeferaZero) return DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE;
    return SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE;
  }, [
    paymentSchedule?.last_payment_days_before_departure,
    paymentSchedule?.lastPaymentDaysBeforeDeparture,
    isDeferaCapital,
    isDeferaZero,
  ]);
  
  const schedule = useMemo(() => {
    // We expect totalWithFee prop to be the "grand total with hold" in Trips/Carts.
    const totalWithHold = Number(totalWithFee || 0);
    
    console.log('PaymentScheduleDisplay useMemo inputs:', {
      totalWithFee,
      totalWithHold,
      effectiveDepartureDate,
      effectiveBookingDate,
      deposit_percentage,
      payment_plan
    });
    
    // Determine the pre-hold total to feed the calculator.
    const storedTotalWithFee = Number(paymentSchedule?.total_with_fee ?? paymentSchedule?.totalWithFee ?? 0);
    const derivedPreHold = totalWithHold / 1.12;
    
    let preHoldTotal = derivedPreHold;
    if (storedTotalWithFee && !Number.isNaN(storedTotalWithFee)) {
      const distToDerived = Math.abs(storedTotalWithFee - derivedPreHold);
      const distToTotal = Math.abs(storedTotalWithFee - totalWithHold);
      preHoldTotal = distToDerived <= distToTotal ? storedTotalWithFee : derivedPreHold;
    }
    
    // Booking date: use stored booking date if available, else fall back to "now" (best effort).
    const bookDate = effectiveBookingDate || new Date();
    
    // Admin-reconciled bookings (e.g. manual push after Stripe charged Defera Capital
    // deposit but subscription never persisted) need to keep the original hold rate
    // their customer agreed to at booking time — that rate is pinned on
    // `payment_schedule.hold_fee_rate` (historically 0.12, currently 0.15) — even
    // though we had to flip _payment_method to 'installments' to stop this component
    // from calling the Defera Capital /schedule edge function.
    const storedHoldFeeRate = Number(paymentSchedule?.hold_fee_rate ?? paymentSchedule?.holdFeeRate);
    const resolvedHoldFeeRate = Number.isFinite(storedHoldFeeRate) && storedHoldFeeRate > 0
      ? storedHoldFeeRate
      : (isDeferaCapital ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE);

    // ALWAYS recalculate the schedule based on current deposit_percentage and payment_plan
    // This ensures admin edits are reflected immediately
    if (effectiveDepartureDate && Number.isFinite(totalWithHold) && totalWithHold > 0) {
      const firstPaymentDays = currentFlightData?._first_payment_days;
      // Saved bookings persist their locked deposit at booking time. Without this flag the
      // calculator snaps the percentage UP whenever the trip moves into a tighter lead-time
      // tier, which is what produced the PY2RE8 mismatch — the Cost Breakdown / Stripe deposit
      // stayed at the locked $401.44 (30%) but the Payment Schedule re-rendered as $535.25
      // (40%) once the trip crossed inside 60 days of departure.
      //
      // Activation gate: any snapshot evidence of a locked deposit counts. Originally this
      // only checked `deposit_amount_cents`, but recovery-script bookings (e.g. 12I2AQ via
      // `defera-capital-create-missing-subscription.mjs`) wrote a sparse snapshot with only
      // `deposit_percentage` and no `deposit_amount_cents`. The gate misfired and the UI
      // snapped 12I2AQ from 40% ($273.24) to 50% ($341.55) once the trip crossed inside 30
      // days of departure. Treating an explicit `deposit_percentage` as equally authoritative
      // closes that loophole without touching the historical PY2RE8 fix path.
      const hasStoredDeposit =
        Number(paymentSchedule?.deposit_amount_cents) > 0 || hasDepositPct;
      const computed = calculatePaymentSchedule({
        bookingDate: bookDate,
        departureDate: effectiveDepartureDate,
        totalWithFee: preHoldTotal,
        depositPercentage: deposit_percentage,
        customDepositAmount: custom_deposit_amount,
        firstPaymentDays,
        daysBeforeDepartureForLastPayment: resolvedLastPaymentDaysBeforeDeparture,
        deferaCapitalDepositTiers: isDeferaCapital,
        holdFeeRate: resolvedHoldFeeRate,
        respectStoredDepositPercentage: hasStoredDeposit,
        // `totalWithFee` comes in without the Faye premium (the trip modal
        // takes it out before backing out fees); it rides on the installments.
        insurancePremium: (Number(insurancePremiumCents) || 0) / 100,
      });
      
      console.log('PaymentScheduleDisplay: Calculated schedule', {
        deposit_percentage,
        payment_plan,
        preHoldTotal,
        effectiveDepartureDate,
        computed_available: computed?.available,
        computed_depositAmount: computed?.depositAmount,
        computed_weeklyPlan: computed?.weeklyPlan?.paymentAmount,
        computed_biweeklyPlan: computed?.biweeklyPlan?.paymentAmount
      });
      
      if (computed?.available) {
        return computed;
      }
    } else {
      console.log('PaymentScheduleDisplay: Cannot compute - missing data', {
        hasEffectiveDepartureDate: !!effectiveDepartureDate,
        totalWithHold,
        isNaN: Number.isNaN(totalWithHold)
      });
    }
    
    // Fallback to existing schedule if we can't compute
    const existing = paymentSchedule?.schedule;
    const existingLooksValid = !!(existing && existing.available && (existing.weeklyPlan || existing.biweeklyPlan));
    
    console.log('PaymentScheduleDisplay: Fallback check', { existingLooksValid, existing });
    
    if (existingLooksValid) {
      return existing;
    }
    
    return null;
  }, [paymentSchedule, totalWithFee, effectiveDepartureDate, effectiveBookingDate, deposit_percentage, payment_plan, custom_deposit_amount, currentFlightData, resolvedLastPaymentDaysBeforeDeparture, isDeferaCapital, insurancePremiumCents]);
  
  const activePlan = payment_plan === 'weekly' ? schedule?.weeklyPlan : schedule?.biweeklyPlan;
  
  console.log('PaymentScheduleDisplay render:', {
    deposit_percentage,
    payment_plan,
    schedule_depositAmount: schedule?.depositAmount,
    activePlan_paymentAmount: activePlan?.paymentAmount,
    activePlan_paymentCount: activePlan?.paymentCount
  });

  // Check if current user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const user = await User.getCurrentUser();
        if (user?.id) setViewerUserId(user.id);
        if (user && (user.role === 'admin' || user.app_metadata?.roles?.includes('admin'))) {
          setIsAdmin(true);
        }
        if (user && (user.role === 'admin' || canViewTripInternals(user))) {
          setIsStaffViewer(true);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };
    checkAdminStatus();
  }, []);

  /** Admins only (founder, 2026-10-06: customers found the declines
   *  confusing; the server returns none to anyone else either). Never blocks
   *  or fails the schedule: if this can't load, History simply shows the
   *  payments that went through, as it always has. Runs after every schedule
   *  refresh, on the session that refresh already looked up (a second
   *  concurrent lookup raced it), so a decline from an in-app payment shows up
   *  with the refresh that follows it. */
  const fetchFailedAttempts = useCallback(async (accessToken, viewer) => {
    if (!isDeferaStripe || !tripId || !accessToken) return;
    if (!canViewTripInternals(viewer)) {
      setFailedAttempts([]);
      return;
    }
    try {
      const res = await fetch(getApiUrl(`trips/${encodeURIComponent(tripId)}/payment-attempts`), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await safeResJson(res);
      if (!isMounted()) return;
      if (res.ok && Array.isArray(data?.attempts)) {
        setFailedAttempts(data.attempts);
        setAttemptsStaffView(Boolean(data.staff));
      }
    } catch {
      /* History renders without them */
    }
  }, [isDeferaStripe, tripId, isMounted]);

  // Defera Zero: Fetch schedule + payment history from API (source of truth)
  // On 404 (e.g. admin viewing another user's trip), try admin endpoint
  const fetchDzSchedule = useCallback(async () => {
    if (!isDeferaStripe || !tripId) return;
    setDzScheduleLoading(true);
    setDzScheduleError(null);
    let accessToken = null;
    let viewer = null;
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!isMounted()) return;
      viewer = session?.user || null;
      if (!session?.access_token) {
        setDzScheduleError('Sign in to load your payment schedule.');
        setDzScheduleData(null);
        return;
      }
      accessToken = session.access_token;

      let res = await fetch(dzApiUrl('schedule') + `?booking_id=${tripId}`, {
        headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` })
      });
      if (!isMounted()) return;
      if (!res.ok && res.status === 404 && !isDeferaCapitalNmi) {
        const adminPath = isDeferaCapital ? 'admin/defera-capital/schedule' : 'admin/defera-zero/schedule';
        res = await fetch(getApiUrl(adminPath) + `?booking_id=${tripId}`, {
          headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` })
        });
      }
      const data = await safeResJson(res);
      if (!isMounted()) return;
      if (!res.ok) {
        setDzScheduleError(data?.error || 'Could not load payment schedule');
        setDzScheduleData(null);
        return;
      }
      setDzScheduleData(data);
    } catch (e) {
      if (!isMounted()) return;
      console.error('Failed to fetch Defera Zero schedule', e);
      setDzScheduleData(null);
      setDzScheduleError(e instanceof Error ? e.message : 'Could not load payment schedule');
    } finally {
      if (isMounted()) {
        setDzScheduleLoading(false);
        // After the schedule, never in its way; same session, no second lookup.
        if (accessToken) fetchFailedAttempts(accessToken, viewer);
      }
    }
  }, [isDeferaStripe, tripId, dzApiUrl, isDeferaCapital, isDeferaCapitalNmi, fetchFailedAttempts, isMounted]);

  useEffect(() => {
    if (isDeferaStripe && tripId) fetchDzSchedule();
  }, [isDeferaStripe, tripId, fetchDzSchedule]);

  /** True when an admin is viewing someone else's trip — the balance fetch
   *  then acts as the trip OWNER so the button reflects the credits that
   *  would actually be applied (the server always debits the owner's ledger
   *  regardless of who clicks). Strictly admin-gated: sending the act-as
   *  header as a non-admin gets the whole request 403'd. */
  const viewingAsOwner = Boolean(isAdmin && tripOwnerUserId);
  /** Defera Credit balance — drives the Apply Credits button (hidden at $0).
   *  Same endpoint/pattern as the checkout preview in Review.jsx. Non-fatal:
   *  any failure just leaves the balance at 0 and the button hidden. */
  const fetchDeferaCreditBalance = useCallback(async () => {
    if (!isDeferaStripe || !tripId || !showActions) return;
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!isMounted() || !session?.access_token) return;
      const headers = mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` });
      if (viewingAsOwner) headers['X-Defera-Act-As-User'] = tripOwnerUserId;
      const res = await fetch(deferaCreditUrl('balance'), { headers, credentials: 'omit' });
      if (res.ok) {
        const data = await res.json();
        if (!isMounted()) return;
        setDeferaCreditCents(Math.max(0, Number(data?.balance_cents) || 0));
      }
    } catch (_e) { /* leave at 0 — credit is purely additive */ }
  }, [isDeferaStripe, tripId, showActions, viewingAsOwner, tripOwnerUserId, isMounted]);

  useEffect(() => {
    fetchDeferaCreditBalance();
  }, [fetchDeferaCreditBalance]);

  /** After an NMI payment, the webhook may land a moment after the edge handler.
   *  Subscribe to installment + intent row changes so the schedule refreshes
   *  without a manual reload — same idea as Stripe invoice webhooks updating
   *  the subscription schedule. */
  useEffect(() => {
    if (!isDeferaCapitalNmi || !tripId) return undefined;
    let channel = null;
    let cancelled = false;
    (async () => {
      const { supabase } = await import('@/lib/supabase');
      if (cancelled) return;
      channel = supabase
        .channel(`nmi-schedule-${tripId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'nmi_installment_attempts', filter: `booking_id=eq.${tripId}` },
          () => { if (!cancelled) fetchDzSchedule(); },
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'nmi_customer_payment_intents', filter: `booking_id=eq.${tripId}` },
          () => { if (!cancelled) fetchDzSchedule(); },
        )
        .subscribe();
    })();
    return () => {
      cancelled = true;
      if (channel) {
        import('@/lib/supabase').then(({ supabase }) => {
          supabase.removeChannel(channel);
        }).catch(() => {});
      }
    };
  }, [isDeferaCapitalNmi, tripId, fetchDzSchedule]);

  const pollNmiScheduleAfterPayment = useCallback(async () => {
    toast.message('Payment processing — updating your schedule…');
    for (let i = 0; i < 8; i += 1) {
      await fetchDzSchedule();
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, 2000); });
    }
  }, [fetchDzSchedule]);

  // Defera Zero: Open inline Update Card modal (like Reserve)
  const handleDeferaZeroUpdateCard = () => {
    if (!tripId) {
      toast.error('Trip not found');
      return;
    }
    setShowDeferaZeroUpdateCard(true);
  };

  // Defera Zero: Change Schedule - fetch preview
  const fetchDzPreview = async (freq) => {
    if (!tripId || freq === payment_plan) {
      setDzPreview(null);
      return;
    }
    setDzLoadingPreview(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const res = await fetch(dzApiUrl('preview-schedule'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: tripId, payment_frequency: freq })
      });
      const data = await safeResJson(res);
      if (!res.ok) throw new Error(data.error);

      const hasSchedulePreview =
        (data.current_schedule?.num_payments ?? 0) > 0 &&
        (data.proposed_schedule?.num_payments ?? 0) > 0;

      if (isDeferaCapitalNmi && !hasSchedulePreview) {
        const local = buildNmiScheduleChangePreview({
          upcomingRows: filterNmiOpenInstallmentRows(effectiveDisplayPlan?.scheduleRows || []),
          paymentPlan: payment_plan,
          proposedFrequency: freq,
          paymentSchedule,
          totalAmountCents: dzScheduleData?.total_amount_cents || currentGrandTotalCents || 0,
        });
        if (local) {
          setDzPreview({ ...local, can_update: true, _payment_processor: 'nmi' });
          return;
        }
      }

      setDzPreview(data);
    } catch (err) {
      if (isDeferaCapitalNmi) {
        const local = buildNmiScheduleChangePreview({
          upcomingRows: filterNmiOpenInstallmentRows(effectiveDisplayPlan?.scheduleRows || []),
          paymentPlan: payment_plan,
          proposedFrequency: freq,
          paymentSchedule,
          totalAmountCents: dzScheduleData?.total_amount_cents || currentGrandTotalCents || 0,
        });
        if (local) {
          setDzPreview({ ...local, can_update: true, _payment_processor: 'nmi' });
          return;
        }
      }
      toast.error(err.message || 'Failed to load preview');
      setDzPreview(null);
    } finally {
      setDzLoadingPreview(false);
    }
  };

  const handleDzFrequencySelect = (freq) => {
    setDzNewFrequency(freq);
    if (freq !== payment_plan) fetchDzPreview(freq);
    else setDzPreview(null);
  };

  const handleDzUpdateSchedule = async () => {
    if (dzNewFrequency === payment_plan) {
      setShowDeferaZeroScheduleChange(false);
      return;
    }
    setDzUpdating(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');

      const res = await fetch(dzApiUrl('update-schedule'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: tripId, payment_frequency: dzNewFrequency })
      });
      const data = await safeResJson(res);
      if (!res.ok) throw new Error(data.error);
      toast.success('Payment schedule updated');
      setShowDeferaZeroScheduleChange(false);
      setDzPreview(null);
      fetchDzSchedule();
      if (onScheduleUpdated) onScheduleUpdated();
      else if (onExtensionRequested) onExtensionRequested();
    } catch (err) {
      toast.error(err.message || 'Failed to update schedule');
    } finally {
      setDzUpdating(false);
    }
  };

  const frequencyLabels = { weekly: 'Weekly', biweekly: 'Bi-weekly' };

  // Defera Capital: Skip Upcoming Payment — open modal + fetch before/after preview
  const handleOpenSkipPayment = async () => {
    if (!tripId) {
      toast.error('Trip not found');
      return;
    }
    setShowSkipPaymentModal(true);
    if (isDeferaCapitalNmi) {
      setSkipPreview(null);
      setSkipPreviewError(null);
      setSkipPreviewLoading(false);
      return;
    }
    setSkipPreview(null);
    setSkipPreviewError(null);
    setSkipPreviewLoading(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');
      const res = await fetch(dzApiUrl('preview-skip-payment'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: tripId })
      });
      const data = await safeResJson(res);
      if (!res.ok) throw new Error(data.error || 'Failed to load skip preview');
      setSkipPreview(data);
    } catch (err) {
      setSkipPreviewError(err.message || 'Failed to load skip preview');
    } finally {
      setSkipPreviewLoading(false);
    }
  };

  const handleConfirmSkipPayment = async () => {
    if (!tripId || !skipPreview?.eligible || skipCharging) return;
    setSkipCharging(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');
      const res = await fetch(dzApiUrl('skip-payment'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: tripId, client_request_id: crypto.randomUUID() })
      });
      const data = await safeResJson(res);
      if (!res.ok) throw new Error(formatStripeErrorMessage(data) || data.error || 'Skip payment failed');
      if (data.schedule_sync_pending) {
        toast.warning('Your skip fee was charged, but the schedule update needs a manual review — our team has been notified.');
      } else {
        const nextDate = data.new_next_charge_date ? format(parseISO(data.new_next_charge_date), 'EEE, MMM d') : null;
        toast.success(nextDate ? `Payment skipped — your next charge is ${nextDate}.` : 'Payment skipped.');
      }
      setShowSkipPaymentModal(false);
      setSkipPreview(null);
      fetchDzSchedule();
      if (onScheduleUpdated) onScheduleUpdated();
      else if (onExtensionRequested) onExtensionRequested();
    } catch (err) {
      toast.error(err.message || 'Skip payment failed');
    } finally {
      setSkipCharging(false);
    }
  };

  // Defera Zero / Capital on a saved trip: Stripe API is the only source of truth for amounts.
  // Do not fall back to client calculatePaymentSchedule (Slice hold math) — it causes a lower fake amount, then a jump.
  const effectiveDisplayPlan = useMemo(() => {
    if (isDeferaStripe && dzScheduleData?.schedule) {
      const s = dzScheduleData.schedule;
      /** Per-row metadata so the upcoming list can render decorated rows:
       *    - `paid_ahead`: customer used Pay Ahead — render as "$0.00 (paid ahead)"
       *    - `is_drift_c_prorated`: final cycle is mid-cycle-truncated by cancel_at —
       *      Stripe will bill this row's `amount_cents` (= prorated amount) instead of
       *      the locked unit_amount. Always paired with a sibling `is_auto_correction` row.
       *    - `is_auto_correction`: presentation-only row representing the same-day
       *      one-off PI the `defera-capital-invoice-paid` webhook fires to top the
       *      prorated invoice back up to the originally-quoted amount.
       *    - `credit_applied_cents`: portion of this row Stripe will cover from the
       *      customer's account credit (negative customer.balance) at invoice
       *      finalization. The card is only charged `amount_cents − credit_applied_cents`. */
      const scheduleRows = (s.dates || []).map((d) => (
        typeof d === 'object' && d?.date
          ? {
              date: d.date,
              amount_cents: Number(d.amount_cents) || 0,
              paid_ahead: Boolean(d.paid_ahead),
              /** Links a paid-ahead $0 row back to the payment that covered it ("covered by
               *  your Jun 12 payment") — null on older deployed schedule endpoints. */
              covered_by_date: d.covered_by_date || null,
              covered_amount_cents: Number(d.covered_amount_cents) || 0,
              /** Skip Upcoming Payment ($20 fee) rows — render as "$0.00 Skipped". Unlike
               *  paid-ahead, the customer did NOT pay this installment; the later ones absorbed it. */
              fee_skipped: Boolean(d.fee_skipped),
              is_paid: Boolean(d.is_paid),
              _nmi_status: d._nmi_status || null,
              /** Retry diagnostics from `defera-capital-nmi-schedule`. Power the
               *  "Installment N · 1 failed attempt · Next retry Jun 29 · Insufficient
               *  funds" rose row that mirrors Stripe's past-due invoice card.
               *  Absent on Stripe rows and on legacy NMI deployments that haven't
               *  picked up the field — coerced to safe defaults so the renderer
               *  can read them unconditionally. */
              /** The installment row's id: one move per row ("Can't make this payment?"). */
              _nmi_attempt_id: d._nmi_attempt_id || null,
              _nmi_installment_number: Number.isFinite(Number(d._nmi_installment_number))
                ? Number(d._nmi_installment_number)
                : null,
              _nmi_retry_count: Number(d._nmi_retry_count) || 0,
              _nmi_attempted_at: d._nmi_attempted_at || null,
              _nmi_gateway_response_text: d._nmi_gateway_response_text || null,
              is_drift_c_prorated: Boolean(d.is_drift_c_prorated),
              is_auto_correction: Boolean(d.is_auto_correction),
              applies_to_date: d.applies_to_date || null,
              credit_applied_cents: Number(d.credit_applied_cents) || 0,
            }
          : {
              date: d,
              amount_cents: 0,
              paid_ahead: false,
              covered_by_date: null,
              covered_amount_cents: 0,
              fee_skipped: false,
              is_paid: false,
              _nmi_status: null,
              _nmi_installment_number: null,
              _nmi_retry_count: 0,
              _nmi_attempted_at: null,
              _nmi_gateway_response_text: null,
              is_drift_c_prorated: false,
              is_auto_correction: false,
              applies_to_date: null,
              credit_applied_cents: 0,
            }
      ));
      /** `schedule` stays backwards-compatible (date strings, real charges only — excludes
       *  paid-ahead / fee-skipped $0 rows) so legacy consumers (extension comparison,
       *  future-payments filter, etc.) keep working unchanged. The new rendering path reads
       *  `scheduleRows` for the decorated list. */
      const realChargeDates = scheduleRows.filter((r) => !r.paid_ahead && !r.fee_skipped).map((r) => r.date);
      const stripeCents = dzScheduleData.stripe_unit_amount_cents;
      const computedCents = s.per_payment_cents || 0;
      const perPaymentCents =
        typeof stripeCents === 'number' && stripeCents > 0 ? stripeCents : computedCents;
      return {
        schedule: realChargeDates,
        scheduleRows,
        paymentAmount: perPaymentCents / 100,
        paymentCount: s.num_payments || 0,
        paidAheadCount: Number(s.paid_ahead_count) || 0,
        available: true,
        _stripePerPaymentCents: typeof stripeCents === 'number' && stripeCents > 0 ? stripeCents : null,
        _computedPerPaymentCents: computedCents,
        _scheduleMismatch: !!dzScheduleData.schedule_amounts_mismatch_stripe
      };
    }
    if (needsStripeSchedule) return null;
    return activePlan;
  }, [isDeferaStripe, dzScheduleData, activePlan, needsStripeSchedule]);

  const hasDisplayPlan = effectiveDisplayPlan && effectiveDisplayPlan.available && Array.isArray(effectiveDisplayPlan.schedule);
  const stripeScheduleReady = !needsStripeSchedule || Boolean(dzScheduleData?.schedule);

  /** Open NMI installment rows — pending/retrying/past-due only (excludes paid success rows). */
  const nmiOpenUpcomingRows = useMemo(() => {
    if (!isDeferaCapitalNmi || !effectiveDisplayPlan?.scheduleRows) return [];
    return filterNmiOpenInstallmentRows(effectiveDisplayPlan.scheduleRows);
  }, [isDeferaCapitalNmi, effectiveDisplayPlan]);

  /** Upcoming charge rows for the NMI Skip Payment modal — same source as the
   *  timeline list, built locally so the preview doesn't depend on a redeployed
   *  preview-skip-payment edge function. */
  const nmiSkipUpcomingRows = nmiOpenUpcomingRows;

  /** Trip is in a terminal state — i.e. cancelled or defaulted in our database, OR the
   *  Stripe subscription has fully ended. In either case, no future installment will
   *  ever fire, so showing "Upcoming Payments" / "Final Balance" / past-due rows /
   *  Make-a-Payment buttons creates a false expectation that the customer can still
   *  pay against this trip. NPH9N0 was the canary — `bookings.status='defaulted'`,
   *  Stripe sub `canceled` with `ended_at` set, but the dashboard kept rendering
   *  $336.42 upcoming payments. Paid history still renders so the customer can see
   *  what they actually paid before things ended.
   *
   *  IMPORTANT: a Stripe sub becomes `canceled` for THREE distinct reasons (see
   *  stripe-webhook/defera-capital.ts docstring on `handleDeferaCapitalSubscriptionDeleted`):
   *  (1) `cancel_at` was hit after every installment paid (trip is paid in full),
   *  (2) Stripe gave up retrying a failing card, or (3) the trip was actually
   *  cancelled. We must NOT show "Cancelled" copy for (1) — the customer fully
   *  paid for their trip. Detect (1) via `remaining_cents === 0` and route those
   *  trips to the "paid in full" branch instead of the terminal-state branch.
   *  `incomplete_expired` still counts as terminated regardless of paid amount
   *  (3DS abandoned — sub never activated, deposit needs refund). */
  const tripStatusLower = typeof tripStatus === 'string' ? tripStatus.toLowerCase() : '';
  const tripStatusTerminated = tripStatusLower === 'cancelled' || tripStatusLower === 'canceled' || tripStatusLower === 'defaulted';
  /** Ops marks trips `completed` once every installment has cleared and the booking
   *  is ticketed. Stripe still reports the sub as `canceled` (natural cancel_at expiry),
   *  and `remaining_cents` can lag at a few cents due to reconciliation — so we must
   *  not treat a completed trip as a terminal cancel. See GOUWVE / paid-off-then-completed
   *  cohort where the schedule showed "Cancelled" despite a green Completed badge. */
  const isTripCompleted = tripStatusLower === 'completed';
  const subStatus = dzScheduleData?.subscription_status;
  const remainingCents = Number(dzScheduleData?.remaining_cents ?? -1);
  const totalAmountCents = Number(dzScheduleData?.total_amount_cents ?? 0);
  /** `remaining === 0` disambiguates the Stripe rail's natural `cancel_at`
   *  expiry (reason 1 in the docstring above) from a real cancellation — but
   *  ONLY when the booking itself isn't terminal. A cancelled NMI trip zeroes
   *  `remaining_cents` by construction (cancel flips every open row to
   *  `cancelled`, which the schedule endpoint excludes from remaining), so
   *  without this guard every cancelled NMI Capital trip rendered the
   *  "Paid in Full" banner (I703QH: $17.49 of $402.16 collected, shown as
   *  paid in full). A booking whose own status is cancelled/defaulted always
   *  takes the terminal branch; natural expiry never sets those statuses. */
  const isPaidInFull = totalAmountCents > 0 && remainingCents === 0 && !tripStatusTerminated;
  /** Skip Upcoming Payment is Defera Capital + Confirmed only (backend enforces too).
   *  Shared by both the mobile and desktop action grids so the 2-per-row layout can
   *  decide whether the odd button out should span the full row. */
  const showSkipPaymentButton = isDeferaCapital && tripStatusLower === 'confirmed' && !isPaidInFull;
  /** Rows Defera Credit can still net, front-to-back, mirroring the server's
   *  eligibility (defera-credit-apply):
   *    - NMI: pending/retrying rows due strictly in the future (rows already
   *      due belong to today's charge cron). Row amounts are stored net, so
   *      `amount_cents` IS the remaining headroom.
   *    - Stripe: upcoming cycle rows, net of credit already being applied
   *      (`credit_applied_cents` — Stripe balance, pay-ahead leftovers, and
   *      committed Defera Credit are all pre-distributed by the schedule
   *      endpoint). Auto-correction rows are display-only PIs, not cycles. */
  const applyCreditsEligibleRows = useMemo(() => {
    const rows = effectiveDisplayPlan?.scheduleRows || [];
    const now = Date.now();
    return rows
      .filter((r) => {
        if (!r?.date || r.paid_ahead || r.fee_skipped || r.is_paid || r.is_auto_correction) return false;
        if (isDeferaCapitalNmi) {
          return (r._nmi_status === 'pending' || r._nmi_status === 'retrying')
            && new Date(r.date).getTime() > now
            && (Number(r.amount_cents) || 0) > 0;
        }
        return ((Number(r.amount_cents) || 0) - (Number(r.credit_applied_cents) || 0)) > 0;
      })
      .map((r) => ({
        date: r.date,
        net_cents: (Number(r.amount_cents) || 0) - (isDeferaCapitalNmi ? 0 : (Number(r.credit_applied_cents) || 0)),
      }));
  }, [effectiveDisplayPlan, isDeferaCapitalNmi]);
  /** Most credit this schedule can absorb right now. */
  const applyCreditsCapCents = useMemo(
    () => applyCreditsEligibleRows.reduce((s, r) => s + r.net_cents, 0),
    [applyCreditsEligibleRows],
  );
  /** Apply Credits: Confirmed installment trips only, and only when the
   *  customer actually has credit AND upcoming payments to point it at.
   *  Hidden entirely at $0 balance per product spec. */
  const applyCreditsMaxCents = Math.min(deferaCreditCents, applyCreditsCapCents);
  const showApplyCreditsButton = isDeferaStripe
    && tripStatusLower === 'confirmed'
    && !isPaidInFull
    && deferaCreditCents > 0
    && applyCreditsCapCents > 0;
  const applyCreditsAmountCents = useMemo(() => {
    const parsed = parseFloat(String(applyCreditsInput).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.round(parsed * 100);
  }, [applyCreditsInput]);
  /** Front-to-back preview of which upcoming payments the entered amount
   *  covers — same walk the server performs, for display only. */
  const applyCreditsPreview = useMemo(() => {
    if (applyCreditsAmountCents <= 0) return [];
    let left = Math.min(applyCreditsAmountCents, applyCreditsMaxCents);
    const out = [];
    for (const row of applyCreditsEligibleRows) {
      if (left <= 0) break;
      const applied = Math.min(left, row.net_cents);
      left -= applied;
      out.push({ date: row.date, before_cents: row.net_cents, after_cents: row.net_cents - applied });
    }
    return out;
  }, [applyCreditsAmountCents, applyCreditsMaxCents, applyCreditsEligibleRows]);

  const handleOpenApplyCredits = () => {
    setApplyCreditsInput('');
    setApplyCreditsError(null);
    setApplyCreditsStage('input');
    applyCreditsRequestIdRef.current = null;
    setShowApplyCreditsModal(true);
  };

  const handleApplyCreditsContinue = () => {
    setApplyCreditsError(null);
    if (applyCreditsAmountCents <= 0) {
      setApplyCreditsError('Enter an amount to apply.');
      return;
    }
    if (applyCreditsAmountCents > deferaCreditCents) {
      setApplyCreditsError(`You only have $${fmtCurrency(deferaCreditCents)} in Defera Credits.`);
      return;
    }
    if (applyCreditsAmountCents > applyCreditsCapCents) {
      setApplyCreditsError(`Your upcoming payments can only absorb $${fmtCurrency(applyCreditsCapCents)}.`);
      return;
    }
    // One idempotency token per confirmation — a retried Confirm click (or a
    // network retry) converges server-side instead of double-applying.
    applyCreditsRequestIdRef.current = crypto.randomUUID();
    setApplyCreditsStage('confirm');
  };

  const handleConfirmApplyCredits = async () => {
    if (!tripId || applyCreditsSubmitting || applyCreditsAmountCents <= 0) return;
    setApplyCreditsSubmitting(true);
    setApplyCreditsError(null);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');
      const res = await fetch(deferaCreditUrl('apply'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({
          booking_id: tripId,
          amount_cents: applyCreditsAmountCents,
          client_request_id: applyCreditsRequestIdRef.current || crypto.randomUUID(),
        }),
      });
      const data = await safeResJson(res);
      if (!res.ok) throw new Error(data?.error || 'Could not apply credits');
      const appliedCents = Number(data?.applied_cents) || 0;
      if (data?.already_applied) {
        toast.success('These credits were already applied to this trip.');
      } else if (data?.clamped && appliedCents > 0) {
        toast.success(`Applied $${fmtCurrency(appliedCents)} in Defera Credits — the rest stays in your balance.`);
      } else {
        toast.success(`Applied $${fmtCurrency(appliedCents)} in Defera Credits to this trip.`);
      }
      setShowApplyCreditsModal(false);
      setApplyCreditsInput('');
      setApplyCreditsStage('input');
      fetchDzSchedule();
      fetchDeferaCreditBalance();
      if (onScheduleUpdated) onScheduleUpdated();
      if (onInternalCommentAdded) onInternalCommentAdded();
    } catch (err) {
      setApplyCreditsError(err.message || 'Could not apply credits');
    } finally {
      setApplyCreditsSubmitting(false);
    }
  };
  /** When the Defera Capital schedule endpoint sees a sub canceled with
   *  `cancellation_details.reason='payment_failed'` AND there's still a
   *  collectible balance, it returns a `plan_restart` block with the
   *  past-due + new schedule preview. That's our cue to render the
   *  "Plan Paused — Restart Plan" CTA instead of the dead-end "Cancelled"
   *  banner. See supabase/functions/defera-capital-schedule/index.ts and
   *  supabase/functions/_shared/defera-capital-restart-plan.ts for the
   *  source of these fields. */
  const planRestart = dzScheduleData?.plan_restart || null;
  const isPlanRestartable = planRestart?.restartable === true;
  /** Sister state of `isPlanRestartable`: the sub died with a balance owed,
   *  but departure is too close for an installment schedule to fit. The
   *  customer must pay off the remaining balance in one shot. Without
   *  this branch, we'd fall through to `isTripTerminated` and render
   *  "Cancelled" — misleading because the booking is still `confirmed`
   *  and there's money owed. Route them to the existing Make-a-Payment
   *  flow instead. */
  const isPlanPayoffRequired = Boolean(planRestart && planRestart.restartable === false && planRestart.payoff_required);
  /** Completion-settlement is a SUBSET of payoff_required. When the schedule
   *  endpoint sets `planRestart.completion_settlement: true`, the plan ran
   *  to natural completion (all cycles fired, card was fine) and the customer
   *  just owes a small reconciliation balance (< 1 unit_amount). The
   *  isPlanPayoffRequired branch's "we couldn't charge your card" copy
   *  would lie about what happened — split off a separate UI state with
   *  honest "Installment Plan Completed — Final Balance" framing. */
  const isCompletionSettlement = Boolean(planRestart && planRestart.completion_settlement === true);
  const subStatusTerminated =
    !isTripCompleted &&
    // `past_due` and `unpaid` are deliberately NOT terminal. Both mean the sub is
    // still alive with open invoices — `unpaid` is Stripe's "retries exhausted,
    // subscription kept" state — and `/invoices/{id}/pay` settles either. They
    // fall through to the normal list, where the rose "Pay now" rows and the
    // past-due banner come from `open_invoices`, so the two statuses render the
    // same surface by construction (pinned by PaymentScheduleDisplay.unpaid.test.jsx).
    (subStatus === 'incomplete_expired' ||
    // `canceled` only counts as terminated when there's still a balance owed
    // AND we don't have a recovery path. If `remaining_cents` is 0, the sub
    // ended naturally at `cancel_at` after the customer paid in full —
    // that's success, not cancellation. If the sub was killed by Stripe due
    // to payment failures, the schedule endpoint surfaces either
    // `plan_restart.restartable=true` (restart UI) or `payoff_required`
    // (pay-off UI). Either way we render an action, not a terminal banner.
    // See PR thread on Nai's 12I2AQ canary and the user's "kick start again"
    // request.
    (subStatus === 'canceled' && !isPaidInFull && !isPlanRestartable && !isPlanPayoffRequired) ||
    (subStatus === 'cancelled' && !isPaidInFull && !isPlanRestartable && !isPlanPayoffRequired));
  const isTripTerminated = tripStatusTerminated || subStatusTerminated;
  const terminatedReasonLabel = tripStatusLower === 'defaulted'
    ? 'Defaulted'
    : (tripStatusLower === 'cancelled' || tripStatusLower === 'canceled')
      ? 'Cancelled'
      : 'Cancelled';

  /** "Pick my own dates" preview. Above the early returns below, like every
   *  hook should be: they don't fire while the Change Schedule dialog is up. */
  const custUnchanged = !isDeferaCapitalNmi
    && custAnchorDate === custSeededAnchor
    && custFrequency === (payment_plan === 'weekly' ? 'weekly' : 'biweekly');

  useEffect(() => {
    const payload = showDeferaZeroScheduleChange && custDatesMode && !custUnchanged
      ? customerDatesPayload({
          tripId,
          isNmi: isDeferaCapitalNmi,
          rows: custRows,
          autoAmounts: custAutoAmounts,
          anchorDate: custAnchorDate,
          frequency: custFrequency,
        })
      : null;
    if (!payload) {
      setCustPreview(null);
      return undefined;
    }
    const ctrl = new AbortController();
    setCustPreview((prev) => ({ status: 'loading', data: prev?.data ?? null }));
    const timer = setTimeout(async () => {
      try {
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token || ctrl.signal.aborted) return;
        const res = await fetch(dzApiUrl('update-schedule'), {
          method: 'POST',
          headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
          body: JSON.stringify({ ...payload, dry_run: true }),
          signal: ctrl.signal,
        });
        const data = await safeResJson(res);
        if (ctrl.signal.aborted) return;
        if (res.ok) {
          setCustPreview({ status: 'ready', data });
        } else if (res.status >= 400 && res.status < 500) {
          // The confirm would be refused the same way, so say why now.
          const why = Array.isArray(data.validation_errors) && data.validation_errors.length
            ? data.validation_errors.join(' ')
            : data.error;
          setCustPreview({ status: 'invalid', error: why || 'These dates can’t be used.' });
        } else {
          // No preview (a blip): fall back to the plain fee wording, never block.
          setCustPreview({ status: 'unavailable' });
        }
      } catch {
        if (!ctrl.signal.aborted) setCustPreview({ status: 'unavailable' });
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [showDeferaZeroScheduleChange, custDatesMode, custUnchanged, tripId, isDeferaCapitalNmi, custRows, custAutoAmounts, custAnchorDate, custFrequency, custPreviewNonce, dzApiUrl]);

  /** "Can't make this payment?": the payment still owed first (a failed one
   *  first) and the days it can move to. Null when there's nothing to move,
   *  the trip used both moves, that payment was already moved, or the cutoff
   *  leaves no day. A Stripe plan the card stopped (restart / pay-off screens)
   *  has no subscription to move. The server decides; this only offers it. */
  const movePayment = useMemo(() => {
    if (!isDeferaStripe || !tripId || !showActions || isTripTerminated || isPaidInFull) return null;
    if (!isDeferaCapitalNmi && (isPlanRestartable || isPlanPayoffRequired)) return null;
    /** An admin looking at a customer's trip: the fee consent is the customer's to give. */
    if (isAdmin && tripOwnerUserId && viewerUserId && viewerUserId !== tripOwnerUserId) return null;
    /** Just moved: hidden until the trip's own record catches up, so the old payment isn't offered again. */
    const recorded = Array.isArray(currentFlightData?._payment_extensions) ? currentFlightData._payment_extensions : [];
    if (justMovedPayment && !recorded.some((e) => e?.to_date === justMovedPayment.to_date)) return null;
    /** A missed payment Defera Credit partly covered can't be moved here. */
    if (dzScheduleData?.move_blocked === 'credit_covered_missed_payment') return null;
    const today = pacificTodayYmd();
    const pick = selectPaymentToMove({
      isNmi: isDeferaCapitalNmi,
      rows: effectiveDisplayPlan?.scheduleRows || [],
      openInvoices: dzScheduleData?.open_invoices || [],
      today,
    });
    if (!pick) return null;
    /** The card on file was declined for good: the move needs a new card first. */
    if (dzScheduleData?.move_blocked === 'card_needs_update') {
      return { ...pick, blocked: 'card_needs_update', choices: null };
    }
    const choices = paymentExtensionChoices({ flightData: currentFlightData, dueDate: pick.dueDate, departureDate, today, failed: pick.failed, paymentId: pick.paymentId });
    return choices.available ? { ...pick, choices } : null;
  }, [isDeferaStripe, isDeferaCapitalNmi, tripId, showActions, isTripTerminated, isPaidInFull, isPlanRestartable, isPlanPayoffRequired, isAdmin, tripOwnerUserId, viewerUserId, effectiveDisplayPlan, dzScheduleData, currentFlightData, departureDate, justMovedPayment]);

  const custPreviewData = custPreview?.status === 'ready' ? custPreview.data : null;
  /** Free only when the server says so: a day already paid for, or a change
   *  Defera granted. Until then the button shows the fee. */
  const custFeeFree = Boolean(custPreviewData && (custPreviewData.fee_waived || custPreviewData.fee_already_paid_today));
  const custPreviewBlocks = custPreview?.status === 'invalid' || Boolean(custPreviewData?.no_change);

  if (!hasPaymentScheduleData || !schedule || !schedule.available) {
    return null;
  }
  if (!isDeferaStripe && (!activePlan || !activePlan.available || !activePlan.schedule || activePlan.schedule.length === 0)) {
    return null;
  }
  // Carts / flows without tripId still use client schedule only
  if (isDeferaStripe && !needsStripeSchedule && !dzScheduleLoading && !hasDisplayPlan && !dzScheduleData) {
    return null;
  }

  // Calculate valid extension options
  const extensionOptions = useMemo(() => {
    if (!effectiveDepartureDate || !activePlan) return [];

    // Get final payment date from finalPaymentDate field or schedule array (last date)
    let finalPaymentDate;
    try {
      if (activePlan.finalPaymentDate) {
        finalPaymentDate = parseISO(activePlan.finalPaymentDate);
      } else if (activePlan.schedule && activePlan.schedule.length > 0) {
        const lastScheduleDate = activePlan.schedule[activePlan.schedule.length - 1];
        finalPaymentDate = parseISO(lastScheduleDate);
      } else {
        return [];
      }
      if (isNaN(finalPaymentDate?.getTime())) return [];
    } catch (error) {
      console.error('Error parsing final payment date:', error);
      return [];
    }

    // Parse departure date
    let departure;
    try {
      departure = typeof effectiveDepartureDate === 'string' ? parseISO(effectiveDepartureDate) : new Date(effectiveDepartureDate);
      if (isNaN(departure.getTime())) {
        return [];
      }
    } catch (error) {
      console.error('Error parsing departure date:', error);
      return [];
    }

    const travelCutoff = subDays(departure, resolvedLastPaymentDaysBeforeDeparture);
    const isWeekly = payment_plan === 'weekly';
    const weekIncrement = isWeekly ? 1 : 2; // 1 week for weekly, 2 weeks for bi-weekly

    const options = [];
    for (let weeks = weekIncrement; weeks <= (isWeekly ? 4 : 8); weeks += weekIncrement) {
      const extendedDate = addWeeks(finalPaymentDate, weeks);
      if (isBefore(extendedDate, travelCutoff) || extendedDate.getTime() === travelCutoff.getTime()) {
        options.push({
          weeks,
          label: isWeekly ? `${weeks} week${weeks > 1 ? 's' : ''}` : `${weeks} weeks`,
          newFinalDate: extendedDate
        });
      }
    }
    return options;
  }, [effectiveDepartureDate, activePlan.finalPaymentDate, activePlan.schedule, payment_plan, resolvedLastPaymentDaysBeforeDeparture]);

  const handleExtensionRequest = async () => {
    if (!selectedExtension) {
      toast.error('Please select an extension period');
      return;
    }

    if (!tripId) {
      console.error('PaymentScheduleDisplay: tripId is missing', { tripId });
      toast.error('Trip ID is missing. Please refresh the page and try again.');
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedOption = extensionOptions.find(opt => opt.weeks.toString() === selectedExtension);
      
      // Get Supabase session for authentication
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }
      
      console.log('PaymentScheduleDisplay: Requesting extension', {
        tripId,
        tripIdType: typeof tripId,
        extension_weeks: parseInt(selectedExtension),
        new_final_payment_date: format(selectedOption.newFinalDate, 'yyyy-MM-dd')
      });
      
      // Call API to save extension request
      const response = await fetch(`/api/trips/${tripId}/extension`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          extension_weeks: parseInt(selectedExtension),
          new_final_payment_date: format(selectedOption.newFinalDate, 'yyyy-MM-dd')
        })
      });

      const responseData = await safeResJson(response);
      console.log('PaymentScheduleDisplay: Extension request response', {
        ok: response.ok,
        status: response.status,
        data: responseData
      });

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to request extension');
      }

      toast.success('Extension request submitted successfully');
      setShowExtensionModal(false);
      setSelectedExtension('');
      
      if (onExtensionRequested) {
        onExtensionRequested();
      }
    } catch (error) {
      console.error('Error requesting extension:', error);
      toast.error(error.message || 'Failed to request extension');
    } finally {
      setIsSubmitting(false);
    }
  };

  const SLICE_PAY_PORTAL_URL = 'https://portal.slicepay.travel/login';

  const handleSlicePayRedirect = () => {
    window.open(SLICE_PAY_PORTAL_URL, '_blank', 'noopener,noreferrer');
    setShowSlicePayRedirectOverlay(false);
  };

  // Handle change payment method - show overlay, then redirect to Slice Pay portal (installment trips)
  const handleChangePaymentMethod = () => {
    setShowSlicePayRedirectOverlay(true);
  };

  // Handle make early payment - show overlay, then redirect to Slice Pay portal (installment trips)
  const handleMakeEarlyPayment = () => {
    setShowSlicePayRedirectOverlay(true);
  };

  // Legacy: handlePaymentSubmit used by Make a Payment modal - no longer shown for installment trips
  const handlePaymentSubmit = () => {
    const bookingRef = tripReference || '';
    let message = '';
    if (paymentOption === 'installment') {
      const amount = effectiveDisplayPlan?.paymentAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';
      message = `I'd like to make an early installment payment of $${amount} for my trip to ${route} [${bookingRef}].`;
    } else if (paymentOption === 'custom') {
      if (!customAmount || isNaN(parseFloat(customAmount)) || parseFloat(customAmount) <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      message = `I'd like to make a custom payment of $${customAmount} for my trip to ${route} [${bookingRef}].`;
    } else if (paymentOption === 'payoff') {
      message = `I'd like to pay off my remaining balance for my trip to ${route} [${bookingRef}].`;
    }
    navigate(buildTommyUrl({ message, bookingReference: bookingRef }));
    setShowPaymentModal(false);
    setCustomAmount('');
    setPaymentOption('installment');
  };

  // Open edit schedule modal (admin only)
  const handleOpenEditSchedule = () => {
    setEditDepositPercentage(deposit_percentage);
    setEditPaymentPlan(payment_plan);
    setEditDepositMode('percentage');
    setEditCustomDepositAmount('');
    setShowEditScheduleModal(true);
  };

  /** ---- Admin: Override Dates (mutates REAL billing, unlike Edit Schedule) --
   *  NMI rail: explicit per-installment {date, amount} list → the update-
   *  schedule edge function's admin `installments` mode (atomic RPC rewrite).
   *  Stripe rails: anchor next-charge date + cadence → trial_end + price +
   *  exact-cycles cancel_at. Subscriptions can't bill irregular lists, so the
   *  editable-rows UI only appears for NMI. State lives in the top hook block
   *  (this file has early returns above this point). */
  const seedOverrideRowsFromSchedule = (includePastDue) => {
    // See src/utils/nmiOverrideSeed.js: mirrors the server planner, including
    // past-due money folded into the balance being rescheduled.
    const seeded = seedNmiOverrideRows(effectiveDisplayPlan?.scheduleRows || [], { includePastDue, nowMs: Date.now() });
    setOverrideRows(seeded.rows);
    // The pot being redistributed = exactly the rows being replaced. Every
    // live sum/auto-split in the modal reconciles against this number.
    setOverrideBaselineCents(seeded.baselineCents);
    setOverridePastDueCents(seeded.pastDueCents);
  };

  /** Mirror of the server planner's auto-split (floor + remainder-on-last) so
   *  the preview shows to the cent what will actually be written. */
  const overrideAutoSplitCents = (n) => {
    if (n <= 0 || overrideBaselineCents <= 0) return [];
    const bse = Math.floor(overrideBaselineCents / n);
    return Array.from({ length: n }, (_, i) => (i === n - 1 ? overrideBaselineCents - bse * (n - 1) : bse));
  };

  const splitCentsEvenly = (totalCents, n) => {
    if (n <= 0 || totalCents <= 0) return [];
    const bse = Math.floor(totalCents / n);
    return Array.from({ length: n }, (_, i) => (i === n - 1 ? totalCents - bse * (n - 1) : bse));
  };

  /** Latest date the customer may pick: departure − cutoff days. */
  const custMaxDate = (() => {
    if (!effectiveDepartureDate) return '';
    const m = String(effectiveDepartureDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    // UTC arithmetic — local setDate() loses a day across the US DST fall-back.
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    d.setUTCDate(d.getUTCDate() - SCHEDULE_DATE_CHANGE_CUTOFF_DAYS);
    return d.toISOString().slice(0, 10);
  })();

  const openCustomerDatesMode = () => {
    setCustErrors([]);
    setCustAutoAmounts(true);
    setCustFrequency(payment_plan === 'weekly' ? 'weekly' : 'biweekly');
    if (isDeferaCapitalNmi) {
      const imminentCutoff = Date.now() + 24 * 3600 * 1000;
      const rows = (effectiveDisplayPlan?.scheduleRows || [])
        .filter((r) => {
          if (r.date && new Date(r.date).getTime() <= imminentCutoff) return false;
          const st = r._nmi_status;
          return st === 'pending' || st === 'retrying';
        })
        .map((r) => ({
          due_date: String(r.date || '').slice(0, 10),
          amount: ((Number(r.amount_cents) || 0) / 100).toFixed(2),
        }))
        .sort((a, b) => (a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0));
      setCustRows(rows);
      setCustBaselineCents(rows.reduce((sum, r) => sum + Math.round((parseFloat(r.amount) || 0) * 100), 0));
    } else {
      const nextRow = (effectiveDisplayPlan?.scheduleRows || []).find((r) => !r.is_paid && !r.paid_ahead && !r.fee_skipped && !r.is_auto_correction);
      const seeded = nextRow?.date ? String(nextRow.date).slice(0, 10) : '';
      setCustAnchorDate(seeded);
      setCustSeededAnchor(seeded);
    }
    setCustDatesMode(true);
  };

  const custRowsTotalCents = custRows.reduce((sum, r) => sum + Math.round((parseFloat(r.amount) || 0) * 100), 0);

  const handleCustomerDatesSubmit = async () => {
    if (!tripId) return;
    setCustErrors([]);
    setCustSaving(true);
    /** Fresh idempotency scope per ATTEMPT (house pattern: payNonceRef /
     *  applyCreditsRequestIdRef). A per-modal-open id let a retry replay the
     *  cached (already-refunded) Stripe PI — a free date change — and locked
     *  declined customers behind the same cached 402 for 24h. */
    const attemptRequestId = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');

      const base = customerDatesPayload({
        tripId,
        isNmi: isDeferaCapitalNmi,
        rows: custRows,
        autoAmounts: custAutoAmounts,
        anchorDate: custAnchorDate,
        frequency: custFrequency,
      });
      if (!base) return;
      const payload = isDeferaCapitalNmi
        ? { ...base, client_request_id: attemptRequestId }
        /** Consent to the fee only when the button asked for it: a change the
         *  preview showed as free must never be charged. */
        : { ...base, pay_date_change_fee: !custFeeFree, client_request_id: attemptRequestId };

      const res = await fetch(dzApiUrl('update-schedule'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify(payload),
      });
      const data = await safeResJson(res);
      if (!res.ok) {
        if (Array.isArray(data.validation_errors) && data.validation_errors.length) {
          setCustErrors(data.validation_errors);
          return;
        }
        if (data.code === 'date_change_fee_required') {
          // Shown as free, but today's pass or the granted change was used up
          // meanwhile (another tab). Nothing was charged; re-quote and ask.
          setCustErrors([`A ${SCHEDULE_DATE_CHANGE_FEE_LABEL} fee applies to this change. Nothing was charged — review it and confirm again.`]);
          setCustPreviewNonce((n) => n + 1);
          return;
        }
        throw new Error(data.error || 'Could not update your payment dates');
      }
      if (data.mode !== 'customer_date_change') {
        throw new Error('Date changes are not available right now — please try again later or contact support. Your card was not charged for a schedule change.');
      }
      toast.success(
        Number(data.fee_cents) > 0
          ? `Payment dates updated — the ${SCHEDULE_DATE_CHANGE_FEE_LABEL} fee was charged to your card on file`
          : data.fee_waived
            ? 'Payment dates updated — no charge, this change was on us'
            : data.fee_already_paid_today
              ? `Payment dates updated — no charge, today's ${SCHEDULE_DATE_CHANGE_FEE_LABEL} already covered it`
              : 'Payment dates updated',
      );
      setShowDeferaZeroScheduleChange(false);
      setCustDatesMode(false);
      await fetchDzSchedule();
      if (onScheduleUpdated) onScheduleUpdated();
      else if (onExtensionRequested) onExtensionRequested();
    } catch (err) {
      toast.error(err.message || 'Could not update your payment dates');
    } finally {
      setCustSaving(false);
    }
  };

  const handleOpenAdminOverride = () => {
    setOverrideErrors([]);
    setOverrideNote('');
    setOverrideAllowTotalChange(false);
    setOverrideIncludePastDue(false);
    setOverrideRollPastDue(false);
    setOverrideAutoAmounts(true);
    if (isDeferaCapitalNmi) {
      seedOverrideRowsFromSchedule(false);
    } else {
      const nextRow = (effectiveDisplayPlan?.scheduleRows || []).find((r) => !r.is_paid && !r.paid_ahead && !r.fee_skipped && !r.is_auto_correction);
      setOverrideAnchorDate(nextRow?.date ? String(nextRow.date).slice(0, 10) : '');
      setOverrideFrequency(payment_plan === 'weekly' ? 'weekly' : 'biweekly');
    }
    setShowAdminOverrideModal(true);
  };

  const overrideRowsTotalCents = overrideRows.reduce(
    (s, r) => s + Math.round((parseFloat(r.amount) || 0) * 100),
    0,
  );

  const handleSaveAdminOverride = async () => {
    if (!isAdmin || !tripId) return;
    setOverrideErrors([]);
    setIsSavingOverride(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');

      const sortedRows = [...overrideRows].sort((a, b) => (a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0));
      const payload = isDeferaCapitalNmi
        ? {
            booking_id: tripId,
            installments: sortedRows.map((r) => (
              overrideAutoAmounts
                ? { due_date: r.due_date }
                : { due_date: r.due_date, amount_cents: Math.round((parseFloat(r.amount) || 0) * 100) }
            )),
            auto_amounts: overrideAutoAmounts,
            include_past_due: overrideIncludePastDue,
            allow_total_change: overrideAutoAmounts ? false : overrideAllowTotalChange,
            note: overrideNote || undefined,
          }
        : {
            booking_id: tripId,
            payment_frequency: overrideFrequency,
            next_charge_date: overrideAnchorDate,
            roll_past_due: overrideRollPastDue,
            note: overrideNote || undefined,
          };

      const res = await fetch(dzApiUrl('update-schedule'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify(payload),
      });
      const data = await safeResJson(res);
      if (!res.ok) {
        if (Array.isArray(data.validation_errors) && data.validation_errors.length) {
          setOverrideErrors(data.validation_errors);
          return;
        }
        throw new Error(data.error || 'Override failed');
      }
      // A stale/mis-deployed edge function ignores unknown fields and would
      // happily run a PLAIN frequency change re-anchored to today — 200 OK,
      // wrong dates. The new functions echo a mode field; require it.
      const expectedMode = isDeferaCapitalNmi ? 'admin_override' : 'admin_anchor_override';
      if (data.mode !== expectedMode) {
        throw new Error('The server does not support date overrides yet (stale deployment) — nothing was applied as requested. Contact engineering before retrying.');
      }
      toast.success('Payment schedule overridden — billing follows the new dates');
      setShowAdminOverrideModal(false);
      await fetchDzSchedule();
      if (onScheduleUpdated) onScheduleUpdated();
      else if (onExtensionRequested) onExtensionRequested();
      if (Array.isArray(data.warnings) && data.warnings.length) {
        data.warnings.forEach((w) => toast.warning ? toast.warning(w) : toast(w));
      }
    } catch (err) {
      toast.error(err.message || 'Failed to override schedule');
    } finally {
      setIsSavingOverride(false);
    }
  };

  // Handle saving edited schedule (admin only)
  const handleSaveSchedule = async () => {
    if (!isAdmin) {
      toast.error('Admin access required');
      return;
    }

    if (!tripId) {
      toast.error('Trip ID not available');
      return;
    }

    console.log('Saving payment schedule for tripId:', tripId, '| Type:', typeof tripId);

    setIsSavingSchedule(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      // Build payload based on deposit mode
      const payload = {
        payment_plan: editPaymentPlan
      };

      if (editDepositMode === 'custom' && editCustomDepositAmount) {
        payload.custom_deposit_amount = parseFloat(editCustomDepositAmount);
      } else {
        payload.deposit_percentage = editDepositPercentage;
      }

      const response = await fetch(`/api/admin/trips/${tripId}/payment-schedule`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await safeResJson(response);
        throw new Error(errData.error || 'Failed to update payment schedule');
      }

      toast.success('Payment schedule updated successfully');
      setShowEditScheduleModal(false);
      
      if (onScheduleUpdated) {
        onScheduleUpdated();
      } else if (onExtensionRequested) {
        // Fall back to extension callback to trigger refresh
        onExtensionRequested();
      }
    } catch (error) {
      console.error('Error updating payment schedule:', error);
      toast.error(error.message || 'Failed to update payment schedule');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  // Handle reverting extension request (admin only)
  const handleRevertExtension = async () => {
    if (!isAdmin) {
      toast.error('Admin access required');
      return;
    }

    setIsReverting(true);
    try {
      // Get Supabase session for authentication
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }
      
      // Call API to revert extension request
      const response = await fetch(`/api/trips/${tripId}/extension`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        const errData = await safeResJson(response);
        throw new Error(errData.error || 'Failed to revert extension request');
      }

      toast.success('Extension request reverted successfully');
      
      if (onExtensionRequested) {
        onExtensionRequested();
      }
    } catch (error) {
      console.error('Error reverting extension:', error);
      toast.error(error.message || 'Failed to revert extension request');
    } finally {
      setIsReverting(false);
    }
  };

  return (
    <>
      <Card className={cn(SECTION_CARD_SHELL, "min-w-0 rounded-3xl overflow-hidden", cardClassName)}>
        <CardHeader className="!p-0">
          {/* Glass band — gradient sheen + faint blue radial + top hairline.
              Mirrors the IndexMinimal "Layaway" card so the modal feels native to
              the marketing surface (per design feedback 2026-05-08). */}
          <div className="relative overflow-hidden border-b border-white/[0.06]">
            <div className="absolute inset-0 bg-gradient-to-b from-white/[0.07] via-white/[0.025] to-transparent pointer-events-none" />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  `radial-gradient(ellipse 70% 140% at 8% 0%, rgba(${BRAND_ROYAL_RGB}, 0.18), transparent 65%)`,
              }}
            />
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/[0.18] to-transparent pointer-events-none" />
            <div className="relative flex items-center gap-2.5 px-6 py-4">
              <Calendar className={cn('w-3.5 h-3.5 flex-shrink-0', BRAND_ICON_CLASS)} />
              <CardTitle className="text-white/90 font-medium text-xs uppercase tracking-widest">
                Payment Schedule
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        {/** `fillHeight` (FlightDetailsModal) applies the flex-1/min-h-0 chain at
         *  md+ only. Below md the modal column is content-sized, so the chain
         *  is pointless - and because the past-payments list is uncapped
         *  (overflow visible), a shrinkable chain let the "Finished paying?"
         *  strip render over the last row on iOS when the schedule endpoint
         *  data landed after first layout (13Q84U, 2026-09-09). */}
        <CardContent className={cn("!p-6 space-y-4 min-w-0", fillHeight && "md:flex md:flex-col md:flex-1 md:min-h-0")}>
          {/* Deposit and Plan Info */}
          {/* Prefer the deposit cents we actually charged at checkout
              (`paymentSchedule.deposit_amount_cents`) over the locally-recomputed
              `schedule.depositAmount`. The recompute divides total_with_fee by the
              hold-fee rate and can drift by a few cents on older bookings whose
              stored `total_with_fee` is missing or whose hold-fee rate has since
              changed — matters most for the new 5%/10% DC tiers where every
              cent is visible. */}
          {(() => {
            const storedDepositDollars = Number.isFinite(Number(paymentSchedule?.deposit_amount_cents))
              && Number(paymentSchedule.deposit_amount_cents) > 0
              ? Number(paymentSchedule.deposit_amount_cents) / 100
              : null;
            const displayedDepositDollars = storedDepositDollars
              ?? schedule.depositAmount
              ?? custom_deposit_amount
              ?? 0;
            return (
          <div className="grid grid-cols-2 gap-4 pb-4 border-b border-white/10">
            <div>
              <p className="text-xs text-white/60 mb-1">Deposit</p>
              <p className="text-sm text-white font-medium">
                {/* toLocaleString, not toFixed: a four-figure deposit rendered
                    "$1234.50" reads as a number before it reads as money. */}
                {custom_deposit_amount
                  ? `$${fmtCurrency(Math.round(displayedDepositDollars * 100))} (${deposit_percentage}%)`
                  : `${deposit_percentage}% ($${fmtCurrency(Math.round(displayedDepositDollars * 100))})`
                }
              </p>
            </div>
            <div>
              <p className="text-xs text-white/60 mb-1">Payment Plan</p>
              <p className="text-sm text-white font-medium capitalize">{payment_plan === 'weekly' ? 'Weekly' : 'Bi-weekly'}</p>
            </div>
            {isAdmin && tripId && showActions && (
              <div className="col-span-2 pt-2 flex flex-wrap items-center gap-2">
                {/* Legacy Slice Pay rail only: this button PUTs the
                    flight_data-only admin endpoint, which is that rail's
                    source of truth. On Stripe/NMI rails it would change the
                    DISPLAYED schedule while the billing rail kept charging
                    the old plan — those rails get Override Dates instead,
                    which mutates real billing. */}
                {!isDeferaStripe && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenEditSchedule}
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-7 text-xs"
                  >
                    <Zap className="w-3 h-3 mr-1.5" />
                    Edit Schedule
                  </Button>
                )}
                {isDeferaStripe && !isTripTerminated && !isPaidInFull && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenAdminOverride}
                    className="bg-brand-500/10 border-brand-500/25 text-brand-100 hover:bg-brand-500/20 hover:border-brand-400/40 h-7 text-xs"
                  >
                    <Zap className="w-3 h-3 mr-1.5" />
                    Override Dates
                  </Button>
                )}
                {(changeFlightHistory?.length > 0 || archivedFlightData) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowChangeHistoryModal(true)}
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-7 text-xs"
                  >
                    <History className="w-3 h-3 mr-1.5" />
                    Change History
                  </Button>
                )}
              </div>
            )}
          </div>
            );
          })()}

          {/* Payment Details — for saved Stripe trips, wait for API so we never flash Slice-calculated amounts */}
          <div className="pb-4 border-b border-white/10">
            {dzScheduleError && needsStripeSchedule ? (
              <div className="rounded-lg border border-brand-500/25 bg-brand-500/5 px-3 py-2 text-xs text-brand-200/90">
                {dzScheduleError}
              </div>
            ) : (
              <>
                {isStaffViewer && dzScheduleData?.trip_total_discrepancy && (
                  <div className="rounded-lg border border-brand-500/30 bg-brand-500/10 px-3 py-2 text-xs text-brand-100/90 mb-3">
                    Database trip total differed from Stripe metadata; displayed amounts use the Stripe plan total (
                    ${(dzScheduleData.trip_total_discrepancy.stripe_metadata_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    ). Booking columns had{' '}
                    ${(dzScheduleData.trip_total_discrepancy.booking_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.
                  </div>
                )}
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-white/60">
                    {isDeferaZero && effectiveDisplayPlan?._stripePerPaymentCents != null
                      ? 'Each charge'
                      : 'Payment Amount'}
                  </p>
                  {needsStripeSchedule && !stripeScheduleReady ? (
                    <div className="flex items-center gap-2 text-sm text-white/45">
                      <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                      <span>Loading…</span>
                    </div>
                  ) : (() => {
                    /** Like "Number of Payments" below, this is a plan disclosure — the
                     *  installment the customer agreed to, not a live average. The NMI
                     *  schedule endpoint derives `per_payment_cents` from `realCharges`,
                     *  which excludes cancelled/superseded/skipped rows, so a cancelled
                     *  plan has no real charges left and the line rendered "$0.00" next
                     *  to a schedule of $311.20 installments (OPTJZQ). Fall back to the
                     *  amount persisted at booking whenever the live figure is empty. */
                    const livePaymentAmount = Number(effectiveDisplayPlan?.paymentAmount) || 0;
                    const persistedInstallmentCents = Number(
                      paymentSchedule?.installment_amount_cents ?? paymentSchedule?.installmentAmountCents
                    );
                    const displayAmount = livePaymentAmount > 0
                      ? livePaymentAmount
                      : (Number.isFinite(persistedInstallmentCents) && persistedInstallmentCents > 0
                        ? persistedInstallmentCents / 100
                        : 0);
                    return (
                      <p className="text-sm text-white font-medium">
                        ${displayAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    );
                  })()}
                </div>
                {(() => {
                  /** Number of Payments is a **plan disclosure**, not a transaction count.
                   *  It must equal the number of installments in the original schedule the
                   *  customer agreed to at booking — stable regardless of:
                   *    - custom early payments (don't add — they're ad-hoc payments AGAINST
                   *      the plan, not extra installments. e.g. ZOBW9F: customer prepaid
                   *      $130 + $125, the plan is still 8 biweekly installments)
                   *    - past-due retries (the cycle is the same cycle, paid later)
                   *    - drift-C compression / auto-correction same-day rows (presentation
                   *      artefacts of how Stripe truncates the final cycle when cancel_at
                   *      lands mid-period — same single installment, billed in two parts)
                   *  Without this anchor, `Number × Payment Amount + Deposit` doesn't
                   *  reconcile to the grand total (e.g. for ZOBW9F that math read 9 ×
                   *  $256.47 + $195.26 = $2,503.49 ≠ $2,245.54). With original installment
                   *  count it does (8 × $256.47 + $195.26 ≈ $2,247.02; the ~$1.50 drift is
                   *  Stripe's last-cycle rounding which the schedule list shows truthfully).
                   *  Source of truth (in priority order):
                   *    1. `payment_schedule.installment_count` — persisted at booking time
                   *       (server-side, in UTC), captures the exact plan the customer
                   *       agreed to at checkout. Stable forever, immune to timezone drift.
                   *    2. `activePlan.paymentCount` — client-side recompute via
                   *       `calculatePaymentSchedule`. Reproduces the booking flow but uses
                   *       browser-local-day rounding, so a customer in PT and an admin in
                   *       UTC viewing the same trip can derive different counts (3ET73N
                   *       canary: PT browser computes 5 biweekly, UTC browser computes 4 —
                   *       same booking, both renders shown to humans). Used for legacy
                   *       bookings without `installment_count` persisted.
                   *    3. `effectiveDisplayPlan.paymentCount` — pulled from Stripe via the
                   *       schedule endpoint. Reflects REMAINING cycles, not original plan,
                   *       so it decrements as invoices fire (bad fit for "Number of
                   *       Payments" which the docstring above demands be stable). Last
                   *       resort. */
                  const persistedInstallmentCount = Number(
                    paymentSchedule?.installment_count ?? paymentSchedule?.installmentCount
                  );
                  const installmentCount = Number.isFinite(persistedInstallmentCount) && persistedInstallmentCount > 0
                    ? persistedInstallmentCount
                    : (activePlan?.paymentCount
                      ?? effectiveDisplayPlan?.paymentCount
                      ?? 0);
                  /** Remaining Payments = original installments that haven't cleared. We
                   *  count only `type === 'subscription'` paid entries; one-time custom
                   *  payments are excluded because they don't consume an installment slot
                   *  in the customer's mental model (they reduce the principal owed via
                   *  the same per-payment-amount-recompute path the charge endpoint runs,
                   *  but the plan still says "you have N installments"). For ZOBW9F: 0
                   *  sub invoices have fired (still trialing) → "Remaining 8". For
                   *  9SA00R: 1 sub invoice paid (May 4), 1 past-due (May 11), 1 upcoming
                   *  (May 18) → "Remaining 2" (the past-due hasn't cleared yet so it's
                   *  still remaining). Always shown when the plan has any installments —
                   *  hiding this line on freshly-created trips made the disclosure feel
                   *  inconsistent (Number of Payments: 8 but no Remaining context). */
                  const paidInstallmentsCount = isDeferaStripe
                    ? countPaidInstallmentSlots(
                        dzScheduleData?.payment_history,
                        installmentCount,
                        remainingCents,
                        tripStatusTerminated,
                      )
                    : (dzScheduleData?.payment_history?.length || 0);
                  /** Remaining Payments counts what will actually still be charged, not
                   *  `original plan − cleared`. Those two agree on a plan that runs as
                   *  booked, and diverge hard on one restructured mid-flight: NQLL7E was
                   *  reinstated in July, re-amortizing $1,124.60 from 17 × ~$66 into
                   *  larger $151.43 cycles, so "17 booked − 4 cleared = 13" rendered
                   *  next to a live plan with exactly 2 rows left.
                   *
                   *  `schedule.num_payments` is the server's future-charge count. Open
                   *  past-due invoices are billed outside that list and render their own
                   *  rows, so they're added back — otherwise a failing cycle would
                   *  silently drop out of the count while still being retried.
                   *
                   *  On a plan that never restructured this is arithmetically identical
                   *  to the old expression, so the existing canaries are untouched:
                   *  ZOBW9F (8 booked, 0 fired → 8) and 9SA00R (1 paid, 1 past-due,
                   *  1 upcoming → 2) both land where they did before.
                   *
                   *  Stripe rails only. NMI reports `num_payments` as
                   *  `realCharges.length` — every non-skipped row INCLUDING cleared ones,
                   *  and minus `credited` ones — so there it's a plan-size figure, not a
                   *  remainder. NMI keeps the original arithmetic. */
                  const scheduledRemaining = Number(dzScheduleData?.schedule?.num_payments);
                  /** NMI: count the rows that are still open — pending, retrying,
                   *  or declined/delinquent awaiting the customer. 1OSUWO showed "6"
                   *  (8 − 2, with two make-a-payments counted as installment slots)
                   *  while five rows were genuinely outstanding and the restart card
                   *  said "4×". Skipped, credited, superseded, cancelled and paid rows
                   *  are none of the customer's remaining payments. */
                  const nmiOpenStatuses = ['pending', 'retrying', 'declined', 'error', 'delinquent'];
                  const nmiRemainingCount = isDeferaCapitalNmi && Array.isArray(effectiveDisplayPlan?.scheduleRows)
                    ? effectiveDisplayPlan.scheduleRows.filter((r) => nmiOpenStatuses.includes(r._nmi_status)).length
                    : null;
                  const liveRemainingCount =
                    isDeferaCapitalNmi && nmiRemainingCount != null
                      ? nmiRemainingCount
                      : isDeferaStripe && !isDeferaCapitalNmi && Number.isFinite(scheduledRemaining)
                        ? Math.max(0, scheduledRemaining) + (dzScheduleData?.open_invoices?.length || 0)
                        : null;
                  const remainingCount = liveRemainingCount ?? Math.max(0, installmentCount - paidInstallmentsCount);
                  /** A live count of 0 is only the truth when nothing is owed. When the
                   *  sub died with a balance still on it (C1NT4Q: Stripe's scheduler won
                   *  the tie at cancel_at and the last installment never billed) there
                   *  are no scheduled rows AND real money left — "Remaining Payments 0"
                   *  would read as "all done". The payoff card below is the honest
                   *  statement there, so drop the line rather than print a lie. */
                  const remainingCountIsHonest = remainingCount > 0 || !(remainingCents > 0);
                  return (
                    <>
                      <div className="flex justify-between items-center">
                        <p className="text-xs text-white/60">Number of Payments</p>
                        {needsStripeSchedule && !stripeScheduleReady ? (
                          <div className="h-5 w-8 rounded bg-white/10 animate-pulse" aria-hidden />
                        ) : (
                          <p className="text-sm text-white font-medium">{installmentCount}</p>
                        )}
                      </div>
                      {/** Suppressed once the booking is cancelled/defaulted: the plan is
                        *  dead, so "Remaining Payments" has no honest value. 0 reads as
                        *  "all done" (the bug this whole guard exists to kill) and the real
                        *  never-charged count reads as "you still owe 20 payments". The
                        *  terminal banner below already says no further payments will be
                        *  charged, which is the only true statement here. */}
                      {installmentCount > 0 && !tripStatusTerminated && remainingCountIsHonest && (
                        <div className="flex justify-between items-center mt-2">
                          <p className="text-xs text-white/60">Remaining Payments</p>
                          {needsStripeSchedule && !stripeScheduleReady ? (
                            <div className="h-5 w-8 rounded bg-white/10 animate-pulse" aria-hidden />
                          ) : (
                            <p className="text-sm text-white font-medium">{remainingCount}</p>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>

          {flightCreditCents > 0 && (
            <div className="rounded-xl border border-teal-500/30 bg-teal-500/10 p-4">
              <p className="text-teal-400 text-sm font-normal tabular-nums">Flight credit: ${(flightCreditCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-teal-400/70 text-xs mt-1 font-normal">You’ll receive this credit once your installment plan is paid in full.</p>
            </div>
          )}

          {/* Upcoming Payments List */}
          <div className={cn("min-w-0", fillHeight && "md:flex md:flex-col md:flex-1 md:min-h-0")}>
            {/* Buttons - shown first on mobile, on right on desktop */}
            {/* Only show action buttons in Trips (showActions=true), not in Carts.
                Hide entirely when the trip is in a terminal state (cancelled/defaulted
                or Stripe sub fully ended) — Make a Payment / Update Card / Change
                Schedule against a dead subscription would either no-op or 4xx and
                confuse the customer. */}
            {tripId && showActions && !isTripTerminated && (
              // Mobile: clean 2-per-row action grid. When the count is odd, the last
              // button spans the full row so the grid never leaves a dangling half-cell.
              <div className="grid sm:hidden grid-cols-2 gap-2 mb-6">
                {isDeferaStripe ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeferaZeroUpdateCard}
                      className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full px-2.5")}
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                      Update Card
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeferaZeroMakePayment(true)}
                      className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full px-2.5")}
                    >
                      <Zap className="w-3.5 h-3.5 mr-1.5" />
                      Make a Payment
                    </Button>
                    {/* Apply Credits: only when the customer has Defera Credits (hidden at $0). */}
                    {showApplyCreditsButton && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleOpenApplyCredits}
                        className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full px-2.5")}
                      >
                        <Wallet className="w-3.5 h-3.5 mr-1.5" />
                        Apply Credits · ${fmtCurrency(deferaCreditCents)}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowDeferaZeroScheduleChange(true); setDzNewFrequency(payment_plan); setDzPreview(null); }}
                      className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full px-2.5", !showSkipPaymentButton && !showApplyCreditsButton && "col-span-2")}
                    >
                      <Clock className="w-3.5 h-3.5 mr-1.5" />
                      Change Schedule
                    </Button>
                    {/* Skip Upcoming Payment: Defera Capital + Confirmed trips only (backend enforces too). */}
                    {showSkipPaymentButton && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleOpenSkipPayment}
                        className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full px-2.5", showApplyCreditsButton && "col-span-2")}
                      >
                        <SkipForward className="w-3.5 h-3.5 mr-1.5" />
                        Skip Payment
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {hasExtensionRequest && isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRevertExtension}
                        disabled={isReverting}
                        className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full px-2.5")}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        {isReverting ? 'Reverting...' : 'Revert Extension'}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowExtensionModal(true)}
                      disabled={hasExtensionRequest}
                      className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full px-2.5")}
                    >
                      <Clock className="w-3.5 h-3.5 mr-1.5" />
                      {hasExtensionRequest ? 'Extension Requested' : 'Request Extension'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMakeEarlyPayment}
                      className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full px-2.5")}
                    >
                      <Zap className="w-3.5 h-3.5 mr-1.5" />
                      Make a Payment
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleChangePaymentMethod}
                      className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full px-2.5", !(hasExtensionRequest && isAdmin) && "col-span-2")}
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                      Update Card
                    </Button>
                  </>
                )}
              </div>
            )}
            {/* Desktop: Line 1 = buttons, Line 2 = Upcoming Payments label */}
            <div className="hidden sm:block mb-3">
              {tripId && showActions && !isTripTerminated && (
                // Desktop: same 2-per-row action grid as mobile — replaces the old
                // flex-wrap row that left a lone 4th button dangling under the other
                // three. Odd counts promote the last button to span the full row.
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {isDeferaStripe ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeferaZeroUpdateCard}
                        className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full")}
                      >
                        <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                        Update Card
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeferaZeroMakePayment(true)}
                        className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full")}
                      >
                        <Zap className="w-3.5 h-3.5 mr-1.5" />
                        Make a Payment
                      </Button>
                      {/* Apply Credits: only when the customer has Defera Credits (hidden at $0). */}
                      {showApplyCreditsButton && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleOpenApplyCredits}
                          className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full")}
                        >
                          <Wallet className="w-3.5 h-3.5 mr-1.5" />
                          Apply Credits · ${fmtCurrency(deferaCreditCents)}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setShowDeferaZeroScheduleChange(true); setDzNewFrequency(payment_plan); setDzPreview(null); }}
                        className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full", !showSkipPaymentButton && !showApplyCreditsButton && "col-span-2")}
                      >
                        <Clock className="w-3.5 h-3.5 mr-1.5" />
                        Change Schedule
                      </Button>
                      {/* Skip Upcoming Payment: Defera Capital + Confirmed trips only (backend enforces too). */}
                      {showSkipPaymentButton && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleOpenSkipPayment}
                          className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full", showApplyCreditsButton && "col-span-2")}
                        >
                          <SkipForward className="w-3.5 h-3.5 mr-1.5" />
                          Skip Payment
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleChangePaymentMethod}
                        className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full")}
                      >
                        <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                        Update Card
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleMakeEarlyPayment}
                        className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full")}
                      >
                        <Zap className="w-3.5 h-3.5 mr-1.5" />
                        Make a Payment
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowExtensionModal(true)}
                        disabled={hasExtensionRequest}
                        className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full", !(hasExtensionRequest && isAdmin) && "col-span-2")}
                      >
                        <Clock className="w-3.5 h-3.5 mr-1.5" />
                        {hasExtensionRequest ? 'Extension Requested' : 'Request Extension'}
                      </Button>
                      {hasExtensionRequest && isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRevertExtension}
                          disabled={isReverting}
                          className={cn(PRIMARY_ACTION_BTN_CLASS, "w-full")}
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                          {isReverting ? 'Reverting...' : 'Revert Extension'}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            {movePayment?.blocked === 'card_needs_update' && (
              <button
                type="button"
                onClick={handleDeferaZeroUpdateCard}
                className="mb-4 w-full flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left transition-colors hover:border-white/25 hover:bg-white/[0.05] touch-manipulation"
              >
                <CreditCard className="w-4 h-4 text-white/70 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-white/90 text-sm font-medium">Missed a payment? Update your card first</span>
                  <span className="block text-white/55 text-xs font-normal mt-0.5">Your bank won\u2019t approve the card on file again. Once it\u2019s updated, you can pick a new day for the payment.</span>
                </span>
                <ArrowRight className="w-4 h-4 text-white/45 shrink-0" />
              </button>
            )}
            {movePayment?.choices && (
              <button
                type="button"
                onClick={() => setShowMovePayment(true)}
                className="mb-4 w-full flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left transition-colors hover:border-white/25 hover:bg-white/[0.05] touch-manipulation"
              >
                <CalendarClock className="w-4 h-4 text-white/70 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-white/90 text-sm font-medium">
                    {movePayment.failed ? 'Missed a payment? Pick a day that works' : `Can\u2019t make your ${formatScheduleDate(movePayment.dueDate, 'MMM d')} payment?`}
                  </span>
                  <span className="block text-white/55 text-xs font-normal mt-0.5">
                    {movePayment.failed && movePayment.choices.retryDate
                      ? `We\u2019ll try it again on ${formatScheduleDate(movePayment.choices.retryDate, 'MMM d')}, or pick a later day, up to 14 days from today.`
                      : movePayment.failed
                        ? 'Choose when you\u2019ll be able to pay, up to 14 days from today.'
                        : 'Move it up to 14 days later. Nothing is charged today.'}
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-white/45 shrink-0" />
              </button>
            )}
            {/* Refetch after actions — initial load uses skeletons in Payment Details / list */}
            {isDeferaStripe && dzScheduleLoading && stripeScheduleReady && (
              <div className="mb-3 flex items-center gap-2 text-xs text-white/50">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Updating schedule…
              </div>
            )}
            {/* Unified ledger stats strip — replaces the previous "Payments made" header that
                competed with the now-removed "Upcoming Payments" subtitle. Reads as a single
                progress line ("X of Y paid · $A paid · $B remaining") that summarizes the whole
                plan at a glance, leaving the pill list below to be a clean, continuous timeline:
                brand-blue check pills for past, white $ pills for upcoming. */}
            {isDeferaStripe && (dzScheduleData?.payment_history?.length > 0 || dzScheduleData?.open_invoices?.length > 0 || failedAttempts.length > 0 || (effectiveDisplayPlan?.scheduleRows || []).some((r) => r.fee_skipped || r.paid_ahead)) && (() => {
              const history = dzScheduleData?.payment_history || [];
              const openInvoices = dzScheduleData?.open_invoices || [];
              /** Fee-skipped and paid-ahead installments group with the PAID pills (not
               *  the upcoming timeline): they're settled — nothing will ever be charged
               *  on those dates — so they read as "handled" history, same blue fill as
               *  paid. */
              const skippedRows = (effectiveDisplayPlan?.scheduleRows || []).filter((r) => r.fee_skipped);
              /** Paid-ahead installments (skip-mode Pay Ahead) get the same treatment:
               *  the customer already paid these dates early, so they belong with the
               *  payments made — same blue fill — instead of as $0 rows in the upcoming
               *  timeline (where "Jul 10 — Paid ahead — $0.00" read as money applied to
               *  the wrong date; see the 4FLGZU support ticket).
               *
               *  The server only emits paid-ahead rows for dates **still in the future**
               *  (`t > nowMs`, see defera-zero-schedule + defera-capital-schedule), so once
               *  Jun 9 has passed the only thing left was the Jun 7 payment row saying
               *  "Covers Jun 9 installment" with no matching $0 placeholder — visually
               *  inconsistent with future paid-ahead pairs that DO show both rows. We
               *  patch that here by synthesizing past placeholders from each history
               *  item's `covers_dates`, deduped by date so server-provided future rows
               *  win (they carry `covered_amount_cents`). */
              const serverPaidAheadRows = (effectiveDisplayPlan?.scheduleRows || []).filter((r) => r.paid_ahead);
              const paidAheadRows = (() => {
                const byDate = new Map();
                serverPaidAheadRows.forEach((r) => {
                  if (typeof r?.date === 'string') byDate.set(r.date.slice(0, 10), r);
                });
                history.forEach((p) => {
                  const datesArr = Array.isArray(p?.covers_dates) ? p.covers_dates : [];
                  datesArr.forEach((d) => {
                    if (typeof d !== 'string') return;
                    const key = d.slice(0, 10);
                    if (byDate.has(key)) return;
                    byDate.set(key, {
                      date: d,
                      amount_cents: 0,
                      paid_ahead: true,
                      covered_by_date: typeof p?.date === 'string' ? p.date : null,
                      covered_amount_cents: null,
                    });
                  });
                });
                return Array.from(byDate.values());
              })();
              const installmentsPaidCents = history.reduce((sum, p) => sum + (Number(p?.amount_cents) || 0), 0);
              /** Prefer the server's `remaining_cents` (Stripe-truth: total − paid invoices −
               *  one-time payments) over `total − sum(history)` so the number always matches the
               *  schedule headline; falls back to derivation if the server didn't surface it. */
              const installmentsRemainingCents = Number.isFinite(Number(dzScheduleData?.remaining_cents))
                ? Math.max(0, Number(dzScheduleData.remaining_cents))
                : Math.max(0, (Number(dzScheduleData?.total_amount_cents) || 0) - installmentsPaidCents);
              const installmentsTotalCents = installmentsPaidCents + installmentsRemainingCents;
              /** "Paid X/Y" tracks progress through the **original installment plan**, not
               *  the dynamic transaction list. Y is the booking-time installment count
               *  (`activePlan.paymentCount` — 8 for ZOBW9F, 3 for 9SA00R). X is the count
               *  of installments that have actually cleared (= paid subscription invoices
               *  with amount > 0). Custom one-time prepayments don't consume an installment
               *  slot, and past-due invoices count toward "remaining" until they clear.
               *  The dollar line below ($A / $B) is independent: it shows the trip's
               *  total cash position (deposit + customs + paid invoices) / grand total,
               *  so the customer can see all their money in one place even though only
               *  installments move the X/Y count. */
              /** Same source-of-truth priority as the "Number of Payments" header above
               *  (see docstring there) — persisted snapshot wins over client-side
               *  recompute so the X/Y progress line never disagrees with the header. */
              const persistedInstallmentCount = Number(
                paymentSchedule?.installment_count ?? paymentSchedule?.installmentCount
              );
              const installmentCount = Number.isFinite(persistedInstallmentCount) && persistedInstallmentCount > 0
                ? persistedInstallmentCount
                : (activePlan?.paymentCount
                  ?? effectiveDisplayPlan?.paymentCount
                  ?? 0);
              /** Installments paid early via skip-mode Pay Ahead count toward X too:
               *  their dates now render as blue "paid" pills, so a customer who paid
               *  1 of 3 ahead must see "Paid 1/3", not "Paid 0/3" next to a checked
               *  pill. Counted from `covers_dates` on one-time history entries (stable
               *  forever) rather than the paid-ahead schedule rows (which fall off once
               *  the covered date passes). */
              const paidInstallmentsCount = countPaidInstallmentSlots(
                history,
                installmentCount,
                installmentsRemainingCents,
                tripStatusTerminated,
              );
              const pastDueCount = openInvoices.length;
              const pastDueTotalCents = Number(dzScheduleData?.open_invoices_total_cents) || 0;
              /** True trip grand total — base + service fee + hold fee + (deposit, if any).
               *  For Defera Capital the deposit + upfront fees were paid at booking time and
               *  are NOT in `payment_history` (Stripe subscription only knows about installments),
               *  so we have to add them back from the parent-supplied grand total.
               *
               *  Defera Zero is $0-down by definition: every dollar of the trip flows through
               *  the subscription, so paid history + remaining_cents (both Stripe-truth) IS the
               *  grand total. Never trust the parent-supplied row total for DZ — when the row's
               *  `grand_total_with_hold` drifts (AdminEditModal double-fee corruption, C1NT4Q),
               *  using it here fabricates the difference as phantom "paid upfront" money and the
               *  progress strip stops reconciling ($1,742.93 paid / $3,526.86 against a
               *  $2,898.93 Stripe plan). */
              /** Terminal trips are excluded from the Defera Zero branch: it derives the
               *  denominator from the live plan aggregates, which collapse to just the
               *  paid amount once cancellation voids the open rows — the bar would read
               *  "$X / $X" again by a different route. A dead plan's only stable "of how
               *  much" is the trip's own grand total. */
              const grandTotalCents = isDeferaZero && installmentsTotalCents > 0 && !tripStatusTerminated
                ? installmentsTotalCents
                : Number(currentGrandTotalCents) > 0
                  ? Number(currentGrandTotalCents)
                  : installmentsTotalCents;
              /** Anything in the grand total that isn't part of the installment subscription was
               *  by definition collected upfront at booking (deposit + service fee + hold fee). */
              const upfrontPaidCents = Math.max(0, grandTotalCents - installmentsTotalCents);
              /** That upfront inference is only sound while the plan is alive. Cancelling an
               *  NMI plan flips every open installment to `cancelled`, which the schedule
               *  endpoint drops from `remaining_cents` — so `installmentsTotalCents` shrinks
               *  to just the cash collected, and `grandTotal − installmentsTotal` fabricates
               *  the ENTIRE uncollected balance as "paid upfront at booking". OPTJZQ: $933.60
               *  actually collected, $6,224.05 invented, strip rendered $7,157.65 / $7,157.65
               *  at 100% under a CANCELLED banner. Every cancelled NMI trip carried it
               *  ($13.8k of phantom money across 11 trips), including three that had paid
               *  literally $0 and still showed as complete.
               *
               *  A terminated plan takes the server's audited figure instead:
               *  `paid_amount_cents` is deposit + cash-collected installments
               *  (success/superseded/credited — sumNmiCollectedCents) on the NMI rail,
               *  which is the cash the NMI cancel endpoint pays back, and total −
               *  remaining on both Stripe rails, which also counts Defera Credit an
               *  invoice consumed (the Stripe cancel pays back the cash part and
               *  restores that credit separately). Floored at the history sum so
               *  a partial payment that reduced an installment without clearing it can't
               *  push the figure BELOW money the customer can see in the pills above. */
              const serverPaidCashCents = Number(dzScheduleData?.paid_amount_cents);
              /** NMI reports its OWN authoritative paid figure (deposit net of
               *  checkout credit + collected attempts + partial slices), so the
               *  `grandTotal − installmentsTotal` inference is never needed here
               *  — and is actively dangerous. That inference assumes the plan
               *  still covers the trip; when installments were cancelled without
               *  replacement it fabricates the shortfall as "paid upfront at
               *  booking" and the trip reads as internally consistent while the
               *  money is simply missing.
               *
               *  EROBQA canary (2026-08-18): one $128.70 installment cancelled
               *  on booking day with nothing inserted. The strip showed
               *  $265.98 paid against $137.28 actually collected — the phantom
               *  was EXACTLY the gap, so nothing looked wrong until the customer
               *  did the arithmetic herself and opened a ticket.
               *
               *  Consumed Defera Credit is added back because `paid_amount_cents`
               *  is cash-only on this rail: a credit-covered installment is money
               *  the customer really paid, and dropping it would under-report
               *  every credit-using trip (10 live ones) to make one honest.
               *  Guarded on a finite server figure so a failed schedule fetch
               *  falls back to the old behaviour rather than showing $0 paid. */
              const nmiCreditAppliedCents = Number(dzScheduleData?.customer_credit_cents) || 0;
              const useServerPaid = isDeferaCapitalNmi && Number.isFinite(serverPaidCashCents);
              const totalPaidCents = tripStatusTerminated
                ? Math.max(
                    installmentsPaidCents,
                    Number.isFinite(serverPaidCashCents) && serverPaidCashCents > 0 ? serverPaidCashCents : 0,
                  )
                : useServerPaid
                  ? Math.max(installmentsPaidCents, serverPaidCashCents + nmiCreditAppliedCents)
                  : upfrontPaidCents + installmentsPaidCents;
              const totalCents = grandTotalCents;
              const remainingCents = Math.max(0, totalCents - totalPaidCents);
              /** Defera Credit returned at cancellation, stamped on the booking by all four
               *  cancel endpoints (`defera-capital-nmi-cancel-trip`, `defera-capital-cancel-trip`,
               *  `defera-zero-cancel-trip`, `defera-credit-default-opt-in`). Replaces the
               *  "$X remaining" trailing line on a dead plan — "remaining" is meaningless once
               *  nothing more will be charged, and this is the number the customer actually
               *  wants: what they got back and can spend. Absent (0) on defaulted trips that
               *  never opted into credit, in which case the line is dropped entirely. */
              const cancelledForCreditCents = Number(currentFlightData?._cancelled_for_credit_cents) || 0;
              const progressPct = totalCents > 0
                ? Math.min(100, Math.max(0, (totalPaidCents / totalCents) * 100))
                : 0;
              /** Committed credit that will retire the rest of the balance without the
               *  card being touched again. `remaining_cents` deliberately counts only
               *  money an invoice has CONSUMED, so credit the customer already
               *  committed — debited from their ledger at Apply time — sits outside it
               *  until Stripe creates the cycle that nets it. Both figures are true,
               *  but "$302.85 remaining" next to "$302.86 credit on file" reads as a
               *  bill the customer still owes when their card will never be charged
               *  again (NQLL7E).
               *
               *  We do NOT fold this into `remaining_cents`: Risk Review, Capital
               *  forecasting and collections all consume that figure as "not yet
               *  settled", and the credit is genuinely recoverable until consumed —
               *  `restore_defera_credit_on_cancel` (migration 082) releases the FULL
               *  committed total on cancellation. So the distinction stays in the data
               *  and gets resolved here, in the one place a customer reads it.
               *
               *  NMI is excluded by construction: its rows are rewritten to net
               *  amounts when credit is committed, so the credit is ALREADY inside
               *  `remaining_cents` there and counting it again would double it.
               *
               *  A queued pay-ahead credit (`subscription_credit_cents`) is not in
               *  this sum either: the payment that funded it is already counted in
               *  `remaining_cents`, and the rows below show it coming off the next
               *  payment. Subtracting it here as well said "$389.20 due · $200.00
               *  covered by Defera Credit" on BDDD7D, whose payments add up to
               *  $589.20 — and it isn't Defera Credit (audit 2026-10-04). */
              const committedCreditCents = isDeferaCapitalNmi ? 0 : Math.max(0,
                (Number(dzScheduleData?.customer_credit_cents) || 0)
                + (Number(dzScheduleData?.defera_credit_pending_cents) || 0),
              );
              /** Clamped for the bar so a deliberate over-commit (NQLL7E carries 1c
               *  extra to absorb Stripe's gross-vs-owed rounding) can't overflow it. */
              const creditCoverageCents = Math.min(remainingCents, committedCreditCents);
              const dueNowCents = Math.max(0, remainingCents - committedCreditCents);
              const creditPct = totalCents > 0
                ? Math.min(100 - progressPct, Math.max(0, (creditCoverageCents / totalCents) * 100))
                : 0;
              return (
                <div className="mb-4">
                  {/* Single-accent progress strip — eyebrow + grand total on top, Royal Blue
                      progress bar in the middle, remaining balance trailing right below the bar
                      so the customer can see how close they are to the finish line. On a
                      cancelled/defaulted trip the trailing line switches to the Defera Credit
                      returned (there is no finish line left to run toward). */}
                  <div className="px-1 mb-3">
                    <div className="flex items-center justify-between gap-3">
                      {/* "installments" is load-bearing: X/Y counts plan
                          installments only, while the dollar figure to the
                          right includes the deposit too — without the word,
                          "Paid 2/14 · $196.43" reads like the count is
                          missing the deposit (F27QK3, 2026-08-04). */}
                      <p className="text-[11px] uppercase tracking-widest text-white/45 font-normal tabular-nums">
                        Paid {paidInstallmentsCount}/{installmentCount || paidInstallmentsCount} installments
                      </p>
                      <p className="text-[11px] tabular-nums whitespace-nowrap font-medium">
                        <span className="text-white/75">${fmtCurrency(totalPaidCents)}</span>
                        <span className="text-white/25"> / </span>
                        <span className="text-white/55">${fmtCurrency(totalCents)}</span>
                      </p>
                    </div>
                    {/* Two segments: solid brand for money an invoice has actually taken,
                        a paler emerald tail for the committed credit that will retire the
                        rest. Same emerald as the credit banner below so the two read as
                        one fact. Inner fills drop `rounded-full` — the track's
                        rounded+overflow-hidden shapes both ends, and rounding each
                        segment would notch the seam between them. */}
                    <div className="mt-2 h-[3px] w-full rounded-full bg-white/[0.06] overflow-hidden flex">
                      <div
                        className="h-full bg-gradient-to-r from-brand-800 to-brand-900 transition-[width] duration-500 ease-out"
                        style={{ width: `${progressPct}%` }}
                      />
                      {creditPct > 0 && (
                        <div
                          className="h-full bg-emerald-500/40 transition-[width] duration-500 ease-out"
                          style={{ width: `${creditPct}%` }}
                        />
                      )}
                    </div>
                    {tripStatusTerminated ? (
                      cancelledForCreditCents > 0 && (
                        <p className="mt-1.5 text-[10.5px] tabular-nums text-emerald-400/70 font-medium text-right">
                          ${fmtCurrency(cancelledForCreditCents)} added to your Defera Credit
                        </p>
                      )
                    ) : committedCreditCents > 0 ? (
                      /* Both halves of the truth: what the card still owes, and what
                         credit is standing behind the rest. Handles partial coverage
                         too — "$202.85 due · $100.00 covered by Defera Credit". */
                      <p className="mt-1.5 text-[10.5px] tabular-nums font-medium text-right">
                        <span className="text-white/45">${fmtCurrency(dueNowCents)} due</span>
                        <span className="text-white/25"> · </span>
                        <span className="text-emerald-400/70">
                          ${fmtCurrency(committedCreditCents)} covered by Defera Credit
                        </span>
                      </p>
                    ) : (
                      <p className="mt-1.5 text-[10.5px] tabular-nums text-white/45 font-medium text-right">
                        ${fmtCurrency(remainingCents)} remaining
                      </p>
                    )}
                    {/* The plan total carries the Faye premium, so say so where the
                        total is read; the policy itself is on the Travel Insurance card. */}
                    {Number(insurancePremiumCents) > 0 && !tripStatusTerminated && (
                      <p className="mt-1 text-[10.5px] tabular-nums text-white/40 font-normal text-right">
                        Includes ${fmtCurrency(Number(insurancePremiumCents))} travel insurance
                      </p>
                    )}
                  </div>
                  {/* Past payments — fulfilled installments render as solid brand-blue pills:
                      "done" reads as filled-with-brand rather than faded-out, mirroring how the
                      progress strip fills as the plan is paid down.
                      No max-h cap: a booking can legitimately accumulate many paid items
                      (deposit + N installments + one-off corrections), and the parent modal
                      already provides outer scroll. Capping inside created a nested-scroll trap
                      that hid the last row on fully-paid trips like 12I2AQ. */}
                  <div className="space-y-2 pr-1">
                    {/** Merge paid + paid-ahead + skipped past rows into a single
                     *  chronologically-sorted list so each $0 paid-ahead placeholder
                     *  sits directly after the payment that covered it (instead of
                     *  all paid rows clustering above all paid-ahead rows, which
                     *  confused customers like 4FLGZU when "Covers Jun 9 installment"
                     *  on a Jun 7 payment had no visible Jun 9 partner row). */}
                    {[
                      ...history.map((p, i) => ({ kind: 'paid', data: p, idx: i, sortDate: p?.date })),
                      ...paidAheadRows.map((r) => ({ kind: 'paid_ahead', data: r, sortDate: r?.date })),
                      ...skippedRows.map((r) => ({ kind: 'skipped', data: r, sortDate: r?.date })),
                      // Declined attempts sit in the same timeline so the story reads in
                      // order (declined, retried, paid). Display only — nothing above counts them.
                      ...failedAttempts.map((a) => ({ kind: 'failed', data: a, sortDate: a?.at })),
                    ].sort((a, b) => {
                      const ta = a.sortDate ? new Date(a.sortDate).getTime() : 0;
                      const tb = b.sortDate ? new Date(b.sortDate).getTime() : 0;
                      return ta - tb;
                    }).map((entry) => {
                    if (entry.kind === 'failed') {
                      return <FailedAttemptPill key={`failed-${entry.data.id}`} attempt={entry.data} showRaw={attemptsStaffView} />;
                    }
                    if (entry.kind === 'paid') {
                      const p = entry.data;
                      const i = entry.idx;
                      /** Skip-mode "Pay Ahead" payments carry the installment dates they
                       *  settled. Spell that out on the payment pill itself so the customer
                       *  reads "this charge covered Jul 10" right where the money shows up —
                       *  the matching $0 row in the upcoming list says the inverse. */
                      const coversDates = (Array.isArray(p.covers_dates) ? p.covers_dates : [])
                        .map((d) => parseISO(d))
                        .filter((d) => !isNaN(d.getTime()));
                      const isPaidEarly = coversDates.length > 0;
                      /** Deposit rows arrive from the schedule endpoints tagged with
                       *  `type: 'deposit'` (NMI: synthesized from `defera_capital_nmi_pending_checkouts`;
                       *  Stripe: the customer-level deposit invoice with metadata
                       *  `purpose=defera_capital_deposit_invoice`). Render a distinct sub-line
                       *  and label so ops / the customer can eyeball the up-front charge
                       *  alongside the installments, instead of guessing which of several
                       *  same-day pills was the deposit. Falls through to the standard
                       *  paid-pill layout otherwise. */
                      const isDeposit = p?.type === 'deposit';
                      /** The checkout-time Defera Credit application. A trip paid
                       *  ENTIRELY with credit has no cash rows at all — every
                       *  installment is a $0 `credited` row and the deposit pill is
                       *  suppressed — so without this the customer read "paid off"
                       *  above an empty timeline and had no idea where their money
                       *  went (1CICJK: $103.17 of credit, card only vaulted). */
                      const isDeferaCreditRow = p?.type === 'defera_credit';
                      /** Late-retry disambiguation. When a Stripe subscription invoice fails on
                       *  its due date and Stripe finally succeeds on a retry attempt, the paid
                       *  pill labels itself with the day the money moved (`paid_at`) — which
                       *  can visually alias with a DIFFERENT open invoice whose `period_end`
                       *  falls on the same calendar day. The customer sees "Jun 30 paid $57.03"
                       *  next to "Cycle ending Jun 30 · Past due · $57.03" and reasonably
                       *  wonders if it's the same charge shown twice. Surface the cycle this
                       *  payment actually settled underneath the date so the two rows read as
                       *  two different cycles. `period_end` is only populated for `subscription`
                       *  history entries (Stripe invoices); one-time payments and DC-NMI rows
                       *  leave it undefined and this subtitle collapses. Comparison is done in
                       *  the display's local timezone to match what's on screen — comparing UTC
                       *  ISO strings would flag on-time payments as late whenever paid_at and
                       *  period_end straddle midnight. 7KHZ82 canary: invoice 0007 (cycle
                       *  06-16→06-23) cleared on its 5th retry on 06-30, and invoice 0008
                       *  (cycle 06-23→06-30) is still failing — both rendered as Jun 30. */
                      /** Cycles settled by committed Defera Credit instead of the card.
                       *  The schedule endpoints emit these with the GROSS installment in
                       *  `amount_cents` (so the paid math and this list stay on one basis)
                       *  plus `credit_applied_cents` saying how much of it was credit.
                       *  Without the label a $151.43 pill reads as "your card was charged"
                       *  on a cycle where it never was — NQLL7E canary, where the cycle
                       *  was previously omitted from the list entirely and the progress
                       *  strip silently lost the same $151.43 from BOTH sides. */
                      const creditAppliedCents = Number(p.credit_applied_cents) || 0;
                      const isCreditSettled = creditAppliedCents > 0
                        && creditAppliedCents >= (Number(p.amount_cents) || 0);
                      const isPartlyCredit = creditAppliedCents > 0 && !isCreditSettled;
                      const paidDate = p.date ? parseISO(p.date) : null;
                      const cycleEnd = p.period_end ? parseISO(p.period_end) : null;
                      const paidDateValid = paidDate && !isNaN(paidDate.getTime());
                      const cycleEndValid = cycleEnd && !isNaN(cycleEnd.getTime());
                      const isLateRetry = !isPaidEarly && paidDateValid && cycleEndValid
                        && format(paidDate, 'yyyy-MM-dd') !== format(cycleEnd, 'yyyy-MM-dd');
                      return (
                        <div
                          key={i}
                          className="flex items-center justify-between py-2.5 px-4 rounded-full bg-brand-800/90 border border-brand-700/40 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)] hover:bg-brand-800 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <CheckCircle className="w-4 h-4 flex-shrink-0 text-white/90" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-white truncate">
                                {p.date ? format(parseISO(p.date), 'EEE, MMM d, yyyy') : '—'}
                              </div>
                              {isPaidEarly && (
                                <div className="text-[11px] text-white/65 truncate leading-snug">
                                  Covers {coversDates.map((d) => format(d, 'MMM d')).join(', ')} installment{coversDates.length > 1 ? 's' : ''}
                                </div>
                              )}
                              {isDeposit && !isPaidEarly && (
                                <div className="text-[11px] text-white/65 truncate leading-snug">
                                  Deposit paid at booking
                                </div>
                              )}
                              {isLateRetry && !isCreditSettled && (
                                <div className="text-[11px] text-white/65 truncate leading-snug">
                                  Cycle ending {format(cycleEnd, 'MMM d')}
                                </div>
                              )}
                              {isDeferaCreditRow && (
                                <div className="text-[11px] text-white/65 truncate leading-snug">
                                  Defera Credit applied at booking · No card charge
                                </div>
                              )}
                              {isCreditSettled && !isDeferaCreditRow && (
                                <div className="text-[11px] text-white/65 truncate leading-snug">
                                  {cycleEndValid ? `Cycle ending ${format(cycleEnd, 'MMM d')} · ` : ''}No card charge
                                </div>
                              )}
                              {isPartlyCredit && (
                                <div className="text-[11px] text-white/65 truncate leading-snug">
                                  Includes ${fmtCurrency(creditAppliedCents)} Defera Credit
                                </div>
                              )}
                            </div>
                            {isPaidEarly && (
                              <span className="text-[10px] uppercase tracking-wider text-white/80 font-medium ml-1">
                                Paid early
                              </span>
                            )}
                            {isDeposit && !isPaidEarly && (
                              <span className="text-[10px] uppercase tracking-wider text-white/80 font-medium ml-1">
                                Deposit
                              </span>
                            )}
                            {(isCreditSettled || isDeferaCreditRow) && !isPaidEarly && !isDeposit && (
                              <span className="text-[10px] uppercase tracking-wider text-emerald-300/90 font-medium ml-1 whitespace-nowrap">
                                Defera Credit
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-medium text-white/90 tabular-nums">
                            ${fmtCurrency(p.amount_cents || 0)}
                          </div>
                        </div>
                      );
                    }
                    if (entry.kind === 'paid_ahead') {
                      const row = entry.data;
                      const coveredByDate = row.covered_by_date ? parseISO(row.covered_by_date) : null;
                      const coveredByValid = coveredByDate && !isNaN(coveredByDate.getTime());
                      return (
                        <div
                          key={`paid-ahead-${row.date}`}
                          className="flex items-center justify-between py-2.5 px-4 rounded-full bg-brand-800/90 border border-brand-700/40 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)] hover:bg-brand-800 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <CheckCircle className="w-4 h-4 flex-shrink-0 text-white/90" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-white truncate">
                                {format(parseISO(row.date), 'EEE, MMM d, yyyy')}
                              </div>
                              {coveredByValid && (
                                <div className="text-[11px] text-white/65 truncate leading-snug">
                                  Covered by your {format(coveredByDate, 'MMM d')} payment
                                </div>
                              )}
                            </div>
                            <span className="text-[10px] uppercase tracking-wider text-white/80 font-medium ml-1">
                              Paid early
                            </span>
                          </div>
                          {/* $0.00 on purpose: the dollars live on the payment pill above
                              (the day the card was actually charged). Showing the covered
                              amount here too would double-display the same money. */}
                          <div className="text-sm font-medium text-white/90 tabular-nums">
                            $0.00
                          </div>
                        </div>
                      );
                    }
                    if (entry.kind === 'skipped') {
                      const row = entry.data;
                      return (
                      <div
                        key={`skipped-${row.date}`}
                        className="flex items-center justify-between py-2.5 px-4 rounded-full bg-brand-800/90 border border-brand-700/40 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)] hover:bg-brand-800 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <CheckCircle className="w-4 h-4 flex-shrink-0 text-white/90" />
                          <div className="text-sm font-medium text-white/80 line-through truncate">
                            {format(parseISO(row.date), 'EEE, MMM d, yyyy')}
                          </div>
                          <span className="text-[10px] uppercase tracking-wider text-white/70 font-normal ml-1">
                            Skipped
                          </span>
                        </div>
                        <div className="text-sm font-medium text-white/80 tabular-nums">
                          $0.00
                        </div>
                      </div>
                      );
                    }
                    return null;
                    })}
                    {/* Paid-ahead installments are rendered above as "Paid early" pills with
                        a "Covered by your <date> payment" sub-line — same data source
                        (`paidAheadRows`), single render. A previous version of this block
                        iterated the same array a second time with a "Paid ahead" badge,
                        which surfaced as a duplicate row per covered installment on the
                        NMI track (see WESX5W after pay-ahead). */}
                    {/* Past-due rows — invoices that already attempted (Stripe `status=open`
                        on a `past_due` subscription). These were INVISIBLE before: not in
                        `payment_history` (not paid), not in `scheduleRows` (in the past), and
                        only surfaced as a banner inside the Make-a-Payment modal. Without a
                        row on the main schedule, customers had no way to know a charge was
                        sitting unpaid until they happened to open Make a Payment — Stripe
                        would meanwhile burn through retry attempts before voiding. Each row
                        opens the existing modal so the proven `/invoices/{id}/pay` path
                        (which clears the sub's `past_due` state in one shot) handles the
                        actual payment instead of duplicating the flow here.
                        Skipped entirely on terminated trips: once the sub is canceled,
                        Stripe rejects /pay against any of its open invoices, and showing a
                        red "Pay now" row that fails would only confuse the customer.
                        Also skipped when the plan is paid in full — any open invoice past
                        that point is a zombie beyond the total the customer agreed to
                        (TGTMPM canary: 5-cycle plan fully paid $661.58/$661.58, Stripe
                        left a 6th $96.88 open invoice from a mis-aligned cancel_at). The
                        server suppresses these in the response too; this is a defense-in-
                        depth filter so a stale schedule payload can't produce a "PAY NOW"
                        button next to the "PAID IN FULL" banner and double-charge the
                        customer. */}
                    {!isTripTerminated && !isPaidInFull && openInvoices.map((inv, invIdx) => {
                      /** Title prefers the billing cycle the invoice covers (`period_end`) over
                       *  Stripe's `due_date`, because `due_date` is ALWAYS null for our
                       *  `charge_automatically` subs (Stripe only sets it for `send_invoice`
                       *  collection). The previous render fell back to `inv.created` on the server,
                       *  which collided with same-day paid rows (e.g. PXYEMK: invoice 0004 paid
                       *  May 30 evening alongside invoice 0005 created May 30 still failing — two
                       *  "Sat May 30" rows that looked like one charge was paid AND past-due).
                       *  "Cycle ending May 30" lines up with the period the customer is actually
                       *  delinquent on, and `next_payment_attempt` tells them when Stripe retries. */
                      /** NMI rows come from defera-capital-nmi-schedule as
                       *  { due_date, amount_cents, status } — none of the Stripe fields
                       *  below exist on them. 1OSUWO rendered "Past due  $0.00" under a
                       *  banner reading "$689.81 hasn't gone through", because the only
                       *  amount field this row knew was amount_remaining_cents. Fall back
                       *  field-by-field so one row shape serves both rails. */
                      const periodEnd = inv.period_end ? parseISO(inv.period_end) : (inv.due_date ? parseISO(inv.due_date) : null);
                      const nextAttempt = inv.next_payment_attempt ? parseISO(inv.next_payment_attempt) : null;
                      const attempts = Number(inv.attempt_count) || 0;
                      const invAmountCents = Number(inv.amount_remaining_cents ?? inv.amount_cents) || 0;
                      return (
                        <div
                          key={inv.id ?? `pastdue-${inv.due_date ?? inv.period_end ?? 'row'}-${invIdx}`}
                          className="flex flex-wrap items-center justify-between gap-2 py-2.5 px-4 rounded-2xl bg-rose-500/[0.07] border border-rose-500/30"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 text-rose-300" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-rose-100 truncate">
                                {periodEnd ? (inv.period_end ? `Cycle ending ${format(periodEnd, 'MMM d, yyyy')}` : `Due ${format(periodEnd, 'MMM d, yyyy')}`) : 'Past due'}
                                <span className="ml-2 text-[10px] uppercase tracking-wider text-rose-300/90 font-medium">Past due</span>
                              </div>
                              {(attempts > 0 || nextAttempt || inv.invoice_number) && (
                                <div className="text-[11px] text-rose-200/70 mt-0.5">
                                  {attempts > 0 && (attempts === 1 ? '1 failed attempt' : `${attempts} failed attempts`)}
                                  {nextAttempt && `${attempts > 0 ? ' · ' : ''}Next retry ${format(nextAttempt, 'MMM d')}`}
                                  {inv.invoice_number && `${attempts > 0 || nextAttempt ? ' · ' : ''}${inv.invoice_number}`}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-medium text-rose-100 tabular-nums">
                              ${fmtCurrency(invAmountCents)}
                            </div>
                            {tripId && showActions && (
                              <Button
                                size="sm"
                                onClick={() => setShowDeferaZeroMakePayment(true)}
                                className="h-7 px-3 text-[11px] font-medium bg-rose-500 hover:bg-rose-400 text-white border border-rose-400/40"
                              >
                                Pay now
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Plain-language disclosure when past-due exists — explains WHY the
                      progress strip didn't move on the attempted date and what happens
                      next. Without this, a customer sees "Paid 1/4 · $237.29 / $380.77"
                      with a rose row dated in the past and no context. Suppressed on
                      terminated trips since the past-due rows themselves are also
                      suppressed — leaving this banner alone would dangle. Same reason
                      for suppressing when the plan is paid in full: the past-due rows
                      above are zombies filtered out by the defense-in-depth check, so
                      the "Stripe will retry automatically" warning would misinform a
                      customer whose card should NOT be charged again. */}
                  {pastDueCount > 0 && !isTripTerminated && !isPaidInFull && (
                    <p className="mt-3 px-1 text-[11px] text-rose-200/70 leading-snug">
                      {(() => {
                        const amt = `$${fmtCurrency(pastDueTotalCents)}`;
                        const lead = pastDueCount === 1
                          ? `A scheduled charge of ${amt} hasn't gone through yet — your card was declined on the attempt above.`
                          : `${pastDueCount} scheduled charges totaling ${amt} haven't gone through yet — your card was declined on the attempts above.`;
                        /** What happens next depends on the rail and the plan state.
                         *  NMI paused (retries exhausted): nothing retries on its own —
                         *  "Stripe will retry automatically" was simply false here, and
                         *  the customer had no idea Restart Plan was the way back. */
                        if (isDeferaCapitalNmi && isPlanRestartable) return `${lead} After several tries your plan was paused, so it won't retry on its own. Use Restart Plan below to catch up and resume your schedule, or pay part of it now — with the card on file or a different card.`;
                        if (isDeferaCapitalNmi) return `${lead} Tap “Pay now” to settle it; otherwise we'll retry on the next scheduled attempt.`;
                        return `${lead} Tap “Pay now” to settle ${pastDueCount === 1 ? 'it' : 'them'}; otherwise Stripe will retry automatically.`;
                      })()}
                    </p>
                  )}
                </div>
              );
            })()}
            {/* Defera Zero: Payment success - show original vs new schedule */}
            {isDeferaStripe && paymentSuccessComparison && (
              <div className="mb-4 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.07] p-4">
                <div className="flex items-start gap-3 mb-4">
                  <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/15 ring-1 ring-inset ring-emerald-500/30">
                    <Check className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-emerald-50 leading-tight">
                      Payment successful
                    </p>
                    <p className="mt-1 text-[12px] sm:text-[13px] text-emerald-200/75 leading-snug">
                      Here's your updated schedule.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-white/[0.04] bg-white/[0.015] p-3">
                    <p className="text-white/25 text-[10px] uppercase tracking-wider mb-2">Original</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                      {(paymentSuccessComparison.before?.scheduleRows?.length
                        ? paymentSuccessComparison.before.scheduleRows
                        : (paymentSuccessComparison.before?.schedule || []).map((dateStr) => ({
                            date: dateStr,
                            amount_cents: Math.round((paymentSuccessComparison.before?.paymentAmount || 0) * 100),
                          }))
                      ).map((row, i) => (
                        <div key={i} className="flex justify-between text-[11px]">
                          <span className="text-white/20">
                            {row?.date ? formatScheduleDate(row.date, 'MMM d') : '—'}
                          </span>
                          <span className="text-white/30">${fmtCurrency(row?.amount_cents || 0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-lg border border-emerald-500/15 bg-emerald-500/[0.03] p-3">
                    <p className="text-emerald-400/50 text-[10px] uppercase tracking-wider mb-2">New</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                      {(paymentSuccessComparison.after?.dates || []).map((d, i) => (
                        <div key={i} className="flex justify-between text-[11px]">
                          <span className="text-emerald-400/30">{d?.date ? formatScheduleDate(d.date, 'MMM d') : '—'}</span>
                          <span className="text-emerald-400/50">${fmtCurrency(d?.amount_cents || 0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setPaymentSuccessComparison(null); onDeferaZeroPaymentSuccessHandled?.(); }}
                  className="mt-3 text-white/80 hover:text-white font-medium text-xs"
                >
                  Dismiss
                </Button>
              </div>
            )}
            {hasExtensionRequest ? (
              // Show side-by-side comparison when extension is requested
              <div className={cn("grid grid-cols-2 gap-2", fillHeight && "md:flex-1 md:min-h-0")}>
                {/* Original Schedule */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex flex-col min-h-0">
                  <p className="text-[10px] text-white/60 mb-2 font-normal">Original</p>
                  <div className={cn("space-y-1 overflow-y-auto", fillHeight ? "md:flex-1 md:min-h-0" : "max-h-64")}>
                    {(effectiveDisplayPlan?.schedule || []).map((dateStr) => {
                      const date = parseISO(dateStr);
                      return (
                        <div
                          key={dateStr}
                          className="flex items-center justify-between py-1.5 px-2 rounded bg-white/5"
                        >
                          <div className="flex items-center gap-1 min-w-0">
                            <DollarSign className="w-2.5 h-2.5 flex-shrink-0 text-white/40" />
                            <div className="text-[10px] font-medium text-white whitespace-nowrap truncate">
                              {format(date, 'MMM d, yyyy')}
                            </div>
                          </div>
                          <div className="text-[10px] font-medium text-white whitespace-nowrap ml-1">
                            ${effectiveDisplayPlan?.paymentAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* New Schedule */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex flex-col min-h-0">
                  <p className="text-[10px] text-white/60 mb-2 font-normal">New</p>
                  <div className={cn("space-y-1 overflow-y-auto", fillHeight ? "md:flex-1 md:min-h-0" : "max-h-64")}>
                    {(effectiveDisplayPlan?.schedule || []).map((dateStr) => {
                      const originalDate = parseISO(dateStr);
                      const newDate = addWeeks(originalDate, extension_request.extension_weeks);
                      return (
                        <div
                          key={dateStr}
                          className="flex items-center justify-between py-1.5 px-2 rounded bg-white/5"
                        >
                          <div className="flex items-center gap-1 min-w-0">
                            <DollarSign className="w-2.5 h-2.5 flex-shrink-0 text-white/40" />
                            <div className="text-[10px] font-medium text-white whitespace-nowrap truncate">
                              {format(newDate, 'MMM d, yyyy')}
                            </div>
                          </div>
                          <div className="text-[10px] font-medium text-white whitespace-nowrap ml-1">
                            ${effectiveDisplayPlan?.paymentAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : needsStripeSchedule && !stripeScheduleReady && !dzScheduleError ? (
              <div className="flex flex-col items-center justify-center gap-3 py-10 text-white/50">
                <Loader2 className="w-8 h-8 animate-spin text-white/30" />
                <p className="text-sm font-normal">Loading payment dates…</p>
              </div>
            ) : isPaidInFull ? (
              /** Plan complete — every installment cleared. Stripe naturally cancels
               *  the sub at `cancel_at` after the final invoice succeeds, which means
               *  `subscription_status` will read `canceled` even though the customer
               *  succeeded. Showing "Cancelled" here would lie about a fully-paid trip
               *  (the exact bug that prompted this fix: see
               *  stripe-webhook/defera-capital.ts → handleDeferaCapitalSubscriptionDeleted).
               *  Past-payment history above still shows every charge so the customer
               *  can audit the trip. */
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.05] px-4 py-5 text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/[0.12] px-3 py-1 mb-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                  <span className="text-[11px] uppercase tracking-widest text-emerald-200 font-medium">
                    Paid in Full
                  </span>
                </div>
                <p className="text-sm text-emerald-100/80 leading-snug max-w-sm mx-auto">
                  Every installment for this trip has cleared. No further payments will be charged.
                </p>
              </div>
            ) : isPlanRestartable ? (
              /** Plan was paused by Stripe after exhausting retries on a failing
               *  card. Instead of the dead-end "Cancelled" banner, show what
               *  the customer can DO to recover: pay the past-due to bring the
               *  account current, and we'll resume their installment schedule
               *  at the original cadence/amount. The schedule endpoint already
               *  computed past_due + new schedule via the shared
               *  `_shared/defera-capital-restart-plan.ts` helper — render
               *  exactly those numbers so backend and frontend agree. */
              <div className="rounded-2xl border border-brand-500/20 bg-brand-500/[0.05] px-4 py-5">
                <div className="flex items-center justify-center gap-2 rounded-full bg-brand-500/[0.12] px-3 py-1 mb-3 mx-auto w-fit">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-300" />
                  <span className="text-[11px] uppercase tracking-widest text-brand-200 font-medium">
                    Plan Paused
                  </span>
                </div>
                <p className="text-sm text-brand-100/85 leading-snug max-w-md mx-auto text-center">
                  We couldn't charge your card after several tries, so your installment plan was paused. Pay the past-due amount below to bring your account current and we'll resume your schedule.
                  {(isDeferaCapitalNmi || isStripePastDuePartial) ? ' Can\u2019t cover all of it today? Pay part of it now, with the card on file or a different card, and the rest stays past due until it\u2019s cleared.' : ''}
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3 max-w-md mx-auto">
                  <div className="rounded-xl border border-brand-500/15 bg-black/30 px-3 py-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-brand-200/70 font-medium mb-1">Pay now</div>
                    <div className="text-lg text-white font-light">${fmtCurrency(planRestart.past_due_cents || 0)}</div>
                    <div className="text-[10px] text-white/40 mt-1">Past-due catch-up</div>
                  </div>
                  <div className="rounded-xl border border-white/[0.08] bg-black/30 px-3 py-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/55 font-normal mb-1">Then</div>
                    <div className="text-lg text-white font-light">
                      {planRestart.new_num_payments}× ${fmtCurrency(planRestart.new_per_payment_cents || 0)}
                    </div>
                    <div className="text-[10px] text-white/40 mt-1">Resumed schedule</div>
                  </div>
                </div>
                {/** The paused-plan card REPLACES the upcoming timeline, so without this
                  *  list the customer sees "4× $752.98" and nothing else — 1OSUWO's owner
                  *  could not tell whether two payments had failed, whether everything was
                  *  now due at once, or whether one payment was left. Show the actual
                  *  dated rows the restart will resume, and mark the ones already past
                  *  due. NMI only: Stripe's restart re-cuts its own schedule server-side. */}
                {isDeferaCapitalNmi && (() => {
                  const rows = (effectiveDisplayPlan?.scheduleRows || [])
                    .filter((r) => ['pending', 'retrying', 'declined', 'error', 'delinquent'].includes(r._nmi_status))
                    .sort((x, y) => new Date(x.date) - new Date(y.date));
                  if (!rows.length) return null;
                  return (
                    <div className="mt-4 max-w-md mx-auto space-y-1.5">
                      <div className="text-[10px] uppercase tracking-widest text-white/45 font-medium px-1">What resumes</div>
                      {rows.map((r) => {
                        const pastDue = ['retrying', 'declined', 'error', 'delinquent'].includes(r._nmi_status);
                        return (
                          <div key={`restart-row-${r.date}`} className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm ${pastDue ? 'bg-rose-500/10 border border-rose-500/20' : 'bg-white/[0.04]'}`}>
                            <span className={pastDue ? 'text-rose-100' : 'text-white/80'}>
                              {r.date ? format(parseISO(r.date), 'EEE, MMM d, yyyy') : '—'}
                              {pastDue && <span className="ml-2 text-[10px] uppercase tracking-wider text-rose-300/90 font-medium">Past due</span>}
                            </span>
                            <span className={`tabular-nums font-medium ${pastDue ? 'text-rose-100' : 'text-white'}`}>${fmtCurrency(r.amount_cents)}</span>
                          </div>
                        );
                      })}
                      <p className="text-[11px] text-white/45 px-1 pt-1">Paying the past-due amount resumes the remaining dates as scheduled.</p>
                    </div>
                  );
                })()}
                {showActions && tripId && (
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    <Button
                      type="button"
                      onClick={() => setShowRestartPlanModal(true)}
                      className={PRIMARY_ACTION_BTN_CLASS + ' px-4'}
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                      Restart Plan
                    </Button>
                    {/** NMI only: the make-a-payment form's "Past due" mode takes any amount
                      *  ≥ $5 toward the past-due rows (card on file or a different card) and
                      *  the server resumes the plan once nothing is past due. Stripe's form
                      *  has no such mode, so the button would dead-end there. */}
                    {(isDeferaCapitalNmi || isStripePastDuePartial) && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setNmiPendingSettlementRow(null);
                          /** Stripe: the paused plan's dead subscription has no open invoices to
                           *  allocate to; Principal mode already accepts any amount here. */
                          if (isStripePastDuePartial) setDzMakePaymentInitialMode('custom');
                          setShowDeferaZeroMakePayment(true);
                        }}
                        className="h-9 px-4 rounded-full text-xs font-medium border border-white/15 bg-white/[0.04] text-white/85 hover:bg-white/[0.08] hover:text-white"
                      >
                        <DollarSign className="w-3.5 h-3.5 mr-1.5" />
                        Pay part of it
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ) : isCompletionSettlement ? (
              /** Installment plan ran to natural completion (every planned
               *  cycle fired, card was fine, cancel_at hit cleanly), but a
               *  small reconciliation balance < 1 unit_amount remains
               *  because of a pricing/cycle math gap at booking time.
               *  Using "Plan Paused — we couldn't charge your card" copy
               *  here would lie — nothing failed. The plan COMPLETED.
               *  Honest framing: "Installment Plan Completed — Final
               *  Balance." Same Make-a-Payment action under the hood. */
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.05] px-4 py-5">
                <div className="flex items-center justify-center gap-2 rounded-full bg-emerald-500/[0.12] px-3 py-1 mb-3 mx-auto w-fit">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                  <span className="text-[11px] uppercase tracking-widest text-emerald-200 font-medium">
                    Installment Plan Completed
                  </span>
                </div>
                <p className="text-sm text-emerald-100/85 leading-snug max-w-md mx-auto text-center">
                  Every scheduled installment fired successfully. A small reconciliation balance remains to fully settle your booking.
                </p>
                <div className="mt-4 rounded-xl border border-emerald-500/15 bg-black/30 px-3 py-3 text-center max-w-xs mx-auto">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-200/70 font-medium mb-1">Final balance</div>
                  <div className="text-lg text-white font-light">${fmtCurrency(planRestart.payoff_cents || remainingCents || 0)}</div>
                  {planRestart.due_by && (
                    <div className="text-[11px] text-emerald-200/70 font-normal mt-1">
                      Due by {format(parseISO(planRestart.due_by), 'MMM d, yyyy')}
                    </div>
                  )}
                </div>
                {showActions && tripId && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      type="button"
                      onClick={() => setShowDeferaZeroMakePayment(true)}
                      className={PRIMARY_ACTION_BTN_CLASS + ' px-4'}
                    >
                      <DollarSign className="w-3.5 h-3.5 mr-1.5" />
                      Pay Final Balance
                    </Button>
                  </div>
                )}
              </div>
            ) : isPlanPayoffRequired ? (
              /** Plan was paused by Stripe AND departure is too close to fit
               *  even one more installment cycle. We can't restart on a
               *  schedule, but the booking is still active — surface the
               *  pay-off path (Make a Payment, preselected at the full
               *  remaining balance) instead of the dead-end "Cancelled"
               *  banner. The schedule endpoint computed `payoff_cents` in
               *  the same call that detected the payment_failed cancellation,
               *  so this never lies about the amount owed. */
              <div className="rounded-2xl border border-brand-500/20 bg-brand-500/[0.05] px-4 py-5">
                <div className="flex items-center justify-center gap-2 rounded-full bg-brand-500/[0.12] px-3 py-1 mb-3 mx-auto w-fit">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-300" />
                  <span className="text-[11px] uppercase tracking-widest text-brand-200 font-medium">
                    Plan Paused
                  </span>
                </div>
                <p className="text-sm text-brand-100/85 leading-snug max-w-md mx-auto text-center">
                  {(planRestart.cancel_reason || planRestart.reason) === 'payment_failed'
                    ? "We couldn't charge your card after several tries, and your trip is now too close to departure to resume installments. Pay the outstanding balance to keep your booking."
                    : "Your installment plan has ended, but a final balance remains on your booking. Pay it before departure to keep your booking confirmed."}
                </p>
                <div className="mt-4 rounded-xl border border-brand-500/15 bg-black/30 px-3 py-3 text-center max-w-xs mx-auto">
                  <div className="text-[10px] uppercase tracking-widest text-brand-200/70 font-medium mb-1">Outstanding balance</div>
                  <div className="text-lg text-white font-light">${fmtCurrency(planRestart.payoff_cents || remainingCents || 0)}</div>
                  {planRestart.due_by && (
                    <div className="text-[11px] text-brand-200/70 font-normal mt-1">
                      Due by {format(parseISO(planRestart.due_by), 'MMM d, yyyy')}
                    </div>
                  )}
                </div>
                {showActions && tripId && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      type="button"
                      onClick={() => setShowDeferaZeroMakePayment(true)}
                      className={PRIMARY_ACTION_BTN_CLASS + ' px-4'}
                    >
                      <DollarSign className="w-3.5 h-3.5 mr-1.5" />
                      Pay Outstanding Balance
                    </Button>
                  </div>
                )}
              </div>
            ) : isTripTerminated ? (
              /** Terminal-state replacement for the upcoming list. Showing dollar-amount
               *  rows for dates that will never be charged (Stripe sub canceled, booking
               *  cancelled or defaulted) is misleading — see NPH9N0, where a defaulted
               *  trip with `sub.status='canceled'` and `ended_at` set was still rendering
               *  $336.42 "Next" + "Final Balance" rows. The customer thought their card
               *  would still be charged. Replace the entire list with a single
               *  status pill + plain-language note. Paid history above stays as-is so
               *  the customer can see what they actually paid before things ended. */
              <div className="rounded-2xl border border-white/[0.08] bg-white/[0.025] px-4 py-5 text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/[0.06] px-3 py-1 mb-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-white/40" />
                  <span className="text-[11px] uppercase tracking-widest text-white/65 font-normal">
                    {terminatedReasonLabel}
                  </span>
                </div>
                <p className="text-sm text-white/65 leading-snug max-w-sm mx-auto">
                  {tripStatusLower === 'defaulted'
                    ? "This trip was marked defaulted, so the installment plan has been ended. No further payments will be charged."
                    : "This trip was cancelled, so the installment plan has been ended. No further payments will be charged."}
                </p>
              </div>
            ) : (
              // Continuous upcoming-payments timeline. The first chargeable row is the
              // "next" charge — gets a subtle "Next" label so the customer immediately
              // sees what's about to come out of their account, without a separate
              // sub-section or accent styling that diverges from future rows.
              (() => {
                const allRows = effectiveDisplayPlan?.scheduleRows
                  ? effectiveDisplayPlan.scheduleRows
                  : (effectiveDisplayPlan?.schedule || []).map((d) => ({ date: d, amount_cents: 0, paid_ahead: false, fee_skipped: false, is_drift_c_prorated: false, is_auto_correction: false }));
                /** NMI retry/decline rows that mirror Stripe's `past_due` invoices.
                 *  `retrying` = NMI cron will keep trying (next attempt on `due_date`,
                 *  `_nmi_retry_count` failures so far). `declined` / `error` / `delinquent`
                 *  = retries exhausted, awaiting customer action. Both render as rose
                 *  past-due cards above the upcoming list (one-for-one with the Stripe
                 *  branch above) and are removed from the regular timeline so the
                 *  "NEXT" pill on a row that's actually a 2nd retry no longer reads as
                 *  the first attempt of a fresh cycle. */
                const NMI_PAST_DUE_STATUSES = ['retrying', 'declined', 'error', 'delinquent'];
                const nmiPastDueRows = isDeferaCapitalNmi
                  ? allRows.filter((r) => NMI_PAST_DUE_STATUSES.includes(r._nmi_status))
                  : [];

                /** Fee-skipped AND paid-ahead rows render up in the past-payments group
                 *  (solid blue pills next to paid installments) — both are settled, nothing
                 *  will ever be charged on those dates, so they read as "handled" history.
                 *  Exclude them from the upcoming timeline so they don't appear twice. */
                const rows = allRows.filter((r) => {
                  if (r.fee_skipped || r.paid_ahead) return false;
                  if (isDeferaCapitalNmi && (r.is_paid || r._nmi_status === 'success')) return false;
                  /** Hoist NMI past-due rows out of the chronological timeline; they
                   *  render as their own rose section above so the "NEXT" pill on the
                   *  next clean upcoming row stays meaningful. */
                  if (isDeferaCapitalNmi && NMI_PAST_DUE_STATUSES.includes(r._nmi_status)) return false;
                  /** Rows retired by a schedule consolidation are history, not upcoming
                   *  charges. The schedule endpoint no longer emits them, but a
                   *  not-yet-redeployed endpoint still does — without this guard GOO3P2
                   *  rendered its real $159.76 NEXT row above two cancelled rows
                   *  ($213.84 of phantom "upcoming", one of them past-dated, because
                   *  display order is installment_number, not date). */
                  if (isDeferaCapitalNmi && r._nmi_status === 'cancelled') return false;
                  return true;
                });
                /** Empty schedule + outstanding balance: Stripe is done firing invoices
                 *  (`current_period_end > cancel_at`) but the booking still owes money — e.g.
                 *  VN1FET's $17.90 left over after a late-cycle custom payment. We get here
                 *  AFTER the terminal-state / paid-in-full / plan-restart branches above
                 *  have all been ruled out, so the only meaningful next action is for the
                 *  customer to settle the balance via Make-a-Payment. Without this banner
                 *  the list area renders blank under the progress strip and the customer
                 *  has no idea why nothing is scheduled.
                 *
                 *  Netted against past-due: since the schedule endpoints stopped splitting
                 *  past-due dollars across the future cycles, a trip whose ENTIRE remaining
                 *  balance is past-due legitimately has zero upcoming rows. That balance is
                 *  already on screen as "Pay now" cards above, and Stripe is still retrying
                 *  it — so "your plan finished firing, nothing is scheduled" would be flatly
                 *  wrong. Only the genuinely unscheduled remainder gets this banner. */
                const unscheduledBalanceCents = Math.max(
                  0,
                  remainingCents - (Number(dzScheduleData?.open_invoices_total_cents) || 0),
                );
                if (isDeferaStripe && rows.length === 0 && unscheduledBalanceCents > 0) {
                  return (
                    <div className="rounded-2xl border border-brand-800/25 bg-brand-900/20 px-4 py-5 text-center">
                      <div className="inline-flex items-center gap-2 rounded-full bg-brand-900/30 px-3 py-1 mb-3">
                        <span className="h-1.5 w-1.5 rounded-full bg-brand-400" />
                        <span className="text-[11px] uppercase tracking-widest text-brand-400 font-normal">
                          Balance Remaining
                        </span>
                      </div>
                      <p className="text-sm text-white/85 leading-snug max-w-sm mx-auto">
                        Your installment plan finished firing — there&rsquo;s ${fmtCurrency(unscheduledBalanceCents)} left on your trip and no scheduled charges. Use Make a Payment to settle it before departure.
                      </p>
                    </div>
                  );
                }
                /** "Next" should fire on the first real chargeable row — skip
                 *  auto-correction rows (presentation-only; they fire same-day as their
                 *  sibling prorated row, not on their own date).
                 *  Gated on `isDeferaStripe`: only Defera Capital and Defera Zero trips
                 *  run on a Stripe-driven installment plan where "Next" reliably maps to
                 *  the next auto-charge. Non-Defera trips (Reserve, etc.) get a plain
                 *  monochrome list — no blue accent ring and no "Next" pill. */
                const firstChargeIdx = isDeferaStripe
                  ? rows.findIndex((r) => !r.is_auto_correction)
                  : -1;
                /** Drift C disclosure: when the final upcoming row is a prorated cycle truncated
                 *  by cancel_at, we render an inline note above the affected pair so the
                 *  customer understands why two transactions hit on the same date. */
                const driftCDisclosure = dzScheduleData?.drift_c_disclosure || null;
                /** Credit on file = Stripe account credit (negative customer.balance,
                 *  e.g. goodwill granted in the Stripe dashboard) + committed Defera
                 *  Credit the invoice webhook will net off future cycles
                 *  (`defera_credit_pending_cents`, from the Apply Credits flow /
                 *  checkout). The schedule endpoint already distributed both across
                 *  the rows via `credit_applied_cents`; this banner explains why the
                 *  next payment(s) show a reduced amount. NMI trips never populate
                 *  either field — their rows are rewritten to net amounts directly. */
                const customerCreditCents = Number(dzScheduleData?.customer_credit_cents) || 0;
                const deferaCreditPendingCents = Number(dzScheduleData?.defera_credit_pending_cents) || 0;
                const creditOnFileCents = customerCreditCents + deferaCreditPendingCents;
                const creditedRowCount = creditOnFileCents > 0
                  ? rows.filter((r) => (Number(r.credit_applied_cents) || 0) > 0).length
                  : 0;
                return (
                  /** No max-h cap by design: trips with many remaining installments
                   *  should show every row (matches the past-payments list which we
                   *  also uncapped). `fillHeight` keeps the legacy flex layout (desktop
                   *  only, see the CardContent note) so the list grows inside a sized
                   *  parent container instead of overflowing. */
                  <div className={cn("space-y-2", fillHeight && "md:flex-1 md:min-h-0 md:overflow-y-auto")}>
                    {creditOnFileCents > 0 && (
                      <div className="flex items-start gap-3 rounded-xl bg-emerald-500/[0.07] border border-emerald-500/25 px-3.5 py-3 sm:px-4">
                        <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/15 ring-1 ring-inset ring-emerald-500/30">
                          <Check className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2.75} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-emerald-50 tabular-nums leading-tight">
                            ${fmtCurrency(creditOnFileCents)} credit {isDeferaCapitalNmi ? 'applied to this plan' : 'on file'}
                          </p>
                          <p className="mt-1 text-[12px] sm:text-[13px] text-emerald-200/75 leading-snug">
                            {/* NMI reports credit that was already netted into the plan when
                                it was committed (rows below are already reduced), so "next
                                payment" phrasing would misread as a future reduction. Stripe
                                credit nets upcoming invoices at finalization, so there the
                                forward-looking copy is accurate. */}
                            {isDeferaCapitalNmi
                              ? 'Already reflected in this plan — the amounts below are what your card will be charged.'
                              : creditedRowCount > 1
                                ? `Applied to your next ${creditedRowCount} payments. Amounts below are what your card will be charged.`
                                : 'Applied to your next payment. Amounts below are what your card will be charged.'}
                          </p>
                        </div>
                      </div>
                    )}
                    {/* NMI past-due / retry-pending rows. Pixel-for-pixel parity with
                        the Stripe `open_invoices` rose cards rendered higher up the tree:
                        same `AlertCircle` icon, same `bg-rose-500/[0.07] border-rose-500/30`
                        shell, identical "Cycle ending MMM d, yyyy · Past due" title row,
                        same subtitle pattern ("1 failed attempt · Next retry MMM d · {decline
                        text}"), same right-side amount + "Pay now" button. Followed by the
                        same plain-language disclosure paragraph Stripe shows — copy adapted
                        for NMI (cron-driven retries instead of Stripe-driven). Suppressed on
                        terminated trips, matching Stripe.

                        Date semantics — we deliberately use the row's CURRENT `date`
                        (= next attempt for retrying rows, last attempt for terminal
                        states) as the "cycle ending" stamp because the original due date
                        is overwritten in place by the retry engine. The customer's mental
                        model ("the charge dated MMM d hasn't gone through") works either
                        way because the date on the row IS the date associated with the
                        current attempt window. */}
                    {!isTripTerminated && isDeferaCapitalNmi && nmiPastDueRows.map((row) => {
                      const cycleEnd = row.date ? parseISO(row.date) : null;
                      const isRetrying = row._nmi_status === 'retrying';
                      const nextAttempt = isRetrying && row.date ? parseISO(row.date) : null;
                      const attempts = Number(row._nmi_retry_count) || 0;
                      const installmentN = Number(row._nmi_installment_number) || null;
                      const declineText = (row._nmi_gateway_response_text || '').trim();
                      const installmentLabel = installmentN ? `Installment ${installmentN}` : null;
                      const hasSubtitle = attempts > 0 || nextAttempt || declineText || installmentLabel;
                      return (
                        <div
                          key={`nmi-pastdue-${installmentN ?? row.date}`}
                          className="flex flex-wrap items-center justify-between gap-2 py-2.5 px-4 rounded-2xl bg-rose-500/[0.07] border border-rose-500/30"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 text-rose-300" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-rose-100 truncate">
                                {cycleEnd ? `Cycle ending ${format(cycleEnd, 'MMM d, yyyy')}` : 'Past due'}
                                <span className="ml-2 text-[10px] uppercase tracking-wider text-rose-300/90 font-medium">Past due</span>
                              </div>
                              {hasSubtitle && (
                                <div className="text-[11px] text-rose-200/70 mt-0.5">
                                  {installmentLabel}
                                  {attempts > 0 && `${installmentLabel ? ' · ' : ''}${attempts === 1 ? '1 failed attempt' : `${attempts} failed attempts`}`}
                                  {nextAttempt && `${installmentLabel || attempts > 0 ? ' · ' : ''}Next retry ${format(nextAttempt, 'MMM d')}`}
                                  {declineText && `${installmentLabel || attempts > 0 || nextAttempt ? ' · ' : ''}${declineText}`}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-medium text-rose-100 tabular-nums">
                              ${fmtCurrency(row.amount_cents || 0)}
                            </div>
                            {tripId && showActions && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  /** Route by status, with a guard for the "mixed" case where the
                                   *  trip has BOTH a retrying row and a terminal past-due row at the
                                   *  same time. The terminal rows live in `dzScheduleData.open_invoices`
                                   *  → the Make-a-Payment modal would compute `hasPastDue=true` and
                                   *  lock every tab (server enforces `past_due_blocks_payment` anyway),
                                   *  leaving the customer staring at a useless screen. Route those to
                                   *  Restart Plan instead — the only path that can clear past-due on
                                   *  NMI (Stripe equivalent: `/invoices/{id}/pay`).
                                   *
                                   *    - retrying + no other past-due → Make a Payment modal with a
                                   *      focal "Past due — Installment N · $X" banner that has a
                                   *      one-click Pay button (the Stripe-parity pattern). Tabs stay
                                   *      visible below for ad-hoc payments but are not the focal CTA.
                                   *    - retrying + other past-due rows exist → Restart Plan, because
                                   *      the customer must clear those first anyway.
                                   *    - declined / error / delinquent → Restart Plan. */
                                  const hasTerminalPastDue = (dzScheduleData?.open_invoices || []).length > 0
                                    && (Number(dzScheduleData?.open_invoices_total_cents) || 0) > 0;
                                  if (row._nmi_status === 'retrying' && !hasTerminalPastDue) {
                                    setNmiPendingSettlementRow({
                                      amount_cents: Number(row.amount_cents) || 0,
                                      installment_number: row._nmi_installment_number || null,
                                      attempted_at: row._nmi_attempted_at || null,
                                      gateway_response_text: row._nmi_gateway_response_text || null,
                                    });
                                    setShowDeferaZeroMakePayment(true);
                                  } else {
                                    setShowRestartPlanModal(true);
                                  }
                                }}
                                className="h-7 px-3 text-[11px] font-medium bg-rose-500 hover:bg-rose-400 text-white border border-rose-400/40"
                              >
                                Pay now
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* Plain-language disclosure under the NMI past-due block — same role as
                        the Stripe past-due banner higher in the tree (see line ~3429). Without
                        this paragraph the rose row reads like a styling choice; with it, the
                        customer/admin understands WHY the progress strip didn't move and what
                        happens next (cron will keep retrying, or they can settle now). */}
                    {!isTripTerminated && isDeferaCapitalNmi && nmiPastDueRows.length > 0 && (() => {
                      const pastDueTotal = nmiPastDueRows.reduce((sum, r) => sum + (Number(r.amount_cents) || 0), 0);
                      const hasRetrying = nmiPastDueRows.some((r) => r._nmi_status === 'retrying');
                      const tailCopy = hasRetrying
                        ? "otherwise we'll retry automatically on the next attempt date above."
                        : "we're no longer retrying automatically — settle now to resume the schedule.";
                      return (
                        <p className="mt-3 px-1 text-[11px] text-rose-200/70 leading-snug">
                          {nmiPastDueRows.length === 1
                            ? `A scheduled charge of $${fmtCurrency(pastDueTotal)} hasn't gone through yet — your card was declined on the attempt above. Tap “Pay now” to settle it; ${tailCopy}`
                            : `${nmiPastDueRows.length} scheduled charges totaling $${fmtCurrency(pastDueTotal)} haven't gone through yet — your card was declined on the attempts above. Tap “Pay now” to settle them; ${tailCopy}`}
                        </p>
                      );
                    })()}
                    {rows.map((row, index) => {
                      const date = parseISO(row.date);
                      const isNext = !row.is_auto_correction && index === firstChargeIdx;
                      const isProrated = Boolean(row.is_drift_c_prorated);
                      const isCorrection = Boolean(row.is_auto_correction);
                      /** Per-row amount: trust the row's `amount_cents` whenever the
                       *  server set it (>0), because the schedule endpoint caps the
                       *  FINAL row at `remaining − totalSoFar`
                       *  (see `buildLockedScheduleFromStripe`). For middle rows this is
                       *  identical to the locked unit_amount, so the display is unchanged.
                       *  For the last row when `remaining < unit_amount` — the VN1FET
                       *  case where the customer over-paid an earlier cycle so only $17.90
                       *  is left vs. a $99.54 unit_amount — the row's `amount_cents`
                       *  reflects the actual finish-the-plan amount, while the locked
                       *  unit_amount would mislead the customer into thinking we're about
                       *  to charge more than their entire remaining balance. Prorated and
                       *  auto-correction rows also flow through this branch (their
                       *  `amount_cents` is the Stripe-truth prorated / correction amount).
                       *  Only fall back to `paymentAmount` for legacy non-Stripe rows
                       *  synthesised at line 2799 where `amount_cents` is 0 by construction. */
                      const rowCents = Number(row.amount_cents);
                      const hasRowAmount = Number.isFinite(rowCents) && rowCents > 0;
                      /** Stripe account credit applied to this row at invoice finalization:
                       *  the card is only charged `amount_cents − credit_applied_cents`, so
                       *  show the net amount (with the gross struck through) to stay faithful
                       *  to what will actually hit the customer's statement. */
                      const creditAppliedCents = Number(row.credit_applied_cents) || 0;
                      const hasCredit = hasRowAmount && creditAppliedCents > 0;
                      const netRowCents = hasCredit ? Math.max(0, rowCents - creditAppliedCents) : rowCents;
                      const displayAmount = hasRowAmount
                        ? (netRowCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : (effectiveDisplayPlan?.paymentAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00');
                      return (
                        <div
                          key={`${row.date}-${index}-${isCorrection ? 'corr' : 'reg'}`}
                          className="flex items-center justify-between py-2.5 px-4 rounded-full bg-white/5 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <DollarSign className="w-4 h-4 flex-shrink-0 text-white/40" />
                            <div className="text-sm font-medium truncate text-white">
                              {format(date, 'EEE, MMM d, yyyy')}
                            </div>
                            {isProrated && !isCorrection && (
                              <span className="text-[10px] uppercase tracking-wider text-brand-400/80 font-normal ml-1 hidden sm:inline">
                                Prorated
                              </span>
                            )}
                            {isCorrection && (
                              <span className="text-[10px] uppercase tracking-wider text-brand-400/80 font-normal ml-1">
                                Final balance
                              </span>
                            )}
                            {hasCredit && (
                              <span className="text-[10px] uppercase tracking-wider text-emerald-400/80 font-normal ml-1">
                                Credit applied
                              </span>
                            )}
                            {isNext && !isCorrection && (
                              <span className="text-[10px] uppercase tracking-widest text-white/40 font-normal ml-1 hidden sm:inline">
                                Next
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-medium tabular-nums text-white">
                            {hasCredit && (
                              <span className="text-white/35 line-through mr-2">
                                ${(rowCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            )}
                            ${displayAmount}
                          </div>
                        </div>
                      );
                    })}
                    {/** Drift-C "Heads up" disclosure: explains the prorated + Final
                     *  Balance pair. Anchored *under* those rows (instead of above the
                     *  whole list) so the customer reads "X" → "Y" → "this is why
                     *  there are two charges on that date" in the same eye-line. */}
                    {driftCDisclosure && (
                      <div className="mt-1 px-3 py-2 rounded-lg bg-brand-900/50 border border-brand-800/20">
                        <p className="text-[11px] text-brand-400/85 leading-snug">
                          <span className="font-semibold">Heads up:</span> on{' '}
                          {format(parseISO(driftCDisclosure.date), 'EEE, MMM d')}{' '}
                          you'll see two charges on the same day —{' '}
                          <span className="tabular-nums">${fmtCurrency(driftCDisclosure.prorated_invoice_cents)}</span>{' '}
                          (subscription, prorated to align with departure) and{' '}
                          <span className="tabular-nums">${fmtCurrency(driftCDisclosure.final_correction_cents)}</span>{' '}
                          (final balance correction). Together they total{' '}
                          <span className="tabular-nums">${fmtCurrency(Number(driftCDisclosure.display_total_cents) || (Number(driftCDisclosure.prorated_invoice_cents) + Number(driftCDisclosure.final_correction_cents)))}</span>{' '}
                          — the remaining balance on your trip.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </div>

          {/** Only surface the confirmation-number CTA once the trip is actually
           *  paid off (`remaining_cents === 0` from the schedule endpoint) —
           *  customers mid-plan were tapping it and asking ops for confirmation
           *  numbers on trips with a balance still owed. Hidden once ops marks
           *  the trip `completed`: that only happens after the confirmation is
           *  out, so there's nothing left to request. Tapping files the
           *  Confirmation Number Release inquiry (or acknowledges the one the
           *  final-payment webhook already filed) — no chat detour. */}
          {onTripPaidClick && isPaidInFull && !isTripCompleted && (
            <button
              type="button"
              onClick={onTripPaidClick}
              disabled={tripPaidBusy}
              className="group mt-4 w-full flex items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-white/[0.025] hover:bg-white/[0.045] border border-white/[0.07] hover:border-white/[0.12] transition-all duration-200 disabled:opacity-60"
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-brand-400" />
                </div>
                <div className="text-left">
                  <p className="text-white/85 text-sm font-medium leading-tight">
                    Finished paying for your trip?
                  </p>
                  <p className="text-white/60 text-xs font-normal mt-0.5">
                    Tap to have our team release your confirmation number
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 text-white/40 group-hover:text-white/70 transition-colors duration-200">
                {tripPaidBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-3.5 h-3.5 -translate-x-0.5 group-hover:translate-x-0.5 transition-transform duration-200" />
                )}
              </div>
            </button>
          )}
          
        </CardContent>
      </Card>

      {/* Extension Request Modal */}
      <Dialog open={showExtensionModal} onOpenChange={setShowExtensionModal}>
        <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-normal tracking-tight">Request Payment Extension</DialogTitle>
            <DialogDescription className="text-white/60 font-normal">
              Please select your preferred extension period. All remaining installment payments will be deferred by the selected duration.
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {extensionOptions.length === 0 ? (
              <p className="text-sm text-white/60 font-normal">
                No extension options available. The final payment date cannot be extended beyond {resolvedLastPaymentDaysBeforeDeparture} days before your departure date.
              </p>
            ) : (
              <>
                <RadioGroup value={selectedExtension} onValueChange={setSelectedExtension}>
                  <div className="space-y-3">
                    {extensionOptions.map((option) => (
                      <div key={option.weeks} className="flex items-center space-x-2">
                        <RadioGroupItem value={option.weeks.toString()} id={`extension-${option.weeks}`} className="border-white/20" />
                        <Label 
                          htmlFor={`extension-${option.weeks}`} 
                          className="text-white font-normal cursor-pointer flex-1"
                        >
                          <div>
                            <span className="font-medium">{option.label}</span>
                            <span className="text-white/60 text-xs ml-2">
                              (New final payment: {format(option.newFinalDate, 'MMM d, yyyy')})
                            </span>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>

                {/* Payment Schedule Comparison */}
                {selectedExtension && (() => {
                  const today = startOfDay(new Date());
                  // Filter to only show future payments (payments after today)
                  const futurePayments = (effectiveDisplayPlan?.schedule || []).filter(dateStr => {
                    const date = parseISO(dateStr);
                    return isAfter(startOfDay(date), today) || date.getTime() === today.getTime();
                  });

                  if (futurePayments.length === 0) return null;

                  const selectedOption = extensionOptions.find(opt => opt.weeks.toString() === selectedExtension);
                  if (!selectedOption) return null;

                  return (
                    <div className="mt-6 space-y-4">
                      <p className="text-xs text-white font-medium">Payment Schedule Comparison</p>
                      <div className="grid grid-cols-2 gap-2">
                        {/* Original Schedule */}
                        <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                          <p className="text-[10px] text-white/60 mb-2 font-normal">Original</p>
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {futurePayments.map((dateStr, index) => {
                              const date = parseISO(dateStr);
                              return (
                                <div
                                  key={dateStr}
                                  className="flex items-center justify-between py-1.5 px-2 rounded bg-white/5"
                                >
                                  <div className="flex items-center gap-1 min-w-0">
                                    <DollarSign className="w-2.5 h-2.5 flex-shrink-0 text-white/40" />
                                    <div className="text-[10px] font-medium text-white whitespace-nowrap truncate">
                                      {format(date, 'MMM d, yyyy')}
                                    </div>
                                  </div>
                                  <div className="text-[10px] font-medium text-white whitespace-nowrap ml-1">
                                    ${effectiveDisplayPlan?.paymentAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* New Schedule */}
                        <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                          <p className="text-[10px] text-white/60 mb-2 font-normal">New</p>
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {futurePayments.map((dateStr, index) => {
                              const originalDate = parseISO(dateStr);
                              const newDate = addWeeks(originalDate, selectedOption.weeks);
                              return (
                                <div
                                  key={dateStr}
                                  className="flex items-center justify-between py-1.5 px-2 rounded bg-white/5"
                                >
                                  <div className="flex items-center gap-1 min-w-0">
                                    <DollarSign className="w-2.5 h-2.5 flex-shrink-0 text-white/40" />
                                    <div className="text-[10px] font-medium text-white whitespace-nowrap truncate">
                                      {format(newDate, 'MMM d, yyyy')}
                                    </div>
                                  </div>
                                  <div className="text-[10px] font-medium text-white whitespace-nowrap ml-1">
                                    ${effectiveDisplayPlan?.paymentAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>

          {/* Fee Disclosure */}
          {extensionOptions.length > 0 && (
            <>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 mt-4">
                <p className="text-xs text-white/85 font-normal">
                  <strong className="text-white">Important:</strong> A <strong className="text-white">{EXTENSION_FEE_LABEL} extension fee</strong> will be applied to your account upon completion of your final installment payment. Please note that your travel documents will not be released until this outstanding balance has been settled directly with Defera ({ic.extensionAsideDefera}).
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowExtensionModal(false);
                    setSelectedExtension('');
                  }}
                  className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleExtensionRequest}
                  disabled={!selectedExtension || isSubmitting}
                  className="bg-[var(--accent-color)] hover:bg-primary/90 text-white"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Extension'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Slice Pay redirect overlay (installment trips) */}
      <Dialog open={showSlicePayRedirectOverlay} onOpenChange={setShowSlicePayRedirectOverlay}>
        <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white w-full max-w-[calc(100vw-2rem)] sm:max-w-md mx-2 sm:mx-0 rounded-2xl overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white font-normal tracking-tight">{ic.redirectDialogTitle}</DialogTitle>
          </DialogHeader>
          <p className="text-white/85 text-sm font-normal leading-relaxed py-4">{ic.redirectDialogBody}</p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setShowSlicePayRedirectOverlay(false)}
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSlicePayRedirect}
              className="bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Make a Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white w-full max-w-[calc(100vw-2rem)] sm:max-w-md mx-2 sm:mx-0 rounded-2xl max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Make a Payment</DialogTitle>
            <DialogDescription className="text-white/60">
              Select how you would like to make an early payment towards your trip.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <RadioGroup value={paymentOption} onValueChange={setPaymentOption} className="space-y-3">
              {/* Option 1: Installment Amount */}
              <div className={`relative flex items-center space-x-2 rounded-xl border p-4 cursor-pointer transition-all ${
                paymentOption === 'installment' ? 'border-[var(--accent-color)] bg-[var(--accent-color)]/5' : 'border-white/10 hover:bg-white/5'
              }`}>
                <RadioGroupItem value="installment" id="pay-installment" className="border-white/20 text-[var(--accent-color)]" />
                <Label htmlFor="pay-installment" className="flex-1 cursor-pointer">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-white">One Installment</span>
                    <span className="text-white font-medium">${effectiveDisplayPlan?.paymentAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
                  </div>
                  <p className="text-xs text-white/60 mt-1">Pay your next scheduled installment early</p>
                </Label>
              </div>

              {/* Option 2: Custom Amount */}
              <div className={`relative flex flex-col space-y-3 rounded-xl border p-4 cursor-pointer transition-all ${
                paymentOption === 'custom' ? 'border-[var(--accent-color)] bg-[var(--accent-color)]/5' : 'border-white/10 hover:bg-white/5'
              }`}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="custom" id="pay-custom" className="border-white/20 text-[var(--accent-color)]" />
                  <Label htmlFor="pay-custom" className="flex-1 cursor-pointer">
                    <span className="font-medium text-white">Custom Amount</span>
                    <p className="text-xs text-white/60 mt-1">Enter a specific amount to pay</p>
                  </Label>
                </div>
                
                {paymentOption === 'custom' && (
                  <div className="ml-6 mt-2">
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/20"
                        min="1"
                        step="0.01"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Option 3: Pay Off Balance */}
              <div className={`relative flex items-center space-x-2 rounded-xl border p-4 cursor-pointer transition-all ${
                paymentOption === 'payoff' ? 'border-[var(--accent-color)] bg-[var(--accent-color)]/5' : 'border-white/10 hover:bg-white/5'
              }`}>
                <RadioGroupItem value="payoff" id="pay-payoff" className="border-white/20 text-[var(--accent-color)]" />
                <Label htmlFor="pay-payoff" className="flex-1 cursor-pointer">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-white">Pay Off Balance</span>
                    <CheckCircle className="w-4 h-4 text-[var(--accent-color)]" />
                  </div>
                  <p className="text-xs text-white/60 mt-1">Pay remaining balance in full</p>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setShowPaymentModal(false)}
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePaymentSubmit}
              className="bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white"
              disabled={paymentOption === 'custom' && (!customAmount || parseFloat(customAmount) <= 0)}
            >
              Continue to Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Schedule Modal (Admin Only) */}
      {/* Admin: Override Dates — real billing mutation, rail-aware. */}
      <Dialog open={showAdminOverrideModal} onOpenChange={setShowAdminOverrideModal}>
        <DialogContent className={ACTION_MODAL_CONTENT_CLASS}>
          <ActionModalHeader variant="dialog"
            icon={Zap}
            title="Override Payment Dates"
            description={isDeferaCapitalNmi
              ? 'Set the exact date and amount of every remaining installment. The charge run, reminders, and the customer\u2019s schedule follow these rows immediately.'
              : 'Move the next charge to a specific date. Payments repeat at the chosen frequency and the plan end realigns automatically.'}
          />
          <div className={cn(ACTION_MODAL_BODY_CLASS, 'space-y-4')}>
            {isDeferaCapitalNmi ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  {[{ v: true, label: 'Auto-split amounts' }, { v: false, label: 'Custom amounts' }].map((m) => (
                    <button
                      key={String(m.v)}
                      type="button"
                      onClick={() => setOverrideAutoAmounts(m.v)}
                      className={`py-2.5 px-3 rounded-xl text-xs font-medium transition-all border ${
                        overrideAutoAmounts === m.v
                          ? 'bg-brand-800 border-brand-700/60 text-white shadow-[0_0_16px_-6px_rgba(10,18,51,0.7)]'
                          : 'bg-white/[0.03] border-white/10 text-white/70 hover:border-white/25 hover:text-white'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <p className="text-white/35 text-[11px] leading-relaxed -mt-1">
                  {overrideAutoAmounts
                    ? 'Pick the dates — add or remove payments and the balance re-splits evenly across them.'
                    : 'Set each amount by hand — the tally below reconciles as you type.'}
                </p>
                <DateAmountRowsEditor
                  rows={overrideRows}
                  setRows={setOverrideRows}
                  autoAmounts={overrideAutoAmounts}
                  autoSplitCents={overrideAutoSplitCents(overrideRows.length)}
                />
                {overrideAutoAmounts ? (
                  <div className="rounded-xl border border-brand-500/25 bg-brand-500/[0.06] px-3 py-2 text-[11px] text-brand-200/90">
                    Auto-splitting <span className="font-semibold tabular-nums">${fmtCurrency(overrideBaselineCents)}</span> across{' '}
                    <span className="font-semibold tabular-nums">{overrideRows.length}</span> payment{overrideRows.length === 1 ? '' : 's'} — the full balance is always accounted for.
                  </div>
                ) : (() => {
                  const delta = overrideRowsTotalCents - overrideBaselineCents;
                  return (
                    <div className={`rounded-xl border px-3 py-2 text-[11px] ${
                      delta === 0
                        ? 'bg-emerald-500/[0.06] border-emerald-500/25 text-emerald-200'
                        : 'bg-amber-500/[0.07] border-amber-500/30 text-amber-200'
                    }`}>
                      New total <span className="font-semibold tabular-nums">${fmtCurrency(overrideRowsTotalCents)}</span>
                      {' '}vs balance being rescheduled <span className="font-semibold tabular-nums">${fmtCurrency(overrideBaselineCents)}</span>
                      {delta === 0 && <> — fully accounted for ✓</>}
                      {delta < 0 && <> — <span className="font-semibold tabular-nums">${fmtCurrency(Math.abs(delta))} not accounted for</span></>}
                      {delta > 0 && <> — <span className="font-semibold tabular-nums">${fmtCurrency(delta)} over</span> the balance</>}
                    </div>
                  );
                })()}
                <label className="flex items-center gap-2 text-[11px] text-white/60 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={overrideIncludePastDue}
                    onChange={(e) => { setOverrideIncludePastDue(e.target.checked); seedOverrideRowsFromSchedule(e.target.checked); }}
                  />
                  Include past-due installments (re-arms them at the new dates)
                </label>
                {overrideIncludePastDue && overridePastDueCents > 0 && (
                  <p className="text-[11px] text-white/55 -mt-1">
                    ${fmtCurrency(overridePastDueCents)} past due is included, spread over the dates above.
                  </p>
                )}
                {!overrideAutoAmounts && (
                  <label className="flex items-center gap-2 text-[11px] text-white/60 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overrideAllowTotalChange}
                      onChange={(e) => setOverrideAllowTotalChange(e.target.checked)}
                    />
                    Allow the total to change (deliberate write-down / write-up)
                  </label>
                )}
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <p className="text-white/70 text-xs font-normal uppercase tracking-wider">Next charge date</p>
                  <input
                    type="date"
                    value={overrideAnchorDate}
                    onChange={(e) => setOverrideAnchorDate(e.target.value)}
                    className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5 text-xs text-white [color-scheme:dark] focus:border-white/25 focus:outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-white/70 text-xs font-normal uppercase tracking-wider">Then repeating</p>
                  <div className="grid grid-cols-2 gap-2">
                    {['weekly', 'biweekly'].map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setOverrideFrequency(f)}
                        className={`py-2.5 px-3 rounded-xl text-xs font-medium transition-all border ${
                          overrideFrequency === f
                            ? 'bg-brand-800 border-brand-700/60 text-white shadow-[0_0_16px_-6px_rgba(10,18,51,0.7)]'
                            : 'bg-white/[0.03] border-white/10 text-white/70 hover:border-white/25 hover:text-white'
                        }`}
                      >
                        {f === 'weekly' ? 'Weekly' : 'Bi-weekly'}
                      </button>
                    ))}
                  </div>
                </div>
                {Number(dzScheduleData?.open_invoices_total_cents) > 0 && (
                  <label className="flex items-start gap-2 text-[11px] cursor-pointer rounded-xl border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-amber-200">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={overrideRollPastDue}
                      onChange={(e) => setOverrideRollPastDue(e.target.checked)}
                    />
                    <span>
                      Fold the <span className="font-semibold tabular-nums">${fmtCurrency(Number(dzScheduleData.open_invoices_total_cents))}</span> past-due balance into the new schedule — the failing invoice is voided, Stripe stops retrying it, and the amount is collected across the new dates instead. Required to override while past-due.
                    </span>
                  </label>
                )}
                <p className="text-white/30 text-[10px] text-center leading-relaxed">
                  Stripe bills one amount on a fixed cadence — the first charge lands on the date above, the rest follow at the chosen rhythm, and the plan end realigns so exactly the remaining balance is collected.
                </p>
              </>
            )}

            <div className="space-y-2">
              <p className="text-white/70 text-xs font-normal uppercase tracking-wider">Note <span className="text-white/30 normal-case font-normal">(lands in the audit trail)</span></p>
              <input
                type="text"
                maxLength={500}
                value={overrideNote}
                onChange={(e) => setOverrideNote(e.target.value)}
                placeholder="e.g. customer paid biweekly on the 1st and 15th"
                className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5 text-xs text-white placeholder:text-white/20 focus:border-white/25 focus:outline-none"
              />
            </div>

            {overrideErrors.length > 0 && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 space-y-1">
                {overrideErrors.map((e, i) => (
                  <p key={i} className="text-red-200 text-[11px]">{e}</p>
                ))}
              </div>
            )}

            <div className="flex gap-2.5 pt-1">
              <Button
                onClick={handleSaveAdminOverride}
                disabled={isSavingOverride || (isDeferaCapitalNmi ? overrideRows.length === 0 : !overrideAnchorDate)}
                className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1 text-xs sm:text-sm')}
              >
                {isSavingOverride ? 'Applying…' : 'Apply Override'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowAdminOverrideModal(false)}
                className={cn(MODAL_GHOST_BTN_CLASS, 'px-5 text-xs sm:text-sm')}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditScheduleModal} onOpenChange={setShowEditScheduleModal}>
        <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-normal tracking-tight">Edit Payment Schedule</DialogTitle>
            <DialogDescription className="text-white/60 font-normal">
              Adjust the deposit percentage and payment frequency. The schedule will be recalculated automatically.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Deposit Mode Toggle */}
            <div className="space-y-3">
              <p className="text-white text-sm">Deposit Type</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setEditDepositMode('percentage')}
                  className={`flex items-center justify-center w-full py-2 px-3 text-sm font-medium rounded-lg border cursor-pointer transition-all ${
                    editDepositMode === 'percentage'
                      ? 'bg-[var(--accent-color)] border-[var(--accent-color)] text-white'
                      : 'border-white/20 text-white/70 hover:bg-white/10'
                  }`}
                >
                  Percentage
                </button>
                <button
                  type="button"
                  onClick={() => setEditDepositMode('custom')}
                  className={`flex items-center justify-center w-full py-2 px-3 text-sm font-medium rounded-lg border cursor-pointer transition-all ${
                    editDepositMode === 'custom'
                      ? 'bg-[var(--accent-color)] border-[var(--accent-color)] text-white'
                      : 'border-white/20 text-white/70 hover:bg-white/10'
                  }`}
                >
                  Custom Amount
                </button>
              </div>
            </div>

            {/* Deposit Percentage (shown when percentage mode) */}
            {/* Defera Capital admin picker exposes 5/10 (only valid at 90+ day lead);
                Slice Pay stays on 20/30/40/50. Out-of-band overrides still go through Custom Amount. */}
            {editDepositMode === 'percentage' && (
              <div className="space-y-3">
                <p className="text-white text-sm">Deposit Percentage</p>
                <div className="grid grid-cols-6 gap-2">
                  {(isDeferaCapital ? [5, 10, 20, 30, 40, 50] : [20, 30, 40, 50]).map((pct) => (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => setEditDepositPercentage(pct)}
                      className={`flex items-center justify-center w-full py-2 px-3 text-sm font-medium rounded-lg border cursor-pointer transition-all ${
                        editDepositPercentage === pct
                          ? 'bg-[var(--accent-color)] border-[var(--accent-color)] text-white'
                          : 'border-white/20 text-white/70 hover:bg-white/10'
                      }`}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Deposit Amount (shown when custom mode) */}
            {editDepositMode === 'custom' && (
              <div className="space-y-3">
                <p className="text-white text-sm">Custom Deposit Amount</p>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={editCustomDepositAmount}
                    onChange={(e) => setEditCustomDepositAmount(e.target.value)}
                    className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    min="1"
                    step="0.01"
                  />
                </div>
                {editCustomDepositAmount && totalWithFee > 0 && (
                  <p className="text-xs text-white/50">
                    ≈ {((parseFloat(editCustomDepositAmount) / totalWithFee) * 100).toFixed(1)}% of total
                  </p>
                )}
              </div>
            )}

            {/* Payment Plan */}
            <div className="space-y-3">
              <p className="text-white text-sm">Payment Frequency</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setEditPaymentPlan('weekly')}
                  className={`flex flex-col items-center justify-center w-full py-3 px-4 text-sm font-medium rounded-lg border cursor-pointer transition-all ${
                    editPaymentPlan === 'weekly'
                      ? 'bg-[var(--accent-color)] border-[var(--accent-color)] text-white'
                      : 'border-white/20 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <span>Weekly</span>
                  <span className="text-xs opacity-70 mt-1">Every 7 days</span>
                </button>
                <button
                  type="button"
                  onClick={() => setEditPaymentPlan('biweekly')}
                  className={`flex flex-col items-center justify-center w-full py-3 px-4 text-sm font-medium rounded-lg border cursor-pointer transition-all ${
                    editPaymentPlan === 'biweekly'
                      ? 'bg-[var(--accent-color)] border-[var(--accent-color)] text-white'
                      : 'border-white/20 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <span>Bi-weekly</span>
                  <span className="text-xs opacity-70 mt-1">Every 14 days</span>
                </button>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setShowEditScheduleModal(false)}
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSchedule}
              disabled={isSavingSchedule}
              className="bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white"
            >
              {isSavingSchedule ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {movePayment?.choices && (
        <MovePaymentDialog
          open={showMovePayment}
          onOpenChange={setShowMovePayment}
          tripId={tripId}
          apiUrlFn={dzApiUrl}
          paymentFrequency={payment_plan === 'weekly' ? 'weekly' : 'biweekly'}
          choices={movePayment.choices}
          dueDate={movePayment.dueDate}
          dueAmountCents={movePayment.dueAmountCents}
          failed={movePayment.failed}
          onMoved={(moved) => {
            if (moved?.to_date) setJustMovedPayment({ ...moved, payment_id: movePayment.paymentId });
            fetchDzSchedule();
            if (onScheduleUpdated) onScheduleUpdated();
            else if (onExtensionRequested) onExtensionRequested();
          }}
        />
      )}

      {/* Defera Zero: Change Schedule Modal */}
      <Dialog open={showDeferaZeroScheduleChange} onOpenChange={setShowDeferaZeroScheduleChange}>
        <DialogContent className={ACTION_MODAL_CONTENT_CLASS}>
          <ActionModalHeader variant="dialog"
            icon={Clock}
            title="Change Schedule"
            description="Choose a new payment frequency. Your remaining balance will be spread across the new schedule — no charge today."
          />
          <div className={cn(ACTION_MODAL_BODY_CLASS, 'space-y-4')}>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setCustDatesMode(false)}
                className={`py-2.5 px-3 rounded-xl text-xs font-medium transition-all border ${
                  !custDatesMode
                    ? 'bg-brand-800 border-brand-700/60 text-white shadow-[0_0_16px_-6px_rgba(10,18,51,0.7)]'
                    : 'bg-white/[0.03] border-white/10 text-white/70 hover:border-white/25 hover:text-white'
                }`}
              >
                Change frequency
                <span className="block text-[10px] font-normal opacity-70">free</span>
              </button>
              {!isAdmin && (
              <button
                type="button"
                onClick={openCustomerDatesMode}
                className={`py-2.5 px-3 rounded-xl text-xs font-medium transition-all border ${
                  custDatesMode
                    ? 'bg-brand-800 border-brand-700/60 text-white shadow-[0_0_16px_-6px_rgba(10,18,51,0.7)]'
                    : 'bg-white/[0.03] border-white/10 text-white/70 hover:border-white/25 hover:text-white'
                }`}
              >
                Pick my own dates
                <span className="block text-[10px] font-normal opacity-70">{SCHEDULE_DATE_CHANGE_FEE_LABEL} fee</span>
              </button>
              )}
            </div>
            {custDatesMode ? (
              <>
                {isDeferaCapitalNmi ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {[{ v: true, label: 'Auto-split amounts' }, { v: false, label: 'Custom amounts' }].map((m) => (
                        <button
                          key={String(m.v)}
                          type="button"
                          onClick={() => setCustAutoAmounts(m.v)}
                          className={`py-2 px-3 rounded-xl text-[11px] font-medium transition-all border ${
                            custAutoAmounts === m.v
                              ? 'bg-brand-800 border-brand-700/60 text-white'
                              : 'bg-white/[0.03] border-white/10 text-white/60 hover:border-white/25'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                    <DateAmountRowsEditor
                      rows={custRows}
                      setRows={setCustRows}
                      autoAmounts={custAutoAmounts}
                      autoSplitCents={splitCentsEvenly(custBaselineCents, custRows.length)}
                      maxDate={custMaxDate}
                    />
                    {custAutoAmounts ? (
                      <div className="rounded-xl border border-brand-500/25 bg-brand-500/[0.06] px-3 py-2 text-[11px] text-brand-200/90">
                        Your remaining <span className="font-semibold tabular-nums">${fmtCurrency(custBaselineCents)}</span> re-splits evenly across{' '}
                        <span className="font-semibold tabular-nums">{custRows.length}</span> payment{custRows.length === 1 ? '' : 's'}.
                      </div>
                    ) : (() => {
                      const delta = custRowsTotalCents - custBaselineCents;
                      return (
                        <div className={`rounded-xl border px-3 py-2 text-[11px] ${
                          delta === 0
                            ? 'bg-emerald-500/[0.06] border-emerald-500/25 text-emerald-200'
                            : 'bg-amber-500/[0.07] border-amber-500/30 text-amber-200'
                        }`}>
                          Your payments total <span className="font-semibold tabular-nums">${fmtCurrency(custRowsTotalCents)}</span>
                          {' '}vs remaining balance <span className="font-semibold tabular-nums">${fmtCurrency(custBaselineCents)}</span>
                          {delta === 0 && <> — all accounted for ✓</>}
                          {delta < 0 && <> — <span className="font-semibold tabular-nums">${fmtCurrency(Math.abs(delta))} short</span>; every dollar of your balance must be scheduled</>}
                          {delta > 0 && <> — <span className="font-semibold tabular-nums">${fmtCurrency(delta)} over</span> your remaining balance</>}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <p className="text-white/70 text-xs font-normal uppercase tracking-wider">Next payment date</p>
                      <input
                        type="date"
                        value={custAnchorDate}
                        max={custMaxDate || undefined}
                        disabled={custSaving}
                        onChange={(e) => setCustAnchorDate(e.target.value)}
                        className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5 text-xs text-white [color-scheme:dark] focus:border-white/25 focus:outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <p className="text-white/70 text-xs font-normal uppercase tracking-wider">Then repeating</p>
                      <div className="grid grid-cols-2 gap-2">
                        {['weekly', 'biweekly'].map((f) => (
                          <button
                            key={f}
                            type="button"
                            disabled={custSaving}
                            onClick={() => setCustFrequency(f)}
                            className={`py-2 px-3 rounded-xl text-[11px] font-medium transition-all border ${
                              custFrequency === f
                                ? 'bg-brand-800 border-brand-700/60 text-white'
                                : 'bg-white/[0.03] border-white/10 text-white/60 hover:border-white/25'
                            }`}
                          >
                            {f === 'weekly' ? 'Weekly' : 'Bi-weekly'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-white/35 text-[11px] leading-relaxed">
                      Your next payment lands exactly on the date above, then repeats {custFrequency === 'weekly' ? 'weekly' : 'every two weeks'}. Amounts re-spread automatically across the remaining payments.
                    </p>
                    {custPreviewData && Number(custPreviewData.payments_remaining) > 0 && (
                      <div data-testid="custom-dates-preview" className="rounded-xl border border-brand-500/25 bg-brand-500/[0.06] px-3 py-2 text-[11px] text-brand-200/90">
                        Your new plan:{' '}
                        <span className="font-semibold tabular-nums">
                          {custPreviewData.payments_remaining} payment{Number(custPreviewData.payments_remaining) === 1 ? '' : 's'} of ${fmtCurrency(custPreviewData.payment_amount_cents)}
                        </span>
                        {Number(custPreviewData.payments_remaining) === 1
                          ? <> on {fmtPlanDate(custPreviewData.next_payment_date)}.</>
                          : <>, {custPreviewData.payment_frequency === 'weekly' ? 'weekly' : 'every two weeks'} from {fmtPlanDate(custPreviewData.next_payment_date)} to {fmtPlanDate(custPreviewData.last_payment_date)}.</>}
                      </div>
                    )}
                  </>
                )}
                {custPreview?.status === 'invalid' && (
                  <div data-testid="custom-dates-invalid" className="rounded-xl border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-[11px] text-amber-200">
                    {custPreview.error}
                  </div>
                )}
                {custPreviewData?.no_change && (
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-[11px] text-amber-200">
                    These are the dates you already have — change one to continue.
                  </div>
                )}
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-[11px] text-white/70">
                  {/* Was "A one-time $25 fee", which reads as once-ever. On
                      C53QGT that cost a customer $50 for two changes 12
                      minutes apart. The fee is now a day pass, so say what
                      they actually get: one charge, unlimited changes today. */}
                  {custPreviewData?.fee_waived ? (
                    <><span className="text-white font-semibold">No charge</span> — this date change is on us.</>
                  ) : custPreviewData?.fee_already_paid_today ? (
                    <><span className="text-white font-semibold">No charge</span> — the {SCHEDULE_DATE_CHANGE_FEE_LABEL} you paid today covers as many date changes as you need today.</>
                  ) : (
                    <><span className="text-white font-semibold">{SCHEDULE_DATE_CHANGE_FEE_LABEL}</span> is charged to your card on file today, and it covers <span className="text-white font-semibold">as many date changes as you need today</span> — adjust these again later today and there&apos;s no further charge.</>
                  )}{' '}
                  New dates must finish at least {SCHEDULE_DATE_CHANGE_CUTOFF_DAYS} days before departure{custMaxDate ? ` (by ${custMaxDate})` : ''}.
                </div>
                {custErrors.length > 0 && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 space-y-1">
                    {custErrors.map((e, i) => (
                      <p key={i} className="text-red-200 text-[11px]">{e}</p>
                    ))}
                  </div>
                )}
                <div className="flex gap-2.5 pt-1">
                  <Button
                    onClick={handleCustomerDatesSubmit}
                    disabled={custSaving || custPreviewBlocks || (isDeferaCapitalNmi
                      ? custRows.length === 0
                      : (!custAnchorDate || custUnchanged))}
                    className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1 text-xs sm:text-sm')}
                  >
                    {custSaving ? 'Processing…' : custFeeFree ? 'Update Dates' : `Pay ${SCHEDULE_DATE_CHANGE_FEE_LABEL} & Update Dates`}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setCustDatesMode(false)}
                    className={cn(MODAL_GHOST_BTN_CLASS, 'px-5 text-xs sm:text-sm')}
                  >
                    Back
                  </Button>
                </div>
              </>
            ) : (
            <>
            <p className="text-white/70 text-xs font-normal uppercase tracking-wider">Payment frequency</p>
            <div className="grid grid-cols-2 gap-2">
              {['weekly', 'biweekly'].map((f) => (
                <button
                  key={f}
                  onClick={() => handleDzFrequencySelect(f)}
                  className={`py-2.5 px-3 rounded-xl text-xs font-medium transition-all border ${
                    dzNewFrequency === f
                      ? 'bg-brand-800 border-brand-700/60 text-white shadow-[0_0_16px_-6px_rgba(10,18,51,0.7)]'
                      : 'bg-white/[0.03] border-white/10 text-white/70 hover:border-white/25 hover:text-white'
                  }`}
                >
                  {frequencyLabels[f]}
                </button>
              ))}
            </div>
            {dzLoadingPreview && (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 text-white/20 animate-spin" />
              </div>
            )}
            {dzPreview && !dzLoadingPreview && (
              <div className="space-y-3">
                {dzPreview.paid_amount_cents > 0 && (
                  <div className="flex justify-between rounded-xl bg-white/[0.02] border border-white/[0.06] px-3 py-2">
                    <span className="text-white/35 text-[11px]">Already paid</span>
                    <span className="text-white/50 text-[11px] font-normal">${fmtCurrency(dzPreview.paid_amount_cents)}</span>
                  </div>
                )}
                <p className="text-white/30 text-[10px] text-center">Remaining ${fmtCurrency(dzPreview.remaining_cents)} spread across future payments — no charge today</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-3">
                    <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Current · {frequencyLabels[dzPreview.current_frequency]}</p>
                    <p className="text-white/50 text-xs font-normal mb-2">
                      {scheduleSummary(dzPreview.current_schedule, { summarizeNonUniform: true })}
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                      {(dzPreview.current_schedule?.dates || []).map((d, i) => (
                        <div key={i} className="flex justify-between text-[11px]">
                          <span className="text-white/25">{d?.date ? formatScheduleDate(d.date, 'MMM d') : '—'}</span>
                          <span className="text-white/35">${fmtCurrency(d?.amount_cents || 0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-xl border border-brand-500/25 bg-brand-500/[0.06] p-3">
                    <p className="text-brand-300/80 text-[10px] uppercase tracking-wider mb-2">New · {frequencyLabels[dzPreview.proposed_frequency]}</p>
                    <p className="text-brand-200/90 text-xs font-normal mb-2">
                      {scheduleSummary(dzPreview.proposed_schedule, { summarizeNonUniform: true })}
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                      {(dzPreview.proposed_schedule?.dates || []).map((d, i) => (
                        <div key={i} className="flex justify-between text-[11px]">
                          <span className="text-brand-300/50">{d?.date ? formatScheduleDate(d.date, 'MMM d') : '—'}</span>
                          <span className="text-brand-200/70">${fmtCurrency(d?.amount_cents || 0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-center gap-3 text-xs">
                  <span className="text-white/35">${fmtCurrency(dzPreview.current_schedule?.per_payment_cents || 0)} × {dzPreview.current_schedule?.num_payments || 0}</span>
                  <ArrowRight className="w-3 h-3 text-white/20" />
                  <span className="text-brand-300">${fmtCurrency(dzPreview.proposed_schedule?.per_payment_cents || 0)} × {dzPreview.proposed_schedule?.num_payments || 0}</span>
                </div>
              </div>
            )}
            <div className="flex gap-2.5 pt-2">
              <Button
                onClick={handleDzUpdateSchedule}
                disabled={dzUpdating || dzNewFrequency === payment_plan}
                className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1 text-xs sm:text-sm')}
              >
                {dzUpdating ? 'Updating...' : 'Confirm Change'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setShowDeferaZeroScheduleChange(false); setDzNewFrequency(payment_plan); setDzPreview(null); }}
                className={cn(MODAL_GHOST_BTN_CLASS, 'px-5 text-xs sm:text-sm')}
              >
                Cancel
              </Button>
            </div>
            </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Defera Capital: Skip Upcoming Payment Modal ($20 fee).
          NMI bookings use the vault-charge variant (no Stripe subscription).
          Stripe bookings keep the before/after schedule preview + trial_end push. */}
      <Dialog open={showSkipPaymentModal} onOpenChange={(open) => { if (!skipCharging) { setShowSkipPaymentModal(open); if (!open) { setSkipPreview(null); setSkipPreviewError(null); } } }}>
        <DialogContent className={ACTION_MODAL_CONTENT_CLASS}>
          <ActionModalHeader variant="dialog"
            icon={SkipForward}
            title="Skip Upcoming Payment"
            description={isDeferaCapitalNmi
              ? `Skip your next installment for a one-time $${fmtCurrency(SKIP_PAYMENT_FEE_CENTS)} fee charged to the card on file today. That installment becomes $0 and your remaining installments go up to cover it — your payment dates don't change.`
              : `Skip your next payment for a one-time $${fmtCurrency(skipPreview?.fee_cents ?? SKIP_PAYMENT_FEE_CENTS)} fee. Your remaining installments will go up to cover it — your payment dates, cutoff, and final payment date don't change.`}
          />
          {isDeferaCapitalNmi ? (
            <div className={ACTION_MODAL_BODY_CLASS}>
              <DeferaCapitalNmiSkipPaymentForm
                tripId={tripId}
                upcomingRows={nmiSkipUpcomingRows}
                openInvoicesTotalCents={dzScheduleData?.open_invoices_total_cents || 0}
                onPaymentUncertain={pollNmiScheduleAfterPayment}
                onClose={() => {
                  setShowSkipPaymentModal(false);
                  setSkipPreview(null);
                  setSkipPreviewError(null);
                }}
                onSuccess={() => {
                  setShowSkipPaymentModal(false);
                  setSkipPreview(null);
                  fetchDzSchedule();
                  if (onScheduleUpdated) onScheduleUpdated();
                  else if (onExtensionRequested) onExtensionRequested();
                }}
              />
            </div>
          ) : (
          <div className={cn(ACTION_MODAL_BODY_CLASS, 'space-y-4')}>
            {skipPreviewLoading && (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 text-white/20 animate-spin" />
              </div>
            )}
            {!skipPreviewLoading && skipPreviewError && (
              <div className="px-3 py-2 rounded-lg bg-red-500/[0.07] border border-red-500/20">
                <p className="text-[12px] text-red-300/90 leading-snug">{skipPreviewError}</p>
              </div>
            )}
            {!skipPreviewLoading && !skipPreviewError && skipPreview && !skipPreview.eligible && (
              <div className="px-3 py-2 rounded-lg bg-brand-500/[0.07] border border-brand-500/20">
                <p className="text-[12px] text-brand-300/90 leading-snug">
                  {skipPreview.message || "This trip isn't eligible to skip a payment right now."}
                </p>
              </div>
            )}
            {!skipPreviewLoading && !skipPreviewError && skipPreview?.eligible && (
              <div className="space-y-3">
                {/* What's being skipped */}
                <div className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/[0.08] px-3 py-2.5">
                  <div>
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-0.5">Skipping</p>
                    <p className="text-white/85 text-sm font-medium">
                      {skipPreview.skipped_date ? format(parseISO(skipPreview.skipped_date), 'EEE, MMM d, yyyy') : '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-sm tabular-nums">
                    <span className="text-white/40 line-through">${fmtCurrency(skipPreview.skipped_amount_cents || 0)}</span>
                    <ArrowRight className="w-3 h-3 text-white/25" />
                    <span className="text-brand-300 font-medium">$0.00</span>
                  </div>
                </div>
                {/* Before / after schedules */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-3">
                    <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Current</p>
                    <p className="text-white/50 text-xs font-normal mb-2">
                      {scheduleSummary(skipPreview.current_schedule, { summarizeNonUniform: true })}
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                      {(skipPreview.current_schedule?.dates || []).map((d, i) => (
                        <div key={i} className="flex justify-between text-[11px]">
                          <span className="text-white/25">{d?.date ? format(parseISO(d.date), 'MMM d') : '—'}</span>
                          <span className="text-white/35">${fmtCurrency(d?.amount_cents || 0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-xl border border-brand-500/25 bg-brand-500/[0.06] p-3">
                    <p className="text-brand-300/80 text-[10px] uppercase tracking-wider mb-2">New</p>
                    <p className="text-brand-200/90 text-xs font-normal mb-2">
                      {scheduleSummary(skipPreview.proposed_schedule, { summarizeNonUniform: true })}
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                      {skipPreview.skipped_date && (
                        <div className="flex justify-between text-[11px]">
                          <span className="text-brand-300/40 line-through">{format(parseISO(skipPreview.skipped_date), 'MMM d')}</span>
                          <span className="text-brand-200/60">$0.00</span>
                        </div>
                      )}
                      {(skipPreview.proposed_schedule?.dates || []).map((d, i) => (
                        <div key={i} className="flex justify-between text-[11px]">
                          <span className="text-brand-300/50">{d?.date ? format(parseISO(d.date), 'MMM d') : '—'}</span>
                          <span className="text-brand-200/70">${fmtCurrency(d?.amount_cents || 0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-center gap-3 text-xs">
                  <span className="text-white/35">${fmtCurrency(skipPreview.current_schedule?.per_payment_cents || 0)} × {skipPreview.current_schedule?.num_payments || 0}</span>
                  <ArrowRight className="w-3 h-3 text-white/20" />
                  <span className="text-brand-300">${fmtCurrency(skipPreview.proposed_schedule?.per_payment_cents || 0)} × {skipPreview.proposed_schedule?.num_payments || 0}</span>
                </div>
                {/* Fee disclosure */}
                <div className="px-3 py-2.5 rounded-xl bg-brand-500/[0.06] border border-brand-500/20">
                  <p className="text-[11px] text-brand-200/85 leading-snug">
                    A one-time <span className="font-semibold tabular-nums">${fmtCurrency(skipPreview.fee_cents || 2000)}</span> skip fee will be charged to your card on file today. The fee doesn't go toward your trip balance. Your payment cutoff and final payment date stay the same.
                  </p>
                </div>
              </div>
            )}
            <div className="flex gap-2.5 pt-2">
              <Button
                onClick={handleConfirmSkipPayment}
                disabled={skipCharging || skipPreviewLoading || !skipPreview?.eligible}
                className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1 text-xs sm:text-sm')}
              >
                {skipCharging ? (
                  <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Processing…</span>
                ) : (
                  `Pay $${fmtCurrency(skipPreview?.fee_cents ?? SKIP_PAYMENT_FEE_CENTS)} & Skip`
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setShowSkipPaymentModal(false); setSkipPreview(null); setSkipPreviewError(null); }}
                disabled={skipCharging}
                className={cn(MODAL_GHOST_BTN_CLASS, 'px-5 text-xs sm:text-sm')}
              >
                Cancel
              </Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply Credits Modal — customer points Defera Credit (house credit) at this
          trip's upcoming payments. Two stages: amount entry with a front-to-back
          coverage preview, then an explicit "Are you sure…" confirmation. No card
          is charged; the server debits the credit ledger and (NMI) rewrites open
          installment rows / (Stripe) lets the invoice webhook net future cycles. */}
      <Dialog open={showApplyCreditsModal} onOpenChange={(open) => { if (!applyCreditsSubmitting) { setShowApplyCreditsModal(open); if (!open) { setApplyCreditsInput(''); setApplyCreditsStage('input'); setApplyCreditsError(null); } } }}>
        <DialogContent className={ACTION_MODAL_CONTENT_CLASS}>
          <ActionModalHeader variant="dialog"
            icon={Wallet}
            title="Apply Defera Credits"
            description={viewingAsOwner
              ? `This customer has $${fmtCurrency(deferaCreditCents)} in Defera Credits. Credits apply to their immediate upcoming payments first — anything not applied stays in their balance.`
              : `You have $${fmtCurrency(deferaCreditCents)} in Defera Credits. Credits apply to your immediate upcoming payments first — anything you don't apply stays in your balance.`}
          />
          {applyCreditsStage === 'input' ? (
            <div className={cn(ACTION_MODAL_BODY_CLASS, 'space-y-4')}>
              <div>
                <Label htmlFor="apply-credits-amount" className="text-white/60 text-xs">Amount to apply</Label>
                <div className="flex gap-2 mt-1.5">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                    <Input
                      id="apply-credits-amount"
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={applyCreditsInput}
                      onChange={(e) => { setApplyCreditsInput(e.target.value); setApplyCreditsError(null); }}
                      className="pl-7 bg-white/[0.04] border-white/[0.1] text-white"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setApplyCreditsInput(fmtCurrency(applyCreditsMaxCents).replace(/,/g, '')); setApplyCreditsError(null); }}
                    className="h-10 px-3 text-xs rounded-lg border-white/[0.1] bg-white/[0.04] text-white/70 hover:text-white"
                  >
                    Max ${fmtCurrency(applyCreditsMaxCents)}
                  </Button>
                </div>
                <p className="text-white/35 text-[11px] mt-1.5 font-normal">
                  Your upcoming payments can absorb up to ${fmtCurrency(applyCreditsCapCents)}.
                </p>
              </div>
              {applyCreditsPreview.length > 0 && (
                <div className="rounded-xl border border-brand-500/25 bg-brand-500/[0.06] p-3">
                  <p className="text-brand-300/80 text-[10px] uppercase tracking-wider mb-2">What this covers</p>
                  <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                    {applyCreditsPreview.map((p, i) => (
                      <div key={i} className="flex justify-between items-center text-[11px]">
                        <span className="text-brand-300/50">{formatScheduleDate(p.date, 'MMM d, yyyy')}</span>
                        <span className="flex items-center gap-1.5 tabular-nums">
                          <span className="text-white/35 line-through">${fmtCurrency(p.before_cents)}</span>
                          <ArrowRight className="w-3 h-3 text-white/20" />
                          <span className={p.after_cents === 0 ? 'text-brand-300 font-medium' : 'text-brand-200/80'}>
                            ${fmtCurrency(p.after_cents)}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {applyCreditsError && (
                <div className="px-3 py-2 rounded-lg bg-red-500/[0.07] border border-red-500/20">
                  <p className="text-[12px] text-red-300/90 leading-snug">{applyCreditsError}</p>
                </div>
              )}
              <div className="flex gap-2.5 pt-2">
                <Button
                  onClick={handleApplyCreditsContinue}
                  disabled={applyCreditsAmountCents <= 0}
                  className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1 text-xs sm:text-sm')}
                >
                  Continue
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowApplyCreditsModal(false)}
                  className={cn(MODAL_GHOST_BTN_CLASS, 'px-5 text-xs sm:text-sm')}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className={cn(ACTION_MODAL_BODY_CLASS, 'space-y-4')}>
              <div className="rounded-xl bg-white/[0.03] border border-white/[0.08] px-4 py-3.5 text-center">
                <p className="text-white/85 text-sm">
                  Are you sure you want to apply{' '}
                  <span className="font-semibold tabular-nums text-brand-300">${fmtCurrency(applyCreditsAmountCents)}</span>
                  {' '}towards this trip?
                </p>
                <p className="text-white/60 text-[11px] mt-1.5 font-normal">
                  {viewingAsOwner
                    ? 'This can’t be undone from the app — the amount moves from the customer’s Defera Credits to this trip’s upcoming payments.'
                    : 'This can’t be undone from the app — the amount moves from your Defera Credits to this trip’s upcoming payments.'}
                </p>
              </div>
              {applyCreditsError && (
                <div className="px-3 py-2 rounded-lg bg-red-500/[0.07] border border-red-500/20">
                  <p className="text-[12px] text-red-300/90 leading-snug">{applyCreditsError}</p>
                </div>
              )}
              <div className="flex gap-2.5 pt-2">
                <Button
                  onClick={handleConfirmApplyCredits}
                  disabled={applyCreditsSubmitting}
                  className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1 text-xs sm:text-sm')}
                >
                  {applyCreditsSubmitting ? (
                    <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Applying…</span>
                  ) : (
                    `Apply $${fmtCurrency(applyCreditsAmountCents)}`
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setApplyCreditsStage('input'); setApplyCreditsError(null); }}
                  disabled={applyCreditsSubmitting}
                  className={cn(MODAL_GHOST_BTN_CLASS, 'px-5 text-xs sm:text-sm')}
                >
                  Back
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Make a Payment Modal (inline, like Reserve inject).
          NMI bookings use the Collect.js-free variant — payment is charged
          against the on-file vault, no card collection step. Stripe bookings
          keep the original Elements-wrapped form with card picker + new card. */}
      <Dialog open={showDeferaZeroMakePayment} onOpenChange={setShowDeferaZeroMakePayment}>
        <DialogContent className={ACTION_MODAL_CONTENT_CLASS}>
          <ActionModalHeader variant="dialog"
            icon={Zap}
            title="Make a Payment"
            description={isDeferaCapitalNmi
              ? "Pay any amount toward your trip with the card on file or a different card, and we'll show your updated schedule."
              : "Pay any amount toward your trip. Choose a card to charge and we'll show your updated schedule."}
          />
          <div className={ACTION_MODAL_BODY_CLASS}>
            {isDeferaCapitalNmi ? (
              <DeferaCapitalNmiMakePaymentForm
                tripId={tripId}
                flightData={currentFlightData}
                onClose={() => {
                  setShowDeferaZeroMakePayment(false);
                  /** Drop the pending-settlement context so the next time the customer
                   *  opens this modal via the generic "Make a Payment" CTA they land on
                   *  the tabs-first layout instead of a stale focal past-due banner. */
                  setNmiPendingSettlementRow(null);
                }}
                onSuccess={({ current_schedule, proposed_schedule }) => {
                  setShowDeferaZeroMakePayment(false);
                  setNmiPendingSettlementRow(null);
                  if (current_schedule || proposed_schedule) {
                    setPaymentSuccessComparison({
                      before: {
                        schedule: (current_schedule?.dates || []).map((d) => d?.date),
                        scheduleRows: current_schedule?.dates || [],
                        paymentAmount: (current_schedule?.per_payment_cents || 0) / 100,
                        paymentCount: current_schedule?.num_payments,
                      },
                      after: proposed_schedule,
                    });
                  }
                  fetchDzSchedule();
                  if (onScheduleUpdated) onScheduleUpdated();
                  else if (onExtensionRequested) onExtensionRequested();
                }}
                remainingCentsHint={dzScheduleData?.remaining_cents || 0}
                perPaymentCentsHint={dzScheduleData?.stripe_unit_amount_cents || dzScheduleData?.schedule?.per_payment_cents || 0}
                upcomingDates={nmiOpenUpcomingRows}
                openInvoices={dzScheduleData?.open_invoices || []}
                openInvoicesTotalCents={dzScheduleData?.open_invoices_total_cents || 0}
                planRestartable={isPlanRestartable}
                onPaymentUncertain={pollNmiScheduleAfterPayment}
                /** Drives the focal "Past due — Installment N · $X" banner-with-Pay-button
                 *  at the top of the form. Set by the upcoming-list's rose "Pay now" click;
                 *  null on a plain "Make a Payment" open (which keeps the tabs as the
                 *  primary CTA). */
                pendingSettlementRow={nmiPendingSettlementRow}
              />
            ) : stripePromise ? (
              <Elements stripe={stripePromise}>
                <DeferaZeroMakePaymentForm
                  tripId={tripId}
                  apiUrlFn={dzApiUrl}
                  pastDuePartialEnabled={isStripePastDuePartial}
                  initialMode={dzMakePaymentInitialMode}
                  fmtCurrency={fmtCurrency}
                  /** Past-due / open invoices come from the schedule endpoint (Edge function adds
                   *  these for both DZ and DC). Empty array when no past-due invoices, hides the
                   *  banner entirely so customers in good standing aren't visually alarmed. */
                  openInvoices={dzScheduleData?.open_invoices || []}
                  openInvoicesTotalCents={dzScheduleData?.open_invoices_total_cents || 0}
                  /** The PM the subscription will auto-charge if the customer does nothing — used
                   *  to (a) preselect that card in the picker, (b) hide the "Use this card for
                   *  future installments" checkbox when it's already the default. */
                  subscriptionDefaultPaymentMethodId={dzScheduleData?.subscription_default_payment_method_id || null}
                  /** Stripe-locked per-payment cents, the source of truth for the "Pay ahead"
                   *  stepper math. Without it the form falls back to custom-mode only. */
                  stripeUnitAmountCents={dzScheduleData?.stripe_unit_amount_cents || 0}
                  /** Current upcoming dates power the stepper hard cap (1..upcomingCount) and the
                   *  "Next charge moves" before/after row in the Pay-ahead card. */
                  currentScheduleDates={dzScheduleData?.schedule?.dates || []}
                  /** Remaining balance hint for the payoff CTA before the first preview returns. */
                  remainingCentsHint={dzScheduleData?.remaining_cents || 0}
                  /** Committed Defera Credit the payoff will spend, so the Pay off tab's first paint
                   *  is already net of it (the tab's preview then confirms the server's figure). */
                  payoffCreditHintCents={dzScheduleData?.defera_credit_pending_cents || 0}
                  onClose={() => { setShowDeferaZeroMakePayment(false); setDzMakePaymentInitialMode(null); }}
                  onSuccess={({ current_schedule, proposed_schedule }) => {
                    setShowDeferaZeroMakePayment(false);
                    if (current_schedule || proposed_schedule) {
                      setPaymentSuccessComparison({
                        before: {
                          schedule: (current_schedule?.dates || []).map((d) => d?.date),
                          scheduleRows: current_schedule?.dates || [],
                          paymentAmount: (current_schedule?.per_payment_cents || 0) / 100,
                          paymentCount: current_schedule?.num_payments,
                        },
                        after: proposed_schedule,
                      });
                    }
                    fetchDzSchedule();
                    if (onScheduleUpdated) onScheduleUpdated();
                    else if (onExtensionRequested) onExtensionRequested();
                  }}
                />
              </Elements>
            ) : (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-white/40" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Card (inline modal, like Reserve).
          Branch on processor: NMI bookings get the Collect.js form (no Stripe
          Elements wrap — Collect.js renders its own iframes). Stripe bookings
          keep the existing Elements-wrapped form. */}
      <Dialog open={showDeferaZeroUpdateCard} onOpenChange={setShowDeferaZeroUpdateCard}>
        <DialogContent className={ACTION_MODAL_CONTENT_CLASS}>
          <ActionModalHeader variant="dialog"
            icon={CreditCard}
            title="Update Card"
            description={isDeferaCapitalNmi
              ? "Replace the card we charge for your installments. Your next scheduled payment will use the new card."
              : "Add a new card for your subscription payments. Future charges will use this card."}
          />
          <div className={ACTION_MODAL_BODY_CLASS}>
            {isDeferaCapitalNmi ? (
              <DeferaCapitalNmiUpdateCardForm
                tripId={tripId}
                flightData={currentFlightData}
                onClose={() => setShowDeferaZeroUpdateCard(false)}
                /** `fetchDzSchedule` refreshes the SCHEDULE, which carries no card
                 *  fields — the card badge reads `_nmi_card_last4` off
                 *  `currentFlightData`, a prop from the parent's booking row. Without
                 *  asking the parent to refetch, the badge (and the Make-a-Payment
                 *  modal, which is handed the same prop) keeps showing the OLD card
                 *  until a full reload, so a successful update looks like it silently
                 *  failed. GOO3P2's customer rotated her card three times and then
                 *  filed a support inquiry over exactly this — the vault, the booking
                 *  row and her activity log had all been correct since her second
                 *  attempt, 45 minutes before she reported it. */
                onSuccess={() => {
                  setShowDeferaZeroUpdateCard(false);
                  fetchDzSchedule();
                  if (onScheduleUpdated) onScheduleUpdated();
                  else if (onExtensionRequested) onExtensionRequested();
                }}
              />
            ) : stripePromise ? (
              <Elements stripe={stripePromise}>
                <DeferaZeroUpdateCardForm
                  tripId={tripId}
                  apiUrlFn={dzApiUrl}
                  onClose={() => setShowDeferaZeroUpdateCard(false)}
                  onSuccess={() => { setShowDeferaZeroUpdateCard(false); fetchDzSchedule(); }}
                />
              </Elements>
            ) : (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-white/40" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Defera Capital: Restart Plan (after retry exhaustion on either processor).
          Branch on processor: NMI uses Collect.js + restart-plan; Stripe keeps
          the existing Elements + setup-intent + restart-plan flow. */}
      <Dialog open={showRestartPlanModal} onOpenChange={setShowRestartPlanModal}>
        <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl max-w-md max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-normal tracking-tight">Restart Plan</DialogTitle>
            <DialogDescription className="text-white/60 font-normal">
              Pay your past-due balance and we'll resume your installment schedule.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {isDeferaCapitalNmi ? (
              <DeferaCapitalNmiRestartPlanForm
                tripId={tripId}
                fmtCurrency={fmtCurrency}
                planRestart={planRestart}
                onClose={() => setShowRestartPlanModal(false)}
                onPaymentUncertain={pollNmiScheduleAfterPayment}
                onSuccess={() => {
                  setShowRestartPlanModal(false);
                  fetchDzSchedule();
                  if (onScheduleUpdated) onScheduleUpdated();
                }}
              />
            ) : stripePromise ? (
              <Elements stripe={stripePromise}>
                <DeferaCapitalRestartPlanForm
                  tripId={tripId}
                  apiUrlFn={dzApiUrl}
                  fmtCurrency={fmtCurrency}
                  planRestart={planRestart}
                  onClose={() => setShowRestartPlanModal(false)}
                  onSuccess={() => {
                    setShowRestartPlanModal(false);
                    fetchDzSchedule();
                    if (onScheduleUpdated) onScheduleUpdated();
                  }}
                />
              </Elements>
            ) : (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-white/40" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Flight History (admin) */}
      <Dialog open={showChangeHistoryModal} onOpenChange={setShowChangeHistoryModal}>
        <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white font-normal tracking-tight">Change Flight History</DialogTitle>
            <DialogDescription className="text-white/60 font-normal">
              Previous itineraries and price changes for this trip.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pr-2 -mr-2">
            {(() => {
              const getLegacyPrevCents = () => {
                const ps = archivedFlightData?.pricing_snapshot;
                if (ps?.grand_total && parseFloat(ps.grand_total) > 0) return Math.round(parseFloat(ps.grand_total) * 100);
                const base = parseFloat(ps?.original_base_fare ?? 0);
                const svc = parseFloat(ps?.service_fee ?? 0);
                const hold = parseFloat(ps?.hold_fee ?? 0);
                if (base > 0 || svc > 0) return Math.round((base + svc + hold) * 100);
                if (flightCreditCents > 0) return currentGrandTotalCents + flightCreditCents;
                const archGt = parseFloat(archivedFlightData?.grand_total ?? archivedFlightData?.total_amount ?? 0);
                if (archGt > 0) return Math.round(archGt * 100);
                return 0;
              };
              const legacyPrev = getLegacyPrevCents();
              const history = Array.isArray(changeFlightHistory) && changeFlightHistory.length > 0
                ? [...changeFlightHistory].reverse()
                : archivedFlightData
                  ? [{
                      changed_at: new Date().toISOString(),
                      archived_flight_data: archivedFlightData,
                      previous_grand_total_cents: legacyPrev,
                      new_grand_total_cents: currentGrandTotalCents,
                      price_diff_cents: currentGrandTotalCents - legacyPrev,
                      flight_credit_cents: flightCreditCents,
                    }]
                  : [];
              if (history.length === 0) {
                return <p className="text-white/50 text-sm font-normal">No change history for this trip.</p>;
              }
              return history.map((entry, idx) => {
                const archived = entry.archived_flight_data || {};
                const slices = archived.slices || [];
                const prevCents = entry.previous_grand_total_cents ?? 0;
                const newCents = entry.new_grand_total_cents ?? 0;
                const diffCents = entry.price_diff_cents ?? (newCents - prevCents);
                const creditCents = entry.flight_credit_cents ?? 0;
                const fmt = (c) => (c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return (
                  <div key={idx} className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <p className="text-white/60 text-xs font-normal uppercase tracking-wider">
                        {entry.changed_at ? format(parseISO(entry.changed_at), 'MMM d, yyyy · h:mm a') : 'Previous change'}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="text-white/50">${fmt(prevCents)}</span>
                        <ArrowRight className="w-4 h-4 text-white/30 flex-shrink-0" />
                        <span className="text-white font-medium">${fmt(newCents)}</span>
                        {diffCents !== 0 && (
                          <span className={diffCents > 0 ? 'text-brand-400' : 'text-teal-400'}>
                            {diffCents > 0 ? '+' : '−'}${fmt(Math.abs(diffCents))}
                          </span>
                        )}
                        {creditCents > 0 && (
                          <span className="text-teal-400 text-xs font-normal">${fmt(creditCents)} credit</span>
                        )}
                      </div>
                    </div>
                    {slices.length > 0 && (
                      <div className="space-y-4">
                        <p className="text-white/40 text-xs uppercase tracking-wider">Previous itinerary</p>
                        <div className="space-y-4">
                          {slices.map((slice, sIdx) => {
                            const segment = slice.segments?.[0];
                            const lastSegment = slice.segments?.[slice.segments?.length - 1];
                            if (!segment) return null;
                            const mc = segment.marketing_carrier;
                            // Name-first: an admin airline rename leaves the stored logo
                            // URL on the OLD carrier, so the name decides which mark renders.
                            const sliceLogo = resolveCarrierLogo(mc);
                            const stops = (slice.segments?.length || 1) - 1;
                            const cabinDisplay = segment.passengers_cabin_class_marketing_name || segment.cabin_class_marketing_name || archived?.cabin_class || 'Economy';
                            return (
                              <div key={sIdx} className="rounded-xl border border-white/10 bg-black/20 p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-white/60 text-xs font-normal uppercase">
                                    {sIdx === 0 ? 'Departing' : 'Return'} • {segment.departing_at ? format(parseISO(segment.departing_at), 'EEE, MMM d') : '—'}
                                  </span>
                                  {stops > 0 && <span className="text-brand-400/80 text-[10px] uppercase">{stops} stop{stops > 1 ? 's' : ''}</span>}
                                </div>
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 flex justify-center overflow-hidden">
                                      {sliceLogo ? (
                                        <img src={sliceLogo} alt="" className="w-full h-full object-contain" />
                                      ) : (
                                        <PlaneTakeoff className="w-5 h-5 text-white/40 m-auto" />
                                      )}
                                    </div>
                                    <div>
                                      <div className="text-white text-sm font-medium">
                                        {segment.departing_at ? format(parseISO(segment.departing_at), 'h:mm a') : ''} – {lastSegment?.arriving_at ? format(parseISO(lastSegment.arriving_at), 'h:mm a') : ''}
                                      </div>
                                      <div className="text-white/40 text-xs">
                                        {mc?.name}{segment.marketing_carrier_flight_number ? ` ${segment.marketing_carrier_flight_number}` : ''}
                                      </div>
                                      {cabinDisplay && <div className="text-white/30 text-xs capitalize mt-0.5">{cabinDisplay}</div>}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-white/50 text-xs uppercase">
                                      {segment.origin?.iata_code}–{lastSegment?.destination?.iata_code}
                                    </div>
                                    <div className="text-white/60 text-xs">{formatSliceDuration(slice.duration)}</div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
